import * as React from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { FileSignature, LogOut } from 'lucide-react';
import { systemClock } from '@reos/config';
import { t, type MessageKey } from '@reos/i18n';
import {
  FieldRow,
  FormSection,
  SaveStatus,
  EmptyState,
  SplitEditorLayout,
  type SaveStatusKind,
} from '@redhand/ui';
import { DomainApiError } from '../lib/api-error.js';
import { humanApiError, humanApiErrorText } from '../lib/form-errors.js';
import { rateLimitMessage, retryOnRateLimit } from '../lib/rate-limit-retry.js';
import {
  useSession,
  resetSession,
  readContractWhereHintDismissed,
  writeContractWhereHintDismissed,
  type SessionUser,
} from '../stores/session.js';
import { signOut } from '../lib/api.js';
import { getBillingOverview, type SupplierDto } from '../lib/billing-api.js';
import { caseNameWithUnit, fetchCaseAddressIndex } from '../lib/case-address-lookup.js';
import { getCase } from '../lib/cases-api.js';
import { fetchPropertyBriefs } from '../lib/properties.js';
import { ClientIntakeScreen } from './client-intake.js';
import { DateField } from './date-field.js';
import { DocumentCoverPlaceholder, PropertyImage } from './PropertyImage.js';
import { V4Bar, V4FormRow, V4Group, V4NavBar, V4Row } from './ux-v4.js';
import { loadCaseCovers } from '../lib/case-covers.js';
import type { CoverInfo } from '../lib/case-rows.js';
import {
  listSignatureSessions,
  remindSignature,
  type SignatureSessionDto,
} from '../lib/signature-api.js';
import {
  CONTRACT_ACTION_LABEL_KEY,
  CONTRACT_FILTER_LABEL_KEY,
  CONTRACT_WHERE_FILTERS,
  contractCountsIfKnown,
  contractHeaderState,
  contractOwnTitle,
  contractPropertyLine,
  contractStandings,
  matchesContractFilter,
  matchesContractQuery,
  partyChips,
  sessionsByContract,
  type ContractNextAction,
  type ContractWhereFilter,
} from '../lib/contract-standing.js';
import { listClientInvitations } from '../lib/client-intake-api.js';
import { inviteMissingParties, partyToPrefill, type PartyInviteOutcome } from '../lib/party-invites.js';
import { listMembers } from '../lib/members-api.js';
import {
  fetchPropertyFacts,
  savePropertyFactFromContract,
  type PropertyFact,
} from '../lib/properties.js';
import { PageHead } from './page-head.js';
import { ListTable } from './list-table.js';
import { SignaturePanel } from './signature-panel.js';
import {
  applyImportMapping,
  createContract,
  createContractVersion,
  createCustomSubject,
  createOfficeTemplate,
  createOfficeTemplateFromDemo,
  deleteContract,
  diffContractVersion,
  explainContractToClient,
  getContract,
  getContractCatalogs,
  getContractFactChanges,
  getOfficeTemplate,
  importContractTemplateDocx,
  getNewContractPlan,
  listAllContractDeadlines,
  listContractDeadlines,
  setContractDeadlineStatus,
  syncContractDeadlines,
  listContracts,
  listContractVersions,
  listCustomSubjects,
  listDemoTemplates,
  listOfficeTemplates,
  setOfficeTemplateBoldFilledFields,
  prefillContractFromCadastre,
  downloadContractPdf,
  printContract,
  reviewContractAgainstSources,
  revertBlockOverride,
  revertBlockToTemplate,
  removeContractBlock,
  insertContractClause,
  listContractClauses,
  createContractClause,
  updateContractClause,
  archiveContractClause,
  saveContractAsTemplate,
  setBlockOverride,
  suggestImportMapping,
  transitionContractStatus,
  updateContract,
  type ContractArticleDto,
  type ContractCatalogsDto,
  type ContractClauseDto,
  type ContractDeadlineDto,
  type ContractDetailDto,
  type ContractTextChangeDto,
  type UnresolvedTextChoiceDto,
  type ContractDto,
  type ContractFillPlanArticleDto,
  type ContractFillPlanDto,
  type FieldFiller,
  type ContractFieldDictionaryEntryDto,
  type ContractFactChangeDto,
  type ContractFieldSource,
  type ContractStatusValue,
  type ContractType,
  type ContractSignaturePolicyDto,
  type ContractVersionDto,
  type ContractAiExplanationResultDto,
  type ContractAiReviewResultDto,
  type ContractVersionDiffResultDto,
  type NewContractPlanDto,
  type CustomSubjectDto,
  type DemoTemplateSummaryDto,
  type ImportApplyDecision,
  type InflectionHitDto,
  type MappingDecision,
  syncContractPartyAddresses,
  type ContractPartyAddressSyncDto,
  type MappingSkipDto,
  type MappingSkipReason,
  type MappingSuggestionDto,
  type OfficeTemplateDto,
  type ServiceState,
} from '../lib/contracts-api.js';
import {
  articleSectionTitle,
  blockTemplateTextFrom,
  boldForFieldInContract,
  documentFileName,
  documentValueText,
  expandedBlockIds,
  fieldControlTestId,
  printDocumentHtml,
  SUBJECT_TYPE_FIELD_KEY,
} from '../lib/contract-doc.js';
import {
  formatCzechNumber,
  isNumericFieldType,
  readCzechNumberInput,
} from '../lib/czech-number.js';
import {
  articleOfBlock,
  checkLines,
  checksSummary,
  CHECKS_PREVIEW_LIMIT,
  textChangeAuthorLine,
  textChangeSummary,
  fieldControlKind,
  fieldSuggestion,
  fieldUsageRomans,
  missingFieldOrder,
  PARTY_LINK_STATE_KEY,
  selectableFieldOf,
  partyLinkStatus,
  stepMissingField,
  type PartyInvitationRef,
  removePartyPatch,
  clientSummary,
  contractCounts,
  signingConditions,
  groupFieldsByArticle,
  ORIGIN_LABEL_KEY,
  ORIGIN_LEGEND,
  originCssClass,
  subjectTypeForPropertyKind,
  addPartyPatch,
  partiesOf,
  partyDisplayName,
  partyFieldPatch,
  partyRoleName,
  partyRolesFor,
  partyFieldKeysFor,
  PARTY_FIELD_LABEL_KEY,
  PARTY_FIELD_PREFIX,
  PARTY_ROLE_LABEL_KEY,
  isPartyFieldKey,
  formatRcInput,
  maskRc,
  rcFormatLooksInvalid,
  officeTemplateFieldCount,
  type CheckLine,
  removeTemplateField,
  renameTemplateField,
  repeatBlockLabelKey,
  addRepeatItemPatch,
  nonPartyRepeatBlockKeys,
  removeRepeatItemPatch,
  repeatItemFieldKeys,
  repeatItemFieldPatch,
  repeatItemsOf,
  repeatRowCount,
  supersedingTemplateId,
  templateFieldKeys,
  type Party,
  type PartyFieldKey,
  type PartyRole,
} from '../lib/contract-presentation.js';

/**
 * Smlouvy v detailu zakázky — šest záložek přesně podle klikacího mockupu
 * `docs/platform/ui/_designs/smlouvy/reos-smlouvy-aplikace-mockup.html`
 * (krok T4, R.B. 29. 9.: „Koukal jsem v adminu na smlouvy — vůbec to tam není,
 * ani to nedělá to, co je v mockupu").
 *
 * Smlouvy NEJSOU samostatná stránka v menu náhradou detailu zakázky: jsou
 * záložkou uvnitř zakázky, která je drží (`caseId`), a zároveň fungují
 * samostatně přes `/smlouvy` jako přehled napříč zakázkami.
 */

const LOCALE = 'cs' as const;

/*
 * Krok SML5 bod 3: u ZNAMEHO duvodu (`type` v problem+json) ma prednost veta,
 * ktera rika CO udelat — server u 4xx posila obecny titulek „Validace vstupu
 * selhala". Mapa duvodu je jedna pro vsechny formulare (`lib/form-errors.ts`).
 */
function errorMessage(error: unknown): string | undefined {
  const lidsky = humanApiErrorText(error);
  if (lidsky !== undefined) return lidsky;
  return error instanceof DomainApiError ? error.message : undefined;
}

/**
 * Krok UXP3b bod 3 (nalez S18e) — „Odeslat stranám" NA UZ ODESLANE SMLOUVE.
 *
 * `ALLOWED_TRANSITIONS` v `db-service.ts` nema smycku ze stavu do sebe
 * (`awaiting_signature -> awaiting_signature` je 409 `invalid_status_transition`).
 * Oba odesilaci handlery (editor i panel podpisu) volaly prechod VZDY, takze
 * opakovane kliknuti na uz odeslanou smlouvu skoncilo obecnou chybou „Data se
 * nepodařilo načíst" a pozvanky stranam vubec nevznikly. Prechod se tu proto
 * preskoci, kdyz uz smlouva v cilovem stavu je — zbytek (zalozeni pozvanek) je
 * idempotentni a ma probehnout dal.
 *
 * Oprava e2e 1 (S18e/mobil, 16. 10. 2026): stejnou obecnou chybu dostal makler
 * i na smlouve, ktera uz je DAL nez cil — `signed -> awaiting_signature` zpatky
 * nevede (`signed: ['archived']`) a `signed -> ready_to_send` v editoru take ne.
 * Preskakuje se proto kazdy ZPETNY krok, ne jen smycka do sebe.
 *
 * Oprava e2e 2 (S18e/mobil, 16. 10. 2026): prechod padl na `429 rate_limited`
 * (doklad: `POST /v1/contracts/…/status` v trace behu) a shodil CELE odeslani —
 * pozvanky stranam se vubec nezkusily zalozit a obrazovka hlasila „⚠ Data se
 * nepodařilo načíst.", tedy chybu CTENI u akce, ktera zapisuje. Prechod proto
 * uz nevyhazuje: vraci duvod a volajici pokracuje k pozvankam (jsou
 * idempotentni a na stavu smlouvy nezavisi) a rekne PRAVDU o obojim.
 */
interface TransitionAttempt {
  /** Smlouva po pokusu — pri neuspechu puvodni, nedotcena. */
  readonly contract: ContractDetailDto;
  /** Proc se stav nezmenil; `null` = zmenil se, nebo nebylo co menit. */
  readonly error: string | null;
  /**
   * Krok KLIKFIX1 (vada V6): stav se nezmenil proto, ze SMLOUVA NENI UPLNA
   * (`422 documents/contract-incomplete`). Jen tenhle duvod znamena „neposilat
   * stranam nic"; 409 (uz odeslano/podepsano) a 429 (zahlceni) o obsahu
   * smlouvy nerikaji nic, a pozvanky se u nich zakladaji dal (specy S18e).
   */
  readonly incomplete: boolean;
}
/*
 * Poradi NENI druha kopie `ALLOWED_TRANSITIONS` (ta je graf a zustava jedinou
 * pravdou o tom, co server povoli) — rika jen „uz jsme stejne daleko nebo dal",
 * aby UI zpetny prechod vubec nezkouselo.
 */
const STATUS_ORDER: readonly ContractStatusValue[] = [
  'draft',
  'ready_to_send',
  'awaiting_signature',
  'signed',
  // `archived` je koncovy stav mimo linku odesilani — zadny prechod z nej nevede.
  'archived',
];

async function transitionIfNeeded(
  contractId: string,
  current: ContractDetailDto,
  next: ContractStatusValue,
): Promise<TransitionAttempt> {
  const alreadyThere = STATUS_ORDER.indexOf(current.status) >= STATUS_ORDER.indexOf(next);
  if (alreadyThere) return { contract: current, error: null, incomplete: false };
  try {
    return {
      contract: await transitionContractStatus(contractId, next),
      error: null,
      incomplete: false,
    };
  } catch (err: unknown) {
    const reason = errorMessage(err) ?? (err instanceof Error ? err.message : String(err));
    return { contract: current, error: reason, incomplete: isIncompleteContractError(err) };
  }
}

/**
 * Krok KLIKFIX1 (vada V6): odmitl server prechod kvuli NEUPLNE SMLOUVE?
 * Strojovy kod je `documents/contract-incomplete` (viz
 * `unprocessableIncompleteContract` v `@reos/documents`), status 422 je
 * zaloha pro starsi odpoved bez `type`.
 */
function isIncompleteContractError(error: unknown): boolean {
  if (!(error instanceof DomainApiError)) return false;
  return error.type === 'documents/contract-incomplete' || error.status === HTTP_UNPROCESSABLE;
}

/** 422 — smlouva neni uplna, server ji dal neposune. */
const HTTP_UNPROCESSABLE = 422;

/**
 * Krok UXP1 (nalez P1-4) — CHYBA PRI UKLADANI TEXTU SMLOUVY.
 *
 * Rucni pruchod 10. 10. 2026: smazani vlozky `[DOPLNÍ PRÁVNÍK…]` skoncilo
 * `409 PUT /v1/contracts/…/blocks/upozorneni-1/override` a nahore se objevilo
 * jen „⚠ Data se nepodařilo načíst." — hlaska o CTENI u chyby ZAPISU. Makler
 * z ni nemel jak poznat, ze odstavec je zamceny, ani co delat dal.
 *
 * `undefined` tu nesmi vzniknout: prazdna chyba znamena, ze se text vratil
 * a na obrazovce nestalo nic.
 */
function textSaveErrorMessage(error: unknown): string {
  const fromServer = errorMessage(error);
  const status = error instanceof DomainApiError ? error.status : null;
  const reason = fromServer ?? (error instanceof Error ? error.message : String(error));
  return status === HTTP_CONFLICT
    ? t('webAdmin.contracts.editor.textSaveConflict', { locale: LOCALE, values: { reason } })
    : t('webAdmin.contracts.editor.textSaveFailed', { locale: LOCALE, values: { reason } });
}

/** 409 — smlouvu nebo odstavec mezitim zamkl/zmenil nekdo jiny. */
const HTTP_CONFLICT = 409;

const TYPE_KEY: Readonly<Record<ContractType, MessageKey>> = {
  'najem-byt': 'webAdmin.contracts.type.najem-byt',
  'najem-nebyt': 'webAdmin.contracts.type.najem-nebyt',
  kupni: 'webAdmin.contracts.type.kupni',
  rezervace: 'webAdmin.contracts.type.rezervace',
  zprostredkovatelska: 'webAdmin.contracts.type.zprostredkovatelska',
};

const SERVICE_STATE_KEY: Readonly<Record<ServiceState, MessageKey>> = {
  'v-zalohach': 'webAdmin.contracts.editor.serviceState.v-zalohach',
  prevedeno: 'webAdmin.contracts.editor.serviceState.prevedeno',
  sami: 'webAdmin.contracts.editor.serviceState.sami',
  'nedodava-se': 'webAdmin.contracts.editor.serviceState.nedodava-se',
};

const SERVICE_STATES: readonly ServiceState[] = ['v-zalohach', 'prevedeno', 'sami', 'nedodava-se'];

/**
 * Zdroje chybejicich poli v poradi predlohy (katastr, klienti, ty) — poradi
 * proužku i legendy. Samotne ROZHODNUTI, kdo pole doplni, dela server
 * (`fill-plan.ts`); tady jsou jen popisky.
 */
const FILLER_ORDER: readonly FieldFiller[] = ['katastr', 'klienti', 'makler'];

/**
 * Krok APP3 bod 3 (nalez A34): PROC se potvrzene rozhodnuti neprovedlo.
 * Duvod rozhoduje server (`applyMappingDecisions`), tady je jen popisek.
 */
const IMPORT_SKIP_KEY: Readonly<Record<MappingSkipReason, MessageKey>> = {
  'nejednoznacny-usek': 'webAdmin.contracts.templates.import.skippedAmbiguous',
  'usek-nenalezen': 'webAdmin.contracts.templates.import.skippedNotFound',
  'bez-pole': 'webAdmin.contracts.templates.import.skippedNoField',
};

/** Zaloha, kdyz odpoved serveru osnovu jeste nenese (detail z cache). */
const EMPTY_FILL_PLAN: ContractFillPlanDto = {
  total: 0,
  filled: 0,
  missing: 0,
  later: 0,
  missingByFiller: { katastr: 0, klienti: 0, makler: 0 },
  articles: [],
  // Krok APP3 bod 1: seznamy, ze kterych vznikaji pocty i radky kontrol.
  missingKeys: [],
  laterKeys: [],
  blockingKeys: [],
  missingOutsideText: [],
  blocking: 0,
  cadastreKeys: [],
  caseFactKeys: [],
  officeKeys: [],
  fillerByKey: {},
};

const FILLER_LEGEND_KEY: Readonly<Record<FieldFiller, MessageKey>> = {
  katastr: 'webAdmin.contracts.editor.fillerLegendKatastr',
  klienti: 'webAdmin.contracts.editor.fillerLegendKlienti',
  makler: 'webAdmin.contracts.editor.fillerLegendMakler',
};

const FILLER_SHORT_KEY: Readonly<Record<FieldFiller, MessageKey>> = {
  katastr: 'webAdmin.contracts.editor.fillerKatastr',
  klienti: 'webAdmin.contracts.editor.fillerKlienti',
  makler: 'webAdmin.contracts.editor.fillerMakler',
};

const FILLER_NOTE_KEY: Readonly<Record<FieldFiller, MessageKey>> = {
  katastr: 'webAdmin.contracts.editor.fillerNoteKatastr',
  klienti: 'webAdmin.contracts.editor.fillerNoteKlienti',
  makler: 'webAdmin.contracts.editor.fillerNoteMakler',
};

/** Skupiny vzorů REOS → lidský nadpis (T2 nese `skupina` A–D). */
const DEMO_GROUP_KEY: Readonly<Record<string, MessageKey>> = {
  A: 'webAdmin.contracts.new.groupZprostredkovani',
  B: 'webAdmin.contracts.new.groupObchod',
  C: 'webAdmin.contracts.new.groupNajem',
  D: 'webAdmin.contracts.new.groupSluzby',
};

/**
 * Lidsky popisek pole misto technickeho klice (ADM1 bod 2). `FIELD_DICTIONARY`
 * (`@reos/documents`) chodi z `GET /v1/contract-templates/catalogs`. Klic mimo
 * slovnik (vlastni sablona s neznamym tokenem) padne na klic samotny, at
 * editor nikdy nezmizi jen kvuli chybejicimu popisku.
 */
export function fieldLabel(
  catalogs: ContractCatalogsDto | null,
  key: string,
  /**
   * Krok NOC3 bod 6 (nalez N14) — POPISKY VLASTNICH POLI VZORU KANCELARE
   * (`contract_office_templates.extra_fields`, klic -> popisek). Vzor si svoje
   * pole pojmenovava sam; bez toho si editor nazev odvozoval z klice a vysadil
   * ho BEZ DIAKRITIKY („Venkovni prostory", „Jistota urok").
   */
  templateFields?: ReadonlyMap<string, string> | undefined,
): string {
  const fromCatalog = catalogs?.fields?.find((f) => f.key === key)?.label;
  if (fromCatalog !== undefined) return fromCatalog;
  const fromTemplate = templateFields?.get(key.replace(/\[\]$/, '')) ?? templateFields?.get(key);
  if (fromTemplate !== undefined && fromTemplate.trim() !== '') return fromTemplate;
  // Opakovany blok strany (`najemce[]`) ve slovniku neni — ma vlastni nazev,
  // jinak by se do ceskeho UI dostal technicky klic (mockup, bod D.14).
  const blockKey = repeatBlockLabelKey(key);
  return blockKey === undefined ? humanFieldKey(key, catalogs) : t(blockKey, { locale: LOCALE });
}

/**
 * Krok EDIT2 bod 8 (nalez: v editoru stal radek „nemovitost.venkovni-prostory").
 *
 * Vlastni sablona kancelare muze nest token, ktery ve slovniku neni — pak
 * neexistuje popisek a do ceskeho UI se dostal TECHNICKY KLIC. Nazev se proto
 * odvodi z klice: posledni cast bez skupiny, pomlcky na mezery, prvni velke.
 * `nemovitost.venkovni-prostory` → „Venkovni prostory". Lepsi priblizny nazev
 * nez klic slovniku vysazeny jako text.
 */
export function humanFieldKey(key: string, catalogs?: ContractCatalogsDto | null): string {
  const bare = key.replace(/\[\]$/, '');
  const last = bare.split('.').at(-1) ?? bare;
  const words = last.replace(/[-_]+/g, ' ').trim();
  if (words === '') return key;
  const vocabulary = diacriticVocabulary(catalogs ?? null);
  const spelled = words
    .split(' ')
    .map((word) => vocabulary.get(fold(word)) ?? word)
    .join(' ');
  return spelled.charAt(0).toUpperCase() + spelled.slice(1);
}

/** Diakritika pryc — klic tokenu je vzdy ASCII (`venkovni-prostory`). */
function fold(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase();
}

/**
 * Krok NOC3 bod 6 (nalez N14) — SLOVA S DIAKRITIKOU, KTERA SLOVNIK POLI NEZNA.
 *
 * Token smlouvy je ASCII (`{{nemovitost.venkovni-prostory}}`), takze nazev
 * odvozeny z klice je bez diakritiky. Prvni zdroj spravneho pravopisu je
 * SLOVNIK POLI (`GET /v1/contract-templates/catalogs` — tytez popisky, jake
 * editor pouziva u znamych poli: „měsíce", „jistota", „prostory"). Co v nem
 * neni, stoji tady; seznam je kratky zamerne, protoze spravne misto pro
 * popisek je definice pole ve vzoru (`extraFields`) — tohle je az posledni
 * zachranna sit, aby makler necetl „Venkovni prostory".
 */
const EXTRA_DIACRITIC_WORDS: readonly string[] = [
  'venkovní',
  'vnitřní',
  'úrok',
  'úroky',
  'výměra',
  'výpověď',
  'nájemné',
  'nájemce',
  'pronajímatel',
  'předání',
  'převzetí',
  'příslušenství',
  'počet',
  'záloha',
  'zálohy',
  'jistota',
  'měsíce',
  'měsíční',
  'plocha',
  'podlaží',
  'sklep',
  'balkón',
  'terasa',
  'zahrada',
  'garáž',
  'parkování',
  'energie',
  'služby',
  'poplatky',
  'smluvní',
  'pokuta',
  'účet',
  'číslo',
  'datum',
  'částka',
  'doba',
  'den',
  'dní',
];

const VOCABULARY_CACHE = new WeakMap<ContractCatalogsDto, ReadonlyMap<string, string>>();

function diacriticVocabulary(catalogs: ContractCatalogsDto | null): ReadonlyMap<string, string> {
  const cached = catalogs === null ? undefined : VOCABULARY_CACHE.get(catalogs);
  if (cached !== undefined) return cached;
  const vocabulary = new Map<string, string>();
  for (const word of EXTRA_DIACRITIC_WORDS) vocabulary.set(fold(word), word);
  for (const field of catalogs?.fields ?? []) {
    for (const word of field.label.split(/[^\p{L}\p{N}]+/u)) {
      const lower = word.toLowerCase();
      // Zajima nas jen slovo, ktere diakritiku SKUTECNE nese — ASCII podoba
      // by v mape nic nezmenila a prepsala by spravny pravopis.
      if (lower === '' || fold(lower) === lower) continue;
      if (!vocabulary.has(fold(lower))) vocabulary.set(fold(lower), lower);
    }
  }
  if (catalogs !== null) VOCABULARY_CACHE.set(catalogs, vocabulary);
  return vocabulary;
}

/**
 * Predvyplneni "crm" poli slovniku (kancelar/makler) po zalozeni smlouvy
 * (ADM1 bod 2). Zdroj kancelare je dodavatel z fakturace (FAK1
 * `finance_suppliers` — jediny zaznam s IČO/adresou, ktery REOS dnes ma);
 * bez nastavene fakturace zustavaji `kancelar.ico`/`kancelar.adresa`
 * nevyplnena (zadna fabulace). Telefon makleere v session neni — pole
 * `makler.telefon` proto zustava take nevyplnene, makler ho doplni rucne.
 */
function officePrefillFieldValues(
  user: SessionUser | null,
  supplier: SupplierDto | null,
): Record<string, { value: string; source: 'reos_data' }> {
  const entries: [string, string | null][] = [
    ['kancelar.nazev', supplier?.companyName ?? null],
    ['kancelar.ico', supplier?.ico ?? null],
    [
      'kancelar.adresa',
      supplier !== null && supplier.street !== null
        ? [supplier.street, [supplier.zip, supplier.city].filter((v) => v !== null && v !== '').join(' ')]
            .filter((v) => v !== '')
            .join(', ')
        : null,
    ],
    ['makler.jmeno', user?.name ?? null],
    ['makler.email', user?.email ?? null],
  ];
  const out: Record<string, { value: string; source: 'reos_data' }> = {};
  for (const [key, value] of entries) {
    if (value !== null && value.trim() !== '') out[key] = { value, source: 'reos_data' };
  }
  return out;
}

// ------------------------------------------------------- zalozky EDITORU smlouvy

/**
 * Zalozky patri k JEDNE smlouve (krok UXS1).
 *
 * Do ted stalo sest pilulek mockupu nad SEZNAMEM smluv („Editor čeho?",
 * R.B. 1. 10.). V mockupu jsou to zalozky rozpracovane smlouvy — proto jsou
 * ted jen v editoru (`/smlouvy/:id`), a to tri: Editor, Odeslat k podpisu,
 * Klient (magic link). „Smlouvy zakázky" a „Nová smlouva" jsou vlastni adresy
 * (`/smlouvy`, `/smlouvy/nova`) a „Šablony kanceláře" patri do
 * „Smlouvy a šablony" (`/smlouvy-sablony`), jak zada zadani.
 */
export type EditorTabId = 'editor' | 'signature' | 'client';

const EDITOR_TABS: readonly { readonly id: EditorTabId; readonly key: MessageKey }[] = [
  { id: 'editor', key: 'webAdmin.contracts.tabs.editor' },
  { id: 'signature', key: 'webAdmin.contracts.tabs.signature' },
  { id: 'client', key: 'webAdmin.contracts.tabs.client' },
];

// ------------------------------------------------------------------- seznam

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'short' });
}

/**
 * Datum LHUTY (`YYYY-MM-DD`) v ceskem tvaru vcetne roku. Sklada se z casti
 * retezce, ne pres `new Date`: lhuta je DEN, a prevod pres okamzik by ji
 * v zapornych casovych zonach posunul.
 */
function formatDueDate(isoDate: string): string {
  const [year, month, day] = isoDate.split('-');
  if (year === undefined || month === undefined || day === undefined) return isoDate;
  return `${Number(day)}. ${Number(month)}. ${year}`;
}

/**
 * Rezim editoru podle predlohy `Smlouva.dc.html` (krok SML2 bod 1):
 * Vyplnit / Upravit text / Náhled pro klienta.
 */
export type EditorMode = 'fill' | 'text' | 'client';

/**
 * Krok P8F8 bod 5 — KDO HODNOTU NAPSAL, TEN JE JEJI PUVOD.
 *
 * Do P8F8 bral editor pri rucni oprave pole puvod ZE STARE HODNOTY
 * (`before?.source ?? 'broker'`), takze kdyz pole pred tim vyplnil klientsky
 * formular, odesel `PATCH /v1/contracts/:id` s `{"source":"client"}` —
 * smlouva pak tvrdila, ze uzitnou plochu uvedl majitel, i kdyz ji prepsal
 * makler (merení naostro 27. 10. 14:05, nalez z P8F5). `client` smi vzniknout
 * JEN v klientskem formulari, `reos_data` jen pri doplneni ze zakazky.
 *
 * Je to funkce, ne konstanta u volani, aby obe rucni cesty (formular
 * uprostred i panel vpravo) mely jeden zdroj pravdy — druha kopie by se
 * rozesla.
 */
function brokerEditSource(): ContractFieldSource {
  return 'broker';
}

/** „uloženo HH:MM" v hlavicce editoru — hodina a minuta, nic vic. */
function formatClockTime(at: Date): string {
  return at.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' });
}

export interface ContractsScreenProps {
  readonly caseId?: string | undefined;
  /** Nazev zakazky do drobecku (mockup: „Zakázky › X › Smlouvy"). */
  readonly caseTitle?: string | undefined;
  /**
   * Krok UXR2 (zadani bod 5 — „sladit s tokeny a shellem"): na SAMOSTATNE
   * strance `/smlouvy` jde nadpis a primarni akce do hlavicky shellu
   * (`PageHead`), aby nadpis obrazovky byl na mobilu v horni liste a tlacitko
   * v plovouci liste jako na ostatnich obrazovkach. V zalozce zakazky
   * (`caseId` je zadane) zustava vlastni hlavicka — hlavni nadpis stranky
   * tam patri zakazce.
   */
  readonly pageHead?: boolean;
}

/**
 * Vyplnene hodnoty jedne smlouvy jako telo PATCHe pro druhou (duplikat,
 * prevod na novou verzi sablony). `pad` je v DTO `string[]`, kontrakt chce
 * sedmici — prevod je tady na jednom miste, ne dvakrat v obsluze tlacitek.
 */
function copyOfValues(detail: ContractDetailDto): Parameters<typeof updateContract>[1] {
  const custom = detail.customSubject;
  return {
    fieldValues: { ...detail.fieldValues },
    serviceStates: { ...detail.serviceStates },
    ...(custom !== null
      ? {
          customSubject: {
            ...custom,
            pad: custom.pad as [string, string, string, string, string, string, string],
          },
        }
      : {}),
  };
}

/**
 * PREHLED smluv — `/smlouvy` a zalozka „Obchod › Smlouva" v zakazce.
 *
 * Krok SML1 bod 1, predloha `docs/design/ux-v3/SmlouvyPrehled.dc.html`
 * (struktura a obsah) ve vzhledu UX v4: smlouvy se nerovnaji podle
 * technickeho stavu, ale PODLE TOHO, U KOHO JSOU — Aktivní · Na tobě ·
 * U klientů · Ověření podpisu · Podepsané. Pocty u pilulek, zarazeni radku
 * i odznak „Smlouvy" v menu jdou z JEDNE funkce (`lib/contract-standing.ts`).
 *
 * Radek nese typ a nemovitost, stav KAZDE STRANY ze skutecnych pozvanek
 * a podpisu, stav smlouvy lidsky s druhym radkem „proč / do kdy" a JEDNU
 * akci (Podepsat / Pokračovat / Připomenout / Nahrát sken / Stáhnout).
 * Radky „na tobě" jsou zvyraznene. Vpravo stoji lhuty ze smluv (krok T5)
 * a mustry kancelare.
 *
 * Z UXS1 plati dal: klikatelny radek, nazev dokumentu je ODKAZ (ne tlacitko)
 * a zbyle akce jsou v jednom menu „⋯"; editor otevira ADRESA `/smlouvy/:id`,
 * takze funguje Zpet v prohlizeci i odkaz na konkretni smlouvu.
 */
export function ContractsScreen({ caseId, caseTitle, pageHead = false }: ContractsScreenProps): React.JSX.Element {
  const navigate = useNavigate();
  const [rows, setRows] = React.useState<ContractDto[] | null>(null);
  const [details, setDetails] = React.useState<ReadonlyMap<string, ContractDetailDto>>(new Map());
  const [sessions, setSessions] = React.useState<readonly SignatureSessionDto[]>([]);
  /**
   * Krok MRGP1 — PODEPISOVANI UZ DOSLO (selhani se pocita jako „doslo":
   * prehled zustane, jen se o podpisech nic netvrdi, viz effect niz).
   * Prazdne pole `sessions` nejde od nenactenych odlisit, a pritom prave
   * podpisy rozhoduji, jestli smlouva lezi „na vás", nebo „u klientů".
   */
  const [sessionsLoaded, setSessionsLoaded] = React.useState(false);
  const [invitations, setInvitations] = React.useState<readonly PartyInvitationRef[]>([]);
  const [deadlines, setDeadlines] = React.useState<readonly ContractDeadlineDto[]>([]);
  const [covers, setCovers] = React.useState<ReadonlyMap<string, CoverInfo>>(new Map());
  const [templates, setTemplates] = React.useState<ReadonlyMap<string, string>>(new Map());
  const [officeTemplates, setOfficeTemplates] = React.useState<OfficeTemplateDto[]>([]);
  const [error, setError] = React.useState<string | undefined>(undefined);
  // Krok T6 bod 2: prehled napric zakazkami (`caseId === undefined`) ma u
  // kazde smlouvy ukazat zakazku a smlouvu bez zakazky jde k zakazce priradit.
  // Uvnitr zalozky konkretni zakazky (`caseId` je zadane) je sloupec zbytecny —
  // vsechny radky uz do te zakazky patri.
  const [caseIndex, setCaseIndex] = React.useState<ReadonlyMap<string, string> | null>(null);
  const [assigningRowId, setAssigningRowId] = React.useState<string | null>(null);
  const [assignQuery, setAssignQuery] = React.useState('');
  const [assignStatus, setAssignStatus] = React.useState<SaveStatusKind>('idle');
  /**
   * Krok SML1 bod 1: filtr je „u KOHO smlouva je", ne technicky stav.
   * Vychozi je „Aktivní" — predloha `SmlouvyPrehled.dc.html` ma prvni pilulku
   * vybranou a prehled bez filtru by zacinal podepsanymi smlouvami, se
   * kterymi uz neni co delat.
   */
  const [filter, setFilter] = React.useState<ContractWhereFilter>('aktivni');
  const [caseFilter, setCaseFilter] = React.useState('');
  /**
   * Krok TOKYFIX1 (prekazka P2/6): „Hledat" a NACTENI DALSICH.
   *
   * Mereni TOKY1 (14. 10. 2026) nasla smlouvu zalozenou pred minutou v
   * prehledu 29–30 radku jen tak, ze projela cely seznam — pole na hledani
   * prehled nemel a radky nad `LIST_PAGE_SIZE` nemely dotazeny detail, takze
   * stav („zbývá 24") u nich chybel. Proto se krati VYPIS (s tlacitkem
   * „Načíst další"), ne data: filtry a pocty dal pocitaji vsechny radky.
   */
  const [query, setQuery] = React.useState('');
  const [shown, setShown] = React.useState(LIST_PAGE_SIZE);
  const [openMenuId, setOpenMenuId] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState<SaveStatusKind>('idle');

  React.useEffect(() => {
    let cancelled = false;
    setRows(null);
    /** Krok NOC1 bod 2: kvota (429) nebo vypadek site = opakovat, ne spadnout. */
    retryOnRateLimit(() => listContracts(caseId !== undefined ? { caseId } : {}))
      .then((data) => {
        if (cancelled) return;
        /*
         * Krok TOKYFIX1 (prekazka P2/6): NAPOSLED UPRAVENE NAHORE. Server uz
         * radi `order by updated_at desc`, razeni je tu proto, aby poradi
         * prehledu nezaviselo na tom, co prave posilaji JINE cesty (sloucene
         * seznamy, vlozeny radek po `assignCase`) — jeden zdroj pravdy
         * o poradi je obrazovka, ktera ho ukazuje.
         */
        setRows([...data].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)));
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setError(
            rateLimitMessage(err) ??
              errorMessage(err) ??
              t('webAdmin.contracts.loadError', { locale: LOCALE }),
          );
      });
    return () => {
      cancelled = true;
    };
  }, [caseId]);

  /**
   * Stav radku („Rozpracovaná · zbývá 24") potrebuje OSNOVU z detailu
   * (ROZHODNUTI T4-6: paralelni nacteni bez noveho endpointu).
   *
   * Krok TOKYFIX1: detail se dotahuje presne pro VYPSANE radky, ne pro prvnich
   * dvacet ze seznamu. Pred tim mela smlouva na 21. miste prazdny stav
   * a propadla kazdym filtrem (`standing === undefined`), takze „Načíst další"
   * by ukazovalo radky, o kterych prehled nic netvrdi.
   */
  const requestedDetails = React.useRef(new Set<string>());
  React.useEffect(() => {
    requestedDetails.current = new Set();
    setDetails(new Map());
  }, [caseId]);
  React.useEffect(() => {
    const need = (rows ?? [])
      .slice(0, shown)
      .map((row) => row.id)
      .filter((id) => !requestedDetails.current.has(id));
    if (need.length === 0) return;
    for (const id of need) requestedDetails.current.add(id);
    let cancelled = false;
    void Promise.all(
      need.map((id) =>
        getContract(id).then(
          (d) => [id, d] as const,
          () => null,
        ),
      ),
    ).then((loaded) => {
      if (cancelled) return;
      setDetails((current) => {
        const next = new Map(current);
        for (const entry of loaded) if (entry !== null) next.set(entry[0], entry[1]);
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [rows, shown]);

  /**
   * Krok SML1 bod 1: „u koho smlouva je" rozhoduje SKUTECNE podepisovani
   * a SKUTECNE pozvanky, ne stav smlouvy. Obe volani jsou jedno na celou
   * obrazovku (bez filtru contractId), takze radku muze byt kolik chce.
   * Kdyz selzou, prehled zustane — jen se o podpisech nic netvrdi.
   */
  React.useEffect(() => {
    let cancelled = false;
    const scope = caseId !== undefined ? { caseId } : {};
    setSessionsLoaded(false);
    void listSignatureSessions(scope).then(
      (list) => { if (!cancelled) { setSessions(list); setSessionsLoaded(true); } },
      () => { if (!cancelled) setSessionsLoaded(true); },
    );
    void listClientInvitations(scope).then(
      (list) => { if (!cancelled) setInvitations(list); },
      () => undefined,
    );
    void listAllContractDeadlines(caseId).then(
      (list) => { if (!cancelled) setDeadlines(list); },
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
  }, [caseId]);

  /*
   * Krok NOC1 bod 2 (nalez N01): nazvy sablon a index zakazek jsou DOPLNEK
   * prehledu — radky stoji i bez nich. Do NOC1 na nich visel `.then()` BEZ
   * druhe vetve, takze 429 z kvoty skoncil neodchycenou chybou v prohlizeci
   * (`client_error_events`, druh `promise`) a prehled se rozsypal, i kdyz
   * seznam smluv sam dosel. Selhani doplnku proto nikdy nesmi vybublat — jen
   * se o nem obrazovka nedozvi nic noveho.
   */
  React.useEffect(() => {
    let cancelled = false;
    void retryOnRateLimit(() => Promise.all([listOfficeTemplates(), listDemoTemplates()])).then(
      ([office, demo]) => {
        if (cancelled) return;
        const map = new Map<string, string>();
        for (const tpl of office) map.set(tpl.id, `${tpl.name} v${tpl.version}`);
        for (const tpl of demo) map.set(tpl.id, tpl.nazev);
        setTemplates(map);
        setOfficeTemplates(office);
      },
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (caseId !== undefined) return;
    let cancelled = false;
    void retryOnRateLimit(() => fetchCaseAddressIndex()).then(
      ({ byCaseId }) => {
        if (!cancelled) setCaseIndex(byCaseId);
      },
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
  }, [caseId]);

  /**
   * Nahled nemovitosti u radku (predloha v4 § Pravidla 3: „obrazek vsude, kde
   * se s nemovitosti pracuje"). JEDNA funkce pro cely admin (`loadCaseCovers`),
   * jedno volani na vsechny radky; bez `caseId` u nahledu, protoze panorama by
   * na dlouhem seznamu skoncilo rada 404 (tentyz duvod jako v `/zakazky`).
   */
  React.useEffect(() => {
    const ids = [...new Set((rows ?? []).map((r) => r.propertyId).filter((id): id is string => id !== null))];
    if (ids.length === 0) return;
    let cancelled = false;
    void loadCaseCovers(ids).then(
      (loaded) => { if (!cancelled) setCovers(loaded); },
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
  }, [rows]);

  async function assignCase(contractId: string, targetCaseId: string): Promise<void> {
    setAssignStatus('saving');
    try {
      const updated = await updateContract(contractId, { caseId: targetCaseId });
      setRows((current) => current?.map((r) => (r.id === contractId ? updated : r)) ?? current);
      setAssignStatus('saved');
      setAssigningRowId(null);
      setAssignQuery('');
    } catch {
      setAssignStatus('error');
    }
  }

  /**
   * U KOHO JE KAZDA SMLOUVA — jedna funkce (`lib/contract-standing.ts`), ze
   * ktere zaroven pocita odznak „Smlouvy" v menu. Dve kopie tohoto pravidla
   * znamenaly, ze menu hlasilo „na tobě" u smlouvy, ktera cekala na klienta.
   */
  const standings = React.useMemo(
    () => contractStandings({ rows: rows ?? [], sessions, details }),
    [rows, sessions, details],
  );
  /**
   * Krok MRGP1 — POCET, KTERY JESTE NIKDO NEZNA, SE NEKRESLI.
   *
   * `null` = seznam smluv nebo podepisovani jeste nedoslo. Do tohoto kroku
   * se pilulky kreslily hned a tvrdily `0`; makler na pomale siti si tedy
   * precetl „na vás nelezi zadna smlouva" a sada S16 na 390 px na tom
   * 5. 10. 2026 spadla (odznak v profilu 11 proti pilulce 0, desktop zeleny).
   */
  const counts = React.useMemo(
    () => contractCountsIfKnown({ rows, sessions: sessionsLoaded ? sessions : null, details }),
    [rows, sessions, sessionsLoaded, details],
  );
  const sessionByContract = React.useMemo(() => sessionsByContract(sessions), [sessions]);

  const visibleRows = React.useMemo(
    () =>
      (rows ?? []).filter((row) => {
        const standing = standings.get(row.id);
        if (standing !== undefined && !matchesContractFilter(standing, filter)) return false;
        if (caseFilter !== '' && row.caseId !== caseFilter) return false;
        // Krok TOKYFIX1 (prekazka P2/6): hleda se v tom, co makleřka o smlouve
        // ZNA — nazev, typ, adresa a jmena stran. Pravidlo je v `lib`, aby
        // znelo stejne tady i v zalozce zakazky.
        return matchesContractQuery(
          {
            title: row.title,
            typeLabel: t(TYPE_KEY[row.contractType], { locale: LOCALE }),
            propertyText: contractPropertyLine(
              row,
              row.caseId !== null ? (caseIndex?.get(row.caseId) ?? null) : null,
            ).text,
            partyNames: partiesOf(
              details.get(row.id)?.fieldValues ?? row.fieldValues,
              partyRolesFor(row.contractType, details.get(row.id) ?? row),
            ).map((party) => party.values.jmeno),
          },
          query,
        );
      }),
    [rows, standings, filter, caseFilter, query, caseIndex, details],
  );

  /** Vypsana cast prehledu; zbytek si makleřka dotahne („Načíst další"). */
  const pageRows = React.useMemo(() => visibleRows.slice(0, shown), [visibleRows, shown]);
  React.useEffect(() => {
    setShown(LIST_PAGE_SIZE);
  }, [filter, caseFilter, query, caseId]);

  const parties = React.useMemo(
    () =>
      new Map(
        pageRows.map((row) => [
          row.id,
          partiesOf(
            details.get(row.id)?.fieldValues ?? row.fieldValues,
            // Krok PIL1 (vada V18): role urcuje SAMA smlouva, ne jen jeji typ.
            partyRolesFor(row.contractType, details.get(row.id) ?? row),
          ),
        ]),
      ),
    [pageRows, details],
  );

  function openContract(id: string): void {
    void navigate({ to: '/smlouvy/$id', params: { id } });
  }

  async function doPrint(id: string): Promise<void> {
    const win = openPrintWindow();
    const row = rows?.find((r) => r.id === id);
    const result = await printContract(id);
    if (win === null) return;
    writePrintWindow(win, result.html, result.documentCss, row?.title ?? '');
  }

  /** FIX2 bod 3: smazani konceptu z prehledu, s potvrzenim (vzor `invoices.tsx`). */
  async function doDelete(id: string): Promise<void> {
    if (typeof window !== 'undefined' && !window.confirm(t('webAdmin.contracts.list.deleteConfirm', { locale: LOCALE }))) {
      return;
    }
    try {
      await deleteContract(id);
      setRows((current) => current?.filter((r) => r.id !== id) ?? current);
    } catch (err) {
      setError(errorMessage(err) ?? t('webAdmin.contracts.loadError', { locale: LOCALE }));
    }
  }

  /**
   * „Duplikovat" z menu radku: novy koncept z TEZE sablony s PREVZATYMI
   * hodnotami. Zadny novy endpoint — zalozeni + jeden PATCH, stejne jako dela
   * „Nová smlouva" s predvyplnenim (ROZHODNUTI UXS1-C).
   */
  async function doDuplicate(row: ContractDto): Promise<void> {
    setBusy('saving');
    try {
      const detail = details.get(row.id) ?? (await getContract(row.id));
      const created = await createContract({
        title: t('webAdmin.contracts.list.duplicateTitle', {
          locale: LOCALE,
          values: { title: row.title },
        }),
        ...(row.caseId !== null ? { caseId: row.caseId } : {}),
        ...(row.propertyId !== null ? { propertyId: row.propertyId } : {}),
        source: { kind: row.sourceKind, key: row.sourceKey },
        ...(row.subjectType !== null ? { subjectType: row.subjectType } : {}),
      });
      await updateContract(created.id, copyOfValues(detail));
      setBusy('saved');
      openContract(created.id);
    } catch (err) {
      setBusy('error');
      setError(errorMessage(err) ?? t('webAdmin.contracts.loadError', { locale: LOCALE }));
    }
  }

  /**
   * Krok UXS1 bod 5: koncept ze STARSI verze sablony (mustr pred premapovanim
   * ES3 — s pokyny z Wordu v textu) se prevede na aktualni sablonu tak, ze
   * vznikne novy koncept z nove verze a PRENESOU se do nej vyplnene hodnoty.
   * Puvodni koncept se NEMAZE (ROZHODNUTI UXS1-D) — smazat ho jde z tehoz
   * menu, az clovek novy koncept uvidi.
   */
  async function doConvert(row: ContractDto, targetTemplateId: string): Promise<void> {
    setBusy('saving');
    try {
      const detail = details.get(row.id) ?? (await getContract(row.id));
      const created = await createContract({
        title: row.title,
        ...(row.caseId !== null ? { caseId: row.caseId } : {}),
        ...(row.propertyId !== null ? { propertyId: row.propertyId } : {}),
        source: { kind: 'office', key: targetTemplateId },
        ...(row.subjectType !== null ? { subjectType: row.subjectType } : {}),
      });
      await updateContract(created.id, copyOfValues(detail));
      setBusy('saved');
      openContract(created.id);
    } catch (err) {
      setBusy('error');
      setError(errorMessage(err) ?? t('webAdmin.contracts.loadError', { locale: LOCALE }));
    }
  }

  /**
   * JEDNA akce radku (predloha: sloupec „Další krok"). Co ktera znamena,
   * rozhoduje `contractStanding`; tady je jen obsluha.
   */
  function runRowAction(row: ContractDto, action: ContractNextAction): void {
    if (action === 'remind') {
      const session = sessionByContract.get(row.id);
      if (session !== undefined) {
        setBusy('saving');
        void remindSignature(session.id).then(
          () => setBusy('saved'),
          () => setBusy('error'),
        );
        return;
      }
    }
    // Podepsat, pokracovat, nahrat sken i stahnout vedou do smlouvy — tam je
    // podpisova zalozka, formular i dokument. Zadna akce se nedela „na slepo"
    // z radku prehledu.
    void navigate({
      to: '/smlouvy/$id',
      params: { id: row.id },
      ...(action === 'sign' || action === 'upload-scan' || action === 'download'
        ? { search: { zalozka: 'signature' as const } }
        : {}),
    });
  }

  const newContractLink = (
    <Link
      to="/smlouvy/nova"
      search={caseId !== undefined ? { zakazka: caseId } : {}}
      className={pageHead ? 'rh-v4-btn rh-v4-btn--filled' : 'rh-v4-btn rh-v4-btn--filled'}
      data-testid="contracts-new-button"
    >
      {t('webAdmin.contracts.newButtonPlus', { locale: LOCALE })}
    </Link>
  );

  /**
   * Krok UXK3b bod 1: „Mustry kanceláře" uz nestoji trvale vedle tabulky
   * (`/smlouvy-sablony` ma vlastni stranku se spravou) — v prehledu zustava
   * jen tenhle odkaz v hlavicce, vedle primarni akce.
   */
  const manageTemplatesLink = (
    <Link to="/smlouvy-sablony" className="rh-v4-btn rh-v4-btn--plain" data-testid="contracts-manage-templates">
      {t('webAdmin.contracts.list.manageTemplatesLink', { locale: LOCALE })}
    </Link>
  );

  /**
   * STAV CELE OBRAZOVKY NA JEDNOM MISTE (krok QAFIX13, prekazka P2/6).
   *
   * Do tohoto kroku rikal „uz vim, co ukazuju" jen PAS PILULEK (MRGP1), zato
   * koren obrazovky (`contracts-screen`) byl v DOM hned — jeste nad prazdnym
   * `rows`. Kdokoli (clovek i mereni), kdo se podle korene ridi, tedy cetl
   * obrazovku ve chvili, kdy na ni stoji „Načítám…" a TABULKA V DOM VUBEC
   * NENI: pruchod P2 krok 6 tak 21. 10. 2026 21:16 nahlasil „smlouva není
   * v přehledu Smlouvy (pilulka Aktivní, 0 řádků)" nad obrazovkou, kde zadny
   * seznam jeste nebyl (snimek v trace: „Načítám…", `GET /v1/contracts` jeste
   * bezelo). Hlaska mluvila o filtru, pricina byla jen nedoctena obrazovka.
   *
   * `counts === null` je uz z MRGP1 presne „seznam smluv NEBO podepisovani
   * jeste nedoslo" (`contractCountsIfKnown`), takze stav ma jeden zdroj pravdy
   * pro koren i pro pilulky — dve kopie by se rozesly.
   */
  const screenState: 'loading' | 'ready' | 'error' =
    error !== undefined ? 'error' : counts === null ? 'loading' : 'ready';

  return (
    <div data-testid="contracts-screen" data-state={screenState}>
      {/* JEDNA hlavicka stranky: nadpis + veta predlohy, vpravo primarni akce. */}
      {pageHead ? (
        <PageHead
          title={t('webAdmin.contracts.title', { locale: LOCALE })}
          subtitle={t('webAdmin.contracts.list.lede', { locale: LOCALE })}
          titleTestId="contracts-head-title"
          action={newContractLink}
          menu={manageTemplatesLink}
        />
      ) : (
      <div className="reos-doc-head" data-testid="contracts-head">
        <div className="reos-doc-head__grow">
          {caseTitle !== undefined && (
            <div className="reos-crumb" data-testid="contracts-crumb">
              <Link to="/zakazky">{t('webAdmin.contracts.crumbCases', { locale: LOCALE })}</Link>
              {' › '}
              {caseTitle}
            </div>
          )}
          {/* Na vlastni strance je to hlavni nadpis strany; v zalozce zakazky
              je hlavni nadpis zakazka, takze tady je uroven nize. */}
          {React.createElement(
            caseTitle === undefined ? 'h1' : 'h2',
            { className: 'reos-doc-head__title', 'data-testid': 'contracts-head-title' },
            t('webAdmin.contracts.title', { locale: LOCALE }),
          )}
          <div className="rh-v4-head__lede">{t('webAdmin.contracts.list.lede', { locale: LOCALE })}</div>
        </div>
        <div className="reos-doc-head__actions">{newContractLink}</div>
      </div>
      )}

      {/*
       * Krok UXK3b bod 1: „Lhůty ze smluv" uz nestoji trvale vedle tabulky —
       * jen nizky souhrnny radek NAD filtry, a jen kdyz nejaka OTEVRENA lhuta
       * skutecne existuje (prazdny stav nema co hlasit). V zalozce zakazky
       * (`caseId` zadane) zustava puvodni bocni panel se vsemi lhutami.
       */}
      {caseId === undefined && openDeadlines(deadlines).length > 0 && (
        <p className="rh-v4-deadlines-banner" data-testid="contracts-deadlines-banner">
          {t('webAdmin.contracts.list.deadlinesBanner', {
            locale: LOCALE,
            values: {
              count: openDeadlines(deadlines).length,
              date: formatDueDate(openDeadlines(deadlines)[0]?.dueDate ?? ''),
              title: openDeadlines(deadlines)[0]?.title ?? '',
            },
          })}
        </p>
      )}

      {/* Filtr PODLE TOHO, U KOHO SMLOUVA JE — pocty z teze funkce. */}
      <div
        className="rh-v4-cfilters"
        // Nalez V2 (QA-UX 21. 10. 2026): NE `tablist` — v pasu stoji i vyber
        // zakazky a hledani, a zadny `tabpanel` k pilulkam neexistuje. `group`
        // snese potomky jakekoli role a popisek pasu zustava.
        role="group"
        aria-label={t('webAdmin.contracts.list.colStatus', { locale: LOCALE })}
        data-testid="contracts-filters"
        // „ready" az kdyz jsou pocty SKUTECNE znamé (krok MRGP1) — jinak by
        // je kdokoli (clovek i sada) precetl jako nuly. Od QAFIX13 je to TEN
        // SAMY stav jako na koreni obrazovky (`screenState`), ne druha kopie.
        data-state={screenState}
      >
        {CONTRACT_WHERE_FILTERS.map((id) => (
          <button
            key={id}
            type="button"
            // Nalez V2: prepinaci filtr je prepinaci tlacitko (`aria-pressed`,
            // tentyz vzor jako jinde v adminu), ne zalozka bez panelu.
            aria-pressed={filter === id}
            className={`rh-v4-cfilter${filter === id ? ' is-on' : ''}${id === 'na-tobe' ? ' rh-v4-cfilter--onyou' : ''}`}
            onClick={() => setFilter(id)}
            data-testid={`contracts-filter-${id}`}
          >
            {t(CONTRACT_FILTER_LABEL_KEY[id], { locale: LOCALE })}
            {counts !== null ? (
              <>
                {' '}
                <span className="rh-v4-cfilter__num">{counts[id]}</span>
              </>
            ) : null}
          </button>
        ))}
        {caseId === undefined && (
          <label className="rh-v4-cfilters__case">
            <span className="rh-sr-only">{t('webAdmin.contracts.list.colCase', { locale: LOCALE })}</span>
            <select
              value={caseFilter}
              onChange={(e) => setCaseFilter(e.target.value)}
              data-testid="contracts-filter-case"
            >
              <option value="">{t('webAdmin.contracts.list.filterCaseAll', { locale: LOCALE })}</option>
              {[...new Set((rows ?? []).map((r) => r.caseId).filter((id): id is string => id !== null))].map(
                (id) => (
                  <option key={id} value={id}>
                    {caseIndex?.get(id) ?? id}
                  </option>
                ),
              )}
            </select>
          </label>
        )}
        {/* Krok TOKYFIX1 (prekazka P2/6): hledani stoji u filtru, ne nad
            tabulkou — makleřka nejdriv zvoli „kde" a pak hleda „co". */}
        <label className="rh-v4-cfilters__search">
          <span className="rh-sr-only">{t('webAdmin.contracts.list.searchLabel', { locale: LOCALE })}</span>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('webAdmin.contracts.list.searchPlaceholder', { locale: LOCALE })}
            data-testid="contracts-search"
          />
        </label>
      </div>

      {error !== undefined && <p role="alert" className="rh-a2-error">{error}</p>}
      {rows === null && error === undefined && (
        <p>{t('webAdmin.contracts.loading', { locale: LOCALE })}</p>
      )}

      {/*
       * Krok UXK3b bod 1: na SAMOSTATNE strance (`caseId === undefined`) jde
       * tabulka pres celou sirku — bocni sloupec („Lhůty ze smluv" + „Mustry
       * kanceláře") zustal jen v zalozce zakazky, kde je kratsi a nenese
       * trvalou zatez (viz `deadlinesBanner` a `manageTemplatesLink` vyse).
       *
       * Krok UXF2 bod 2: samotne vypusteni bocniho sloupce nestacilo —
       * `.rh-v4-cols` je dvousloupcova mrizka, takze tabulka dal dostavala
       * jen 63 % sirky. `--full` z ni na teto strance dela jeden sloupec.
       */}
      <div
        className={
          caseId === undefined ? 'rh-v4-cols rh-v4-cols--full' : 'rh-v4-cols rh-v4-cols--contracts'
        }
      >
        <div className="rh-v4-col">
          {rows !== null && visibleRows.length === 0 && (
            <EmptyState
              title={
                query.trim() === ''
                  ? t('webAdmin.contracts.list.emptyFilter', {
                      locale: LOCALE,
                      values: { filter: t(CONTRACT_FILTER_LABEL_KEY[filter], { locale: LOCALE }) },
                    })
                  : t('webAdmin.contracts.list.emptySearch', {
                      locale: LOCALE,
                      values: {
                        query: query.trim(),
                        filter: t(CONTRACT_FILTER_LABEL_KEY[filter], { locale: LOCALE }),
                      },
                    })
              }
            />
          )}
          {rows !== null && visibleRows.length > 0 && (
        <table className="rh-v4-ctable" data-testid="contracts-table">
          <thead>
            <tr>
              <th>{t('webAdmin.contracts.list.colTitle', { locale: LOCALE })}</th>
              <th>{t('webAdmin.contracts.list.colParties', { locale: LOCALE })}</th>
              <th>{t('webAdmin.contracts.list.colStatus', { locale: LOCALE })}</th>
              <th>{t('webAdmin.contracts.list.colNextStep', { locale: LOCALE })}</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row) => {
              const standing = standings.get(row.id) ?? null;
              const rowParties = parties.get(row.id) ?? [];
              const chips = partyChips({
                session: sessionByContract.get(row.id) ?? null,
                parties: rowParties.map((p) => ({
                  role: p.role,
                  index: p.index,
                  filled: p.values.jmeno.trim() !== '',
                })),
                invitations,
                roleName: (role, index) =>
                  partyRoleName(rowParties, { ...(rowParties[0] ?? EMPTY_PARTY), role: role as PartyRole, index }),
              });
              const newerTemplateId =
                row.sourceKind === 'office'
                  ? supersedingTemplateId(officeTemplates, row.sourceKey)
                  : null;
              const ownTitle = contractOwnTitle({
                title: row.title,
                typeLabel: t(TYPE_KEY[row.contractType], { locale: LOCALE }),
              });
              const property = contractPropertyLine(
                row,
                row.caseId !== null ? (caseIndex?.get(row.caseId) ?? null) : null,
              );
              const cover = row.propertyId === null ? null : (covers.get(row.propertyId) ?? null);
              return (
                <tr
                  key={row.id}
                  // Klikatelny radek (predloha): kliknuti kamkoli otevre editor.
                  // Nazev dokumentu je navic skutecny odkaz, takze funguje
                  // klavesnice, prostredni tlacitko i „otevrit v nove karte".
                  onClick={() => openContract(row.id)}
                  className={`rh-v4-crow${standing?.where === 'na-tobe' ? ' rh-v4-crow--onyou' : ''}`}
                  data-where={standing?.where ?? ''}
                  data-testid={`contracts-row-${row.id}`}
                >
                  {/* Tridy bunek urcuji PORADI na mobilni karte (krok UXF2 bod 2). */}
                  <td
                    className="rh-v4-crow__cdoc"
                    data-label={t('webAdmin.contracts.list.colTitle', { locale: LOCALE })}
                  >
                    <div className="rh-v4-crow__doc">
                      <span className="rh-v4-thumb rh-v4-thumb--row" aria-hidden="true">
                        {/* Krok UXK3b bod 1: smlouva BEZ nemovitosti nikdy nedostane
                            fotku (zadna kaskada nema odkud) — misto vety „Fotka po
                            nahrání" tak stoji neutralni ikona typu, stejny vzor jako
                            karta „Další krok" v `dnesek.tsx`. */}
                        {row.propertyId === null ? (
                          <DocumentCoverPlaceholder
                            icon={<FileSignature size={20} strokeWidth={1.8} />}
                            label={t('webAdmin.caseThumb.documentKindContract', { locale: LOCALE })}
                            testIdPrefix={`contracts-thumb-${row.id}`}
                          />
                        ) : (
                          <PropertyImage
                            photoUrl={cover?.thumbUrl ?? null}
                            propertyId={cover?.propertyId ?? null}
                            lat={cover?.lat ?? null}
                            lon={cover?.lon ?? null}
                            size={{ width: 112, height: 112 }}
                            testIdPrefix={`contracts-thumb-${row.id}`}
                          />
                        )}
                      </span>
                      <span className="rh-v4-crow__texts">
                        <Link
                          to="/smlouvy/$id"
                          params={{ id: row.id }}
                          className="rh-v4-crow__name"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`contracts-open-${row.id}`}
                        >
                          {t(TYPE_KEY[row.contractType], { locale: LOCALE })}
                        </Link>
                        {/*
                          Krok TOKYFIX1 (prekazka P2/6): KRATKY VLASTNI NAZEV
                          V RADKU, na JEDNOM radku. Dlouhy pravni nazev z UXF2
                          bodu 2 (nalez N3) tu dal NENI — rozhoduje
                          `contractOwnTitle`, duvod je u ni.
                        */}
                        {ownTitle !== null && (
                          <span
                            className="rh-v4-crow__sub rh-v4-crow__own"
                            title={ownTitle}
                            data-testid={`contracts-title-${row.id}`}
                          >
                            {ownTitle}
                          </span>
                        )}
                        <span
                          className={`rh-v4-crow__sub${property.missing ? ' rh-v4-crow__sub--missing' : ''}`}
                          data-testid={`contracts-property-${row.id}`}
                        >
                          {property.missing
                            ? t('webAdmin.contracts.list.propertyMissing', { locale: LOCALE })
                            : property.text}
                        </span>
                        {/*
                          Krok UXF2 bod 2 (nalez #30, N3) — DLOUHY PRAVNI NAZEV
                          JE AZ V DETAILU (`contracts-head-title`). V radku
                          zustava jen mustr a posledni zmena; na 1440 se tim
                          vejdou ctyri smlouvy bez posouvani a na 390 px je
                          tahle radka skryta uplne (sazba v `ux-v4.css`).
                        */}
                        <span
                          className="rh-v4-crow__meta rh-v4-crow__docmeta"
                          data-testid={`contracts-meta-${row.id}`}
                        >
                          {[templates.get(row.sourceKey) ?? '', formatDate(row.updatedAt)]
                            .filter((part) => part !== '')
                            .join(' · ')}
                        </span>
                        {newerTemplateId !== null && (
                          <span className="rh-v4-crow__legacy">
                            <span className="rh-v4-pill rh-v4-pill--warn" data-testid={`contracts-legacy-${row.id}`}>
                              {t('webAdmin.contracts.list.legacyTemplate', { locale: LOCALE })}
                            </span>{' '}
                            <button
                              type="button"
                              className="rh-v4-btn rh-v4-btn--plain"
                              onClick={(e) => {
                                e.stopPropagation();
                                void doConvert(row, newerTemplateId);
                              }}
                              data-testid={`contracts-convert-${row.id}`}
                            >
                              {t('webAdmin.contracts.list.convertToCurrent', { locale: LOCALE })}
                            </button>
                          </span>
                        )}
                        {caseId === undefined && (
                          <span
                            className="rh-v4-crow__meta"
                            data-testid={`contracts-case-${row.id}`}
                          >
                            {/* Zastavuji se jen KLIKY NA OVLADACI PRVKY uvnitr
                                (odkaz na zakazku, prirazeni) — cely radek jinak
                                otevira smlouvu. */}
                            {row.caseId !== null ? (
                              <Link
                                to="/zakazky/$id"
                                params={{ id: row.caseId }}
                                search={{ sekce: 'smlouva' }}
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`contracts-case-link-${row.id}`}
                              >
                                {/* Kdyz uz nazev zakazky stoji v radku nemovitosti,
                                    odkaz ho neopakuje — dvakrat tentyz text vedle
                                    sebe vypada jako vada. */}
                                {property.text === (caseIndex?.get(row.caseId) ?? row.caseId)
                                  ? t('webAdmin.contracts.list.caseLinkShort', { locale: LOCALE })
                                  : (caseIndex?.get(row.caseId) ?? row.caseId)}
                              </Link>
                            ) : assigningRowId === row.id ? (
                              <span className="rh-v4-crow__assign" onClick={(e) => e.stopPropagation()}>
                                <input
                                  value={assignQuery}
                                  onChange={(e) => setAssignQuery(e.target.value)}
                                  placeholder={t('webAdmin.contracts.new.casePlaceholder', { locale: LOCALE })}
                                  data-testid={`contracts-assign-search-${row.id}`}
                                />
                                {assignQuery.trim() !== '' && caseIndex !== null && (
                                  <span className="rh-v4-crow__assign-list">
                                    {[...caseIndex.entries()]
                                      .filter(([, name]) =>
                                        name.toLowerCase().includes(assignQuery.trim().toLowerCase()),
                                      )
                                      .slice(0, ASSIGN_OPTIONS_LIMIT)
                                      .map(([id, name]) => (
                                        <button
                                          key={id}
                                          type="button"
                                          className="rh-v4-btn rh-v4-btn--plain"
                                          onClick={() => void assignCase(row.id, id)}
                                          data-testid={`contracts-assign-option-${row.id}-${id}`}
                                        >
                                          {name}
                                        </button>
                                      ))}
                                  </span>
                                )}
                                <button
                                  type="button"
                                  className="rh-v4-btn rh-v4-btn--plain"
                                  onClick={() => {
                                    setAssigningRowId(null);
                                    setAssignQuery('');
                                  }}
                                >
                                  {t('webAdmin.contracts.new.cancel', { locale: LOCALE })}
                                </button>
                                {assignStatus === 'error' && (
                                  <span role="alert">{t('webAdmin.contracts.loadError', { locale: LOCALE })}</span>
                                )}
                              </span>
                            ) : (
                              <button
                                type="button"
                                className="rh-v4-btn rh-v4-btn--plain"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setAssigningRowId(row.id);
                                }}
                                data-testid={`contracts-assign-case-${row.id}`}
                              >
                                {t('webAdmin.contracts.list.assignCase', { locale: LOCALE })}
                              </button>
                            )}
                          </span>
                        )}
                      </span>
                    </div>
                  </td>
                  <td
                    className="rh-v4-crow__cparties"
                    data-label={t('webAdmin.contracts.list.colParties', { locale: LOCALE })}
                    data-testid={`contracts-parties-${row.id}`}
                  >
                    <span className="rh-v4-chips">
                      {chips.map((chip) => (
                        <span
                          key={chip.id}
                          className={`rh-v4-chip rh-v4-chip--${chip.tone}`}
                          data-testid={`contracts-chip-${row.id}-${chip.id}`}
                        >
                          {t(chip.stateKey, { locale: LOCALE, values: { role: chip.role } })}
                        </span>
                      ))}
                    </span>
                  </td>
                  <td
                    className="rh-v4-crow__cstate"
                    data-label={t('webAdmin.contracts.list.colStatus', { locale: LOCALE })}
                    data-testid={`contracts-status-${row.id}`}
                  >
                    {standing !== null && (
                      <>
                        <span
                          className={`rh-v4-crow__state rh-v4-crow__state--${standing.where}`}
                        >
                          {t(standing.stateKey, { locale: LOCALE, values: standing.stateValues })}
                        </span>
                        {standing.whyKey !== null && (
                          <span className="rh-v4-crow__why">
                            {t(standing.whyKey, { locale: LOCALE, values: humanValues(standing.whyValues) })}
                          </span>
                        )}
                        {standing.percent !== null && standing.where === 'na-tobe' && (
                          <V4Bar
                            percent={standing.percent}
                            size="xs"
                            label={t(standing.stateKey, { locale: LOCALE, values: standing.stateValues })}
                            testId={`contracts-progress-${row.id}`}
                          />
                        )}
                      </>
                    )}
                  </td>
                  {/* JEDNA akce + nabidka „⋯" (predloha: sloupec „Další krok"). */}
                  <td
                    className="rh-v4-crow__next reos-row-menu"
                    data-label={t('webAdmin.contracts.list.colNextStep', { locale: LOCALE })}
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`contracts-actions-${row.id}`}
                  >
                    {standing !== null && (
                      <button
                        type="button"
                        className={`rh-v4-btn ${standing.where === 'na-tobe' ? 'rh-v4-btn--filled' : 'rh-v4-btn--tinted'}`}
                        onClick={() => runRowAction(row, standing.action)}
                        data-testid={`contracts-next-${row.id}`}
                      >
                        {t(CONTRACT_ACTION_LABEL_KEY[standing.action], { locale: LOCALE })}
                      </button>
                    )}
                    <button
                      type="button"
                      className="rh-v4-nav__dots reos-row-menu__button"
                      aria-haspopup="menu"
                      aria-expanded={openMenuId === row.id}
                      aria-label={t('webAdmin.contracts.list.colActions', { locale: LOCALE })}
                      onClick={() => setOpenMenuId(openMenuId === row.id ? null : row.id)}
                      data-testid={`contracts-menu-${row.id}`}
                    >
                      ⋯
                    </button>
                    {openMenuId === row.id && (
                      <div className="reos-row-menu__list" role="menu">
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setOpenMenuId(null);
                            void doPrint(row.id);
                          }}
                          data-testid={`contracts-action-print-${row.id}`}
                        >
                          {t('webAdmin.contracts.list.actionPrint', { locale: LOCALE })}
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setOpenMenuId(null);
                            void doDuplicate(row);
                          }}
                          data-testid={`contracts-action-duplicate-${row.id}`}
                        >
                          {t('webAdmin.contracts.list.actionDuplicate', { locale: LOCALE })}
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setOpenMenuId(null);
                            void doDelete(row.id);
                          }}
                          data-testid={`contracts-action-delete-${row.id}`}
                        >
                          {t('webAdmin.contracts.list.actionDelete', { locale: LOCALE })}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
          )}
          {/* Krok TOKYFIX1 (prekazka P2/6): kolik radku prehled ukazuje
              a kolik jich jeste je — misto pevneho stropu bez zminky. */}
          {rows !== null && visibleRows.length > pageRows.length && (
            <div className="rh-v4-cmore">
              <button
                type="button"
                className="rh-v4-btn rh-v4-btn--plain"
                onClick={() => setShown((current) => current + LIST_PAGE_SIZE)}
                data-testid="contracts-load-more"
              >
                {t('webAdmin.contracts.list.loadMore', {
                  locale: LOCALE,
                  values: { count: visibleRows.length - pageRows.length },
                })}
              </button>
            </div>
          )}
          {rows !== null && visibleRows.length > 0 && (
            <p className="rh-v4-crow__meta" data-testid="contracts-list-count">
              {t('webAdmin.contracts.list.countNote', {
                locale: LOCALE,
                values: { shown: pageRows.length, total: visibleRows.length },
              })}
            </p>
          )}
          <SaveStatus
            status={busy}
            texts={{
              saving: t('webAdmin.contracts.saving', { locale: LOCALE }),
              saved: t('webAdmin.contracts.saved', { locale: LOCALE }),
              error: t('webAdmin.contracts.loadError', { locale: LOCALE }),
            }}
          />
          <p className="rh-v4-crow__meta" data-testid="contracts-list-note">
            {t('webAdmin.contracts.list.note', { locale: LOCALE })}
          </p>
        </div>

        {/*
         * Vpravo: lhuty ze smluv (krok T5) a mustry kancelare (predloha).
         * Jen v zalozce zakazky — na samostatne strance nahrazuje bocni
         * panel `deadlinesBanner` nad filtry a `manageTemplatesLink` v
         * hlavicce (krok UXK3b bod 1).
         */}
        {caseId !== undefined && (
        <div className="rh-v4-col">
          <V4Group testId="contracts-deadlines" label={t('webAdmin.contracts.list.deadlinesHeading', { locale: LOCALE })}>
            <div className="rh-v4-card">
              <div className="rh-v4-h2">{t('webAdmin.contracts.list.deadlinesHeading', { locale: LOCALE })}</div>
              <p className="rh-v4-crow__why">{t('webAdmin.contracts.list.deadlinesNote', { locale: LOCALE })}</p>
            </div>
            {openDeadlines(deadlines).length === 0 ? (
              <V4Row
                title={t('webAdmin.contracts.list.deadlinesEmpty', { locale: LOCALE })}
                testId="contracts-deadlines-empty"
              />
            ) : (
              openDeadlines(deadlines).map((row) => (
                <V4Row
                  key={row.id}
                  lead={<span className="rh-num">{formatDueDate(row.dueDate)}</span>}
                  title={row.title}
                  sub={deadlineSourceText(row, rows ?? [])}
                  testId={`contracts-deadline-${row.id}`}
                />
              ))
            )}
          </V4Group>

          <V4Group testId="contracts-office-templates" label={t('webAdmin.contracts.list.officeTemplatesHeading', { locale: LOCALE })}>
            <div className="rh-v4-card">
              <div className="rh-v4-h2">{t('webAdmin.contracts.list.officeTemplatesHeading', { locale: LOCALE })}</div>
            </div>
            {officeTemplates.filter((tpl) => tpl.status === 'active').length === 0 ? (
              <V4Row
                title={t('webAdmin.contracts.list.officeTemplatesEmpty', { locale: LOCALE })}
                testId="contracts-office-templates-empty"
              />
            ) : (
              officeTemplates
                .filter((tpl) => tpl.status === 'active')
                .map((tpl) => (
                  <V4Row
                    key={tpl.id}
                    title={tpl.name}
                    sub={t(TYPE_KEY[tpl.contractType], { locale: LOCALE })}
                    value={t('webAdmin.contracts.list.officeTemplateVersion', {
                      locale: LOCALE,
                      values: { version: tpl.version },
                    })}
                    testId={`contracts-office-template-${tpl.id}`}
                  />
                ))
            )}
            <V4Row
              title={t('webAdmin.contracts.list.officeTemplatesUpload', { locale: LOCALE })}
              chevron
              render={(className, content) => (
                <Link to="/smlouvy-sablony" className={className} data-testid="contracts-office-templates-upload">
                  {content}
                </Link>
              )}
            />
            {/* Predloha ma „Mustry a klauzule" v hlavicce vedle „+ Nová
                smlouva"; na 390 px z nadpisu obrazovky zbylo „Smlo…" (nalez
                ze ziveho overeni), takze odkaz stoji tam, kam patri obsahem. */}
            <V4Row
              title={t('webAdmin.contracts.list.officeTemplatesLink', { locale: LOCALE })}
              chevron
              render={(className, content) => (
                <Link to="/smlouvy-sablony" className={className} data-testid="contracts-templates-link">
                  {content}
                </Link>
              )}
            />
          </V4Group>
        </div>
        )}
      </div>
    </div>
  );
}

/**
 * Hodnoty do vety „proč / do kdy" v lidskem tvaru. `contract-standing.ts` je
 * ciste pravidlo a nese cas tak, jak ho vratil server (ISO) — do vety ale
 * patri „4. 10.", ne `2026-10-04T15:54:38.288Z` (nalez ze ziveho overeni).
 */
function humanValues(
  values: Readonly<Record<string, string | number>>,
): Record<string, string | number> {
  const out: Record<string, string | number> = { ...values };
  const date = out['date'];
  if (typeof date === 'string') out['date'] = formatDate(date);
  return out;
}

/**
 * Kolik radku prehled vypise najednou (a pro kolik si dotahne detail
 * s osnovou). Krok TOKYFIX1: „Načíst další" pridava dalsi takovou porci.
 */
const LIST_PAGE_SIZE = 20;

/**
 * Kolik zakazek nabidne hledani pri prirazeni smlouvy k zakazce.
 *
 * TENTYZ KONCEPT jako `CASE_OPTIONS_LIMIT` u faktury — a tedy i TATAZ
 * vada, kterou u faktury resil krok TOKYFIX3 (prekazka P7/2): `caseIndex`
 * je plneny vzestupne podle zalozeni, takze pri deseti a vic zakazkach na
 * stejne adrese oriznuti zahodi TU NEJNOVEJSI. Tady se zamerne nic nemeni:
 * nabidka stoji na `fetchCaseAddressIndex` (sken cele kancelare), ne na
 * serverovem hledani, takze sjednotit to znamena prevest „Novou smlouvu"
 * na `searchCasesByName` — samostatny krok, ne zasah mimo zadani TOKYFIX3.
 */
const ASSIGN_OPTIONS_LIMIT = 10;

/**
 * Krok APP2 bod 1: prodleva, nez se hledani zakazky zepta serveru (psani
 * adresy). Vlastni hodnota obrazovky, ne obchodni pravidlo — nejkratsi
 * smysluplny dotaz i vlastni hledani zije v `searchCasesByName`
 * (`lib/case-address-lookup.ts`), tady je jen rychlost reakce na klavesy.
 * Tutez volbu ma „Nová faktura" (`invoices.tsx`, krok PROP3B).
 */
const CASE_SEARCH_DEBOUNCE_MS = 250;

/**
 * Prazdna strana pro `partyRoleName`, kdyz stitek stran vznika ze SEZENI
 * (tam zname jen `partyKey` a index, ne hodnoty polí).
 */
const EMPTY_PARTY: Party = {
  role: 'pronajimatel',
  index: 0,
  values: { jmeno: '', rc: '', adresa: '', email: '', telefon: '', podil: '' },
  source: 'broker',
  confirmed: false,
};

/** Lhuty, ktere jeste nejsou odskrtnute, nejblizsi nahore (predloha: tri radky). */
function openDeadlines(rows: readonly ContractDeadlineDto[]): readonly ContractDeadlineDto[] {
  return [...rows]
    .filter((row) => row.status === 'open')
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate));
}

/** Druhy radek lhuty: ze ktere smlouvy a z jakeho pole je (predloha: „čl. III"). */
function deadlineSourceText(
  deadline: ContractDeadlineDto,
  rows: readonly ContractDto[],
): string {
  const contract = rows.find((row) => row.id === deadline.contractId);
  const name = contract === undefined ? '' : t(TYPE_KEY[contract.contractType], { locale: LOCALE });
  return [name, deadline.sourceField].filter((x) => x !== '').join(' · ');
}


// -------------------------------------------------------------- nova smlouva

/**
 * NOVA SMLOUVA — `/smlouvy/nova` (krok UXS1 bod 1).
 *
 * V mockupu je to samostatna obrazovka; do ted to byla zalozka nad seznamem,
 * takze nemela vlastni adresu a Zpet z ni vedlo mimo smlouvy. Po zalozeni
 * konceptu se otevre editor `/smlouvy/:id`.
 */
export function NewContractScreen({ caseId }: { readonly caseId?: string | undefined }): React.JSX.Element {
  const navigate = useNavigate();
  return (
    <NewContractView
      caseId={caseId}
      onCreated={(id) => {
        void navigate({ to: '/smlouvy/$id', params: { id } });
      }}
      onCancel={() => {
        void navigate({ to: '/smlouvy' });
      }}
    />
  );
}

/**
 * NOVA SMLOUVA — jedna obrazovka, TRI KROKY (krok SML1 bod 2, predloha
 * `docs/design/ux-v3/SmlouvaNova.dc.html` ve vzhledu UX v4):
 *
 *   1. ke ktere zakazce (predvybrana, kdyz se jde ze zakazky; „Bez zakázky"
 *      je rovnocenna volba — R.B. 4. 10.: „A když to REOS nemá a jen chci
 *      napsat smlouvu?"),
 *   2. jaka smlouva — u KAZDEHO typu uroven podpisu, kterou rozhodla politika
 *      `levels.ts` na serveru (ne vycet v UI),
 *   3. podle jakeho textu — mustr kancelare / vzor REOS / nahrat .docx.
 *
 * Vpravo „Co se stane po vytvoření" s POCTY Z ENGINU (`fill-plan.ts`): kolik
 * udaju doplni REOS z katastru, kolik klienti pres odkaz a kolik makler.
 * Volba „Rovnou poslat klientům odkaz" se chova jako dnesni pozvanky —
 * na preview (`REOS_OUTBOUND_SEND=off`) se nic neodesle a obrazovka to rekne.
 */
function NewContractView({
  caseId,
  onCreated,
  onCancel,
}: {
  readonly caseId: string | undefined;
  readonly onCreated: (id: string) => void;
  readonly onCancel: () => void;
}): React.JSX.Element {
  const [demoTemplates, setDemoTemplates] = React.useState<DemoTemplateSummaryDto[]>([]);
  const [officeTemplates, setOfficeTemplates] = React.useState<OfficeTemplateDto[]>([]);
  const [catalogs, setCatalogs] = React.useState<ContractCatalogsDto | null>(null);
  /** Podklad „Nová smlouva" ze serveru: uroven podpisu a rozpad „kdo doplni". */
  const [plan, setPlan] = React.useState<NewContractPlanDto | null>(null);
  /**
   * Podklad se nenacetl (server odmitl, sit vypadla). Bez tohohle stavu
   * zustavala uroven podpisu navzdy na „Načítám…" — 11. 10. 2026 to tak
   * videl makler na preview, kdyz API vratilo 429: obrazovka tvrdila, ze
   * jeste nacita, a uz nikdy nic nenacetla.
   */
  const [planFailed, setPlanFailed] = React.useState(false);
  const [sourceKind, setSourceKind] = React.useState<'office' | 'demo' | 'upload'>('office');
  const [sourceKey, setSourceKey] = React.useState('');
  const [contractType, setContractType] = React.useState<ContractType | null>(null);
  const [title, setTitle] = React.useState('');
  const [subjectKey, setSubjectKey] = React.useState('');
  /**
   * Krok D1 bod 4: odeslani NIKDY neni predzaskrtnute. Zalozit navrh a poslat
   * ho klientovi jsou dva kroky — makler se musi rozhodnout, ne odskrtavat.
   */
  const [sendLinks, setSendLinks] = React.useState(false);
  const [status, setStatus] = React.useState<SaveStatusKind>('idle');
  const [errorDetail, setErrorDetail] = React.useState<string | undefined>(undefined);
  const user = useSession((s) => s.user);
  const [supplier, setSupplier] = React.useState<SupplierDto | null>(null);
  // ADM1 bod 2: standalone "/smlouvy" nema zadny zpusob, jak smlouvu privazat
  // k existujici zakazce - `caseId` posilana do API byla vzdy null. `caseId`
  // prop (kdyz je smlouva zalozena ZE zakazky) ma prednost pred vyberem tady.
  const [caseIndex, setCaseIndex] = React.useState<ReadonlyMap<string, string> | null>(null);
  const [caseQuery, setCaseQuery] = React.useState('');
  const [pickedCaseId, setPickedCaseId] = React.useState<string | null>(null);
  /**
   * Doplneni zadani 4. 10.: smlouva BEZ ZAKAZKY musi jit napsat do konce.
   * `noCase` je VOLBA, ne „jeste jsem nevybral" — proto vlastni stav a ne
   * `pickedCaseId === null` (to je jen „nevybrano").
   */
  const [noCase, setNoCase] = React.useState(false);
  const resolvedCaseId = caseId ?? pickedCaseId ?? undefined;

  /**
   * Krok APP2 bod 1 — ZAKAZKU HLEDA SERVER, NE PROHLIZEC.
   *
   * Do APP2 si tenhle krok stahl CELY seznam zakazek kancelare
   * (`fetchCaseAddressIndex`, az `CASE_SCAN_MAX_PAGES` dotazu) a hledal v nem
   * v prohlizeci — presne to, co u faktury resil krok PROP3B a co komentar
   * u `ASSIGN_OPTIONS_LIMIT` oznacil jako „samostatny krok". Tenhle je ten
   * krok: jeden dotaz `GET /v1/cases?q=` od dvou znaku, takze nabidka nemuze
   * zustat prazdna kvuli kvote (429) a nejnovejsi zakazka v ni je
   * (razeni resi `searchCasesByName`).
   *
   * Krok NOC1 bod 2: ani tady nesmi kvota skoncit neodchycenou chybou.
   */
  React.useEffect(() => {
    if (caseId !== undefined) return undefined;
    const needle = caseQuery.trim();
    if (needle === '') {
      setCaseIndex(null);
      return undefined;
    }
    let alive = true;
    // Psani je rychlejsi nez sit — dotaz jde az po odmlce (vzor `klienti.tsx`).
    const timer = window.setTimeout(() => {
      void retryOnRateLimit(() => fetchCaseAddressIndex({ q: needle })).then(
        ({ byCaseId }) => {
          if (alive) setCaseIndex(byCaseId);
        },
        () => undefined,
      );
    }, CASE_SEARCH_DEBOUNCE_MS);
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [caseId, caseQuery]);

  /*
   * Krok P8F6 bod 4 (nalez P8-N13) — SMLOUVA ZE ZAKAZKY UKAZOVALA UUID.
   * Index se pro prop `caseId` zamerne nestahuje (NOC1 bod 2), takze krok
   * „Ke které zakázce" nemel nazev odkud vzit a vypsal id. Tri dotazy na
   * JEDNU zakazku (adresa + jednotka z karty) misto skenu kancelare; pri
   * selhani zbyde „Bez názvu", nikdy ne interni id.
   */
  React.useEffect(() => {
    if (caseId === undefined) return;
    let alive = true;
    void caseNameWithUnit(caseId).then(
      (name) => { if (alive) setCaseIndex(new Map([[caseId, name]])); },
      () => {
        if (alive) setCaseIndex(new Map([[caseId, t('webAdmin.kanban.untitled', { locale: LOCALE })]]));
      },
    );
    return () => { alive = false; };
  }, [caseId]);

  React.useEffect(() => {
    void listDemoTemplates().then(setDemoTemplates, () => undefined);
    void listOfficeTemplates('active').then(setOfficeTemplates, () => undefined);
    void getContractCatalogs().then(setCatalogs, () => undefined);
    void getNewContractPlan().then(
      (nacteny) => {
        setPlan(nacteny);
        setPlanFailed(false);
      },
      () => {
        setPlan(null);
        setPlanFailed(true);
      },
    );
    void getBillingOverview().then(
      (overview) => setSupplier(overview.supplier),
      () => setSupplier(null),
    );
  }, []);

  /**
   * Mockup bod 7: kdyz kancelar zatim nema zadnou sablonu, vychozi podklad
   * jsou Vzory REOS (ne prazdny rozbalovaci seznam — R.B. to videl jako
   * „skoro nic"). Vyber se propise do `aria-selected` na prepinaci.
   *
   * VYCHOZI volba plati jen DOKUD si makler nevybral sam (oprava e2e 1 kroku
   * KAT1l2, S19/mobil). Sablony kancelare dojizdeji ze site, takze se jejich
   * pocet meni 0 → N jeste PO prvnim vykresleni; bez teto pojistky efekt
   * prepnul podklad zpatky na „Šablony kanceláře" i ve chvili, kdy uz makler
   * klepl na „Vzory Makléřova" — vybrany pruh i s tlacitkem „Nahrát vlastní
   * smlouvu (.docx)" mu pod rukama zmizel.
   */
  const sourcePickedByUser = React.useRef(false);
  React.useEffect(() => {
    if (sourcePickedByUser.current) return;
    setSourceKind(officeTemplates.length === 0 ? 'demo' : 'office');
  }, [officeTemplates.length]);

  /** Plan vybraneho podkladu — z nej jdou cisla v pravem panelu. */
  const pickedPlan = React.useMemo(
    () =>
      plan?.templates.find((tpl) => tpl.kind === sourceKind && tpl.key === sourceKey) ?? null,
    [plan, sourceKind, sourceKey],
  );

  /** Uroven podpisu typu dokumentu — rozhodla ji politika na serveru. */
  const policyOf = React.useCallback(
    (type: ContractType): ContractSignaturePolicyDto | null =>
      plan?.types.find((entry) => entry.contractType === type)?.policy ?? null,
    [plan],
  );

  /** Kolik udaju sablona pouziva — ENGINE (`fill-plan.ts`), ne odhad v UI. */
  function templateFieldCount(kind: 'office' | 'demo', key: string): number | null {
    return plan?.templates.find((tpl) => tpl.kind === kind && tpl.key === key)?.total ?? null;
  }

  /**
   * Prepnuti PODKLADU rukou maklere. Jedno misto pro vsechny tri dlazdice:
   * krom prepnuti zahazuje rozepsany vyber A vypina vychozi volbu (efekt
   * vyse), takze tri kopie v `onClick` by se drive nebo pozdeji rozesly.
   */
  function pickSource(kind: 'office' | 'demo' | 'upload'): void {
    sourcePickedByUser.current = true;
    setSourceKind(kind);
    setSourceKey('');
  }

  function pickTemplate(kind: 'office' | 'demo', key: string, name: string, type: ContractType): void {
    sourcePickedByUser.current = true;
    setSourceKind(kind);
    setSourceKey(key);
    setContractType(type);
    if (title.trim() === '') setTitle(name);
  }

  async function submit(): Promise<void> {
    if (sourceKey === '' || title.trim() === '') return;
    setStatus('saving');
    setErrorDetail(undefined);
    try {
      // ADM1 bod 2: kdyz je zakazka vybrana (prop nebo picker), dohledej jeji
      // nemovitost pro predvyplneni adresy - stejny zdroj jako Nemovitosti/Naklady.
      let propertyId: string | null = null;
      let propertyAddress: string | null = null;
      /*
       * Krok P8F4 bod 4: z nemovitosti zakazky se bere adresa I DRUH — jedno
       * strankovani pro oboji (`fetchPropertyBriefs`, UXR5 bod 3), ne dve.
       */
      let propertyKind: string | null = null;
      if (resolvedCaseId !== undefined) {
        const caseDto = await getCase(resolvedCaseId);
        propertyId = caseDto.propertyId;
        if (propertyId !== null) {
          const brief = (await fetchPropertyBriefs([propertyId])).get(propertyId);
          propertyAddress = brief !== undefined && brief.address !== '' ? brief.address : null;
          propertyKind = brief?.kind ?? null;
        }
      }

      const builtin = catalogs?.subjectTypes.find((s) => s.key === subjectKey);
      /*
       * Krok P8F4 bod 4 — „Typ předmětu" SE NEPTA DVAKRAT. Kdyz ho makler
       * v „Nové smlouvě" nevybral, odvodi se z druhu nemovitosti zakazky
       * (jednotka = byt). Bez nej engine nemel co sklonovat a predmet
       * zprostredkovatelske smlouvy na byt zustal prazdny (nalez P8-N5).
       * Rucni volba vzdy vyhrava.
       */
      const derivedSubject =
        builtin !== undefined ? null : subjectTypeForPropertyKind(propertyKind);
      const subjectType = builtin?.key ?? derivedSubject;
      const created = await createContract({
        title: title.trim(),
        ...(resolvedCaseId !== undefined ? { caseId: resolvedCaseId } : {}),
        ...(propertyId !== null ? { propertyId } : {}),
        source: { kind: sourceKind === 'upload' ? 'office' : sourceKind, key: sourceKey },
        ...(subjectType !== null && subjectType !== undefined
          ? { subjectType: subjectType as 'byt' | 'dum' | 'garaz' | 'sklad' | 'prodejna' | 'pozemek' }
          : {}),
      });
      // ADM1 bod 2: predvyplneni kancelare/makleere/nemovitosti hned po
      // zalozeni - jedno dalsi API volani, ale zadny novy zapisovy endpoint
      // (PATCH uz existuje).
      const prefill = {
        ...officePrefillFieldValues(user, supplier),
        ...(propertyAddress !== null
          ? { 'nemovitost.adresa': { value: propertyAddress, source: 'reos_data' as const } }
          : {}),
      };
      const withPrefill =
        Object.keys(prefill).length > 0 ? await updateContract(created.id, { fieldValues: prefill }) : created;
      setStatus('saved');
      onCreated(withPrefill.id);
    } catch (error) {
      setStatus('error');
      setErrorDetail(errorMessage(error));
    }
  }

  const demoGroups = React.useMemo(() => {
    const out = new Map<string, DemoTemplateSummaryDto[]>();
    for (const tpl of demoTemplates) {
      // Krok SML1 bod 2: kdyz uz je vybrany TYP smlouvy, nabidka textu se na
      // nej zuzi — 49 vzoru REOS v jednom sloupci je seznam, ne volba.
      if (contractType !== null && tpl.zakladniTyp !== contractType) continue;
      const list = out.get(tpl.skupina) ?? [];
      list.push(tpl);
      out.set(tpl.skupina, list);
    }
    return [...out.entries()];
  }, [demoTemplates, contractType]);

  /** Body pro pravnika u VYBRANEHO vzoru REOS (krok UXP3, P1-4). */
  const pickedDemoLawyerNotes = React.useMemo(
    () =>
      sourceKind === 'demo' && sourceKey !== ''
        ? (demoTemplates.find((tpl) => tpl.id === sourceKey)?.lawyerNotes ?? [])
        : [],
    [demoTemplates, sourceKind, sourceKey],
  );

  const officeForType = React.useMemo(
    () =>
      officeTemplates.filter((tpl) => contractType === null || tpl.contractType === contractType),
    [officeTemplates, contractType],
  );

  const caseChosen = caseId !== undefined || pickedCaseId !== null || noCase;
  /*
   * Krok P8F7 (nalez P8-N12) — DOKUD SE NEVRATI `caseNameWithUnit`, fallback
   * byval holy UUID zakazky (`caseId`). Misto neceho, co makler nikdy nema
   * videt, stoji „Načítám…" — stejny stav jako jinde na strance smluv.
   */
  const caseName =
    caseId !== undefined
      ? (caseIndex?.get(caseId) ?? t('webAdmin.contracts.loading', { locale: LOCALE }))
      : pickedCaseId !== null
        ? (caseIndex?.get(pickedCaseId) ?? pickedCaseId)
        : null;

  return (
    <div className="rh-v4-new" data-testid="new-contract-view">
      <V4NavBar
        testId="new-contract-nav"
        back={
          <button type="button" onClick={onCancel} data-testid="new-contract-close">
            {`‹ ${t('webAdmin.contracts.new.close', { locale: LOCALE })}`}
          </button>
        }
      />
      <h1 className="rh-v4-head__title">{t('webAdmin.contracts.new.heading', { locale: LOCALE })}</h1>

      <div className="rh-v4-cols rh-v4-cols--new">
        <div className="rh-v4-col">
          {/* ------------------------------------------ 1. ke ktere zakazce */}
          <V4Group testId="new-contract-step-case">
            <div className="rh-v4-card">
              <div className="rh-v4-step">
                <span className="rh-v4-step__num" aria-hidden="true">
                  1
                </span>
                <span className="rh-v4-h2">{t('webAdmin.contracts.new.stepCase', { locale: LOCALE })}</span>
              </div>
            </div>
            {caseName !== null ? (
              <V4Row
                title={caseName}
                sub={t('webAdmin.contracts.new.caseSearchHint', { locale: LOCALE })}
                testId="new-contract-case-picked"
                {...(caseId === undefined
                  ? {
                      right: (
                        <button
                          type="button"
                          className="rh-v4-btn rh-v4-btn--plain"
                          onClick={() => setPickedCaseId(null)}
                        >
                          {t('webAdmin.contracts.new.caseClear', { locale: LOCALE })}
                        </button>
                      ),
                    }
                  : {})}
              />
            ) : noCase ? (
              <V4Row
                title={t('webAdmin.contracts.new.caseNone', { locale: LOCALE })}
                sub={t('webAdmin.contracts.new.caseNoneHint', { locale: LOCALE })}
                testId="new-contract-case-none-picked"
                right={
                  <button
                    type="button"
                    className="rh-v4-btn rh-v4-btn--plain"
                    onClick={() => setNoCase(false)}
                  >
                    {t('webAdmin.contracts.new.caseClear', { locale: LOCALE })}
                  </button>
                }
              />
            ) : (
              <div className="rh-v4-sect__body">
                <label className="rh-v4-field">
                  <span className="rh-v4-field__label">
                    {t('webAdmin.contracts.new.caseLabel', { locale: LOCALE })}
                  </span>
                  <input
                    id="new-contract-case"
                    value={caseQuery}
                    onChange={(e) => setCaseQuery(e.target.value)}
                    placeholder={t('webAdmin.contracts.new.casePlaceholder', { locale: LOCALE })}
                    data-testid="new-contract-case-search"
                  />
                </label>
                {caseQuery.trim() !== '' && caseIndex !== null && (
                  <div className="rh-v4-crow__assign-list">
                    {[...caseIndex.entries()]
                      .filter(([, name]) => name.toLowerCase().includes(caseQuery.trim().toLowerCase()))
                      .slice(0, ASSIGN_OPTIONS_LIMIT)
                      .map(([id, name]) => (
                        <button
                          key={id}
                          type="button"
                          className="rh-v4-btn rh-v4-btn--plain"
                          onClick={() => {
                            setPickedCaseId(id);
                            setCaseQuery('');
                          }}
                          data-testid={`new-contract-case-option-${id}`}
                        >
                          {name}
                        </button>
                      ))}
                  </div>
                )}
                {/*
                  * R.B. 4. 10.: „A když to REOS nemá a jen chci napsat smlouvu?"
                  *
                  * Krok UXF4 bod 10 (#31): volba je VEDLEJSI — hledani zakazky
                  * je hlavni cesta. Do UXF4 nesla „Bez zakázky" jako vyplnene
                  * tlacitko (`rh-v4-btn--tinted`) NAD vysvetlenim a pritahovala
                  * vic pozornosti nez samo hledani. Ted je to textovy odkaz
                  * (`--plain`) pod vysvetlenim, proc ji clovek muze chtit.
                  */}
                <p className="rh-v4-crow__why">
                  {t('webAdmin.contracts.new.caseNoneNote', { locale: LOCALE })}
                </p>
                <button
                  type="button"
                  className="rh-v4-btn rh-v4-btn--plain"
                  onClick={() => {
                    setNoCase(true);
                    setCaseQuery('');
                  }}
                  data-testid="new-contract-case-none"
                >
                  {t('webAdmin.contracts.new.caseNone', { locale: LOCALE })}
                </button>
              </div>
            )}
          </V4Group>

          {/* ------------------------------------------------ 2. jaka smlouva */}
          <V4Group testId="new-contract-step-type">
            <div className="rh-v4-card">
              <div className="rh-v4-step">
                <span className="rh-v4-step__num" aria-hidden="true">
                  2
                </span>
                <span className="rh-v4-h2">{t('webAdmin.contracts.new.stepType', { locale: LOCALE })}</span>
              </div>
            </div>
            <div
              className="rh-v4-typegrid"
              role="radiogroup"
              aria-label={t('webAdmin.contracts.new.stepType', { locale: LOCALE })}
            >
              {CONTRACT_TYPE_ORDER.map((type) => {
                const policy = policyOf(type);
                return (
                  <button
                    key={type}
                    type="button"
                    role="radio"
                    aria-checked={contractType === type}
                    className={`rh-v4-typecard${contractType === type ? ' is-on' : ''}`}
                    onClick={() => {
                      setContractType(type);
                      setSourceKey('');
                    }}
                    data-testid={`new-contract-type-${type}`}
                  >
                    <span className="rh-v4-typecard__name">{t(TYPE_KEY[type], { locale: LOCALE })}</span>
                    <span className="rh-v4-typecard__note">
                      {t(`webAdmin.contracts.new.typeNote.${type}` as MessageKey, { locale: LOCALE })}
                    </span>
                    {/* UROVEN PODPISU Z `levels.ts` — rozhodl server, ne UI. */}
                    <span
                      className={`rh-v4-typecard__level${policy?.electronic === false ? ' rh-v4-typecard__level--paper' : ''}`}
                      data-testid={`new-contract-level-${type}`}
                    >
                      {policy === null
                        ? t(
                            planFailed
                              ? 'webAdmin.contracts.loadError'
                              : 'webAdmin.contracts.loading',
                            { locale: LOCALE },
                          )
                        : t(policy.routeLabelKey as MessageKey, { locale: LOCALE })}
                    </span>
                  </button>
                );
              })}
            </div>
            <V4FormRow label={t('webAdmin.contracts.new.subjectLabel', { locale: LOCALE })}>
              <select
                value={subjectKey}
                onChange={(e) => setSubjectKey(e.target.value)}
                data-testid="new-contract-subject"
              >
                <option value="">—</option>
                {(catalogs?.subjectTypes ?? []).map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </select>
            </V4FormRow>
          </V4Group>

          {/* --------------------------------------- 3. podle jakeho textu */}
          <V4Group testId="new-contract-step-text">
            <div className="rh-v4-card">
              <div className="rh-v4-step">
                <span className="rh-v4-step__num" aria-hidden="true">
                  3
                </span>
                <span className="rh-v4-h2">{t('webAdmin.contracts.new.stepText', { locale: LOCALE })}</span>
              </div>
            </div>
            <div className="rh-v4-sect__body">
              <div className="rh-v4-sources" role="group" data-testid="new-contract-source">
                <button
                  type="button"
                  className={`rh-v4-source${sourceKind === 'office' ? ' is-on' : ''}`}
                  aria-selected={sourceKind === 'office'}
                  onClick={() => { pickSource('office'); }}
                  data-testid="new-contract-source-office"
                >
                  <span className="rh-v4-source__name">
                    {t('webAdmin.contracts.new.sourceOffice', { locale: LOCALE })}
                  </span>
                  {officeTemplates.length > 0 && (
                    <span className="rh-v4-pill" data-testid="new-contract-source-office-recommended">
                      {t('webAdmin.contracts.new.recommended', { locale: LOCALE })}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  className={`rh-v4-source${sourceKind === 'demo' ? ' is-on' : ''}`}
                  aria-selected={sourceKind === 'demo'}
                  onClick={() => { pickSource('demo'); }}
                  data-testid="new-contract-source-demo"
                >
                  <span className="rh-v4-source__name">
                    {t('webAdmin.contracts.new.sourceDemo', { locale: LOCALE })}
                  </span>
                  <span className="rh-v4-source__note">
                    {t('webAdmin.contracts.new.sourceDemoNote', { locale: LOCALE })}
                  </span>
                </button>
                <button
                  type="button"
                  className={`rh-v4-source${sourceKind === 'upload' ? ' is-on' : ''}`}
                  aria-selected={sourceKind === 'upload'}
                  onClick={() => { pickSource('upload'); }}
                  data-testid="new-contract-source-upload"
                >
                  <span className="rh-v4-source__name">
                    {t('webAdmin.contracts.new.sourceUpload', { locale: LOCALE })}
                  </span>
                  <span className="rh-v4-source__note">
                    {t('webAdmin.contracts.new.sourceUploadNote', { locale: LOCALE })}
                  </span>
                </button>
              </div>

              {sourceKind === 'office' && (
                <div className="rh-v4-templates" data-testid="new-contract-office-cards">
                  {officeForType.length === 0 && (
                    <p className="rh-v4-crow__why" data-testid="new-contract-no-office">
                      {officeTemplates.length === 0
                        ? t('webAdmin.contracts.new.noTemplates', { locale: LOCALE })
                        : t('webAdmin.contracts.new.noTemplateForType', { locale: LOCALE })}
                    </p>
                  )}
                  {officeForType.map((tpl) => (
                    <button
                      key={tpl.id}
                      type="button"
                      className={`rh-v4-template${sourceKey === tpl.id ? ' is-on' : ''}`}
                      aria-pressed={sourceKey === tpl.id}
                      onClick={() => pickTemplate('office', tpl.id, tpl.name, tpl.contractType)}
                      data-testid={`new-contract-card-${tpl.id}`}
                    >
                      <span className="rh-v4-template__name">{tpl.name}</span>
                      <span className="rh-v4-template__meta">
                        {t(TYPE_KEY[tpl.contractType], { locale: LOCALE })} ·{' '}
                        {t('webAdmin.contracts.templates.versionShort', {
                          locale: LOCALE,
                          values: { version: tpl.version },
                        })}
                      </span>
                      <span className="rh-v4-template__meta">
                        {t('webAdmin.contracts.new.fieldsCount', {
                          locale: LOCALE,
                          values: { count: templateFieldCount('office', tpl.id) ?? 0 },
                        })}
                      </span>
                      <span className="rh-v4-template__hint">
                        {t('webAdmin.contracts.new.autoFill', { locale: LOCALE })}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {sourceKind === 'demo' && (
                <>
                  {/* Krok SML6 bod 5: PRUH NAD SEZNAMEM VZORU. Do SML6 to byl
                      drobny radek POD seznamem, kde ho makler precetl az po
                      vyberu vzoru (a na snimku brany splyval s disclaimerem).
                      Vzory v aplikaci jsou NASE UKAZKY — hlavni cesta je
                      vlastni smlouva kancelare, takze to obrazovka rika jako
                      prvni a dava na ni tlacitko. */}
                  <div className="rh-v4-sample" data-testid="new-contract-demo-sample-badge">
                    <p className="rh-v4-sample__text">
                      {t('webAdmin.contracts.new.demoSampleBadge', { locale: LOCALE })}
                    </p>
                    <Link
                      to="/smlouvy-sablony"
                      className="rh-btn rh-btn--primary"
                      data-testid="new-contract-demo-sample-link"
                    >
                      {t('webAdmin.contracts.new.demoSampleUpload', { locale: LOCALE })}
                    </Link>
                  </div>
                  {demoGroups.map(([group, items]) => (
                    <div key={group}>
                      <p className="rh-v4-crow__meta">
                        {t(DEMO_GROUP_KEY[group] ?? 'webAdmin.contracts.tabs.new', { locale: LOCALE })}
                      </p>
                      <div className="rh-v4-templates" data-testid={`new-contract-demo-group-${group}`}>
                        {items.map((tpl) => (
                          <button
                            key={tpl.id}
                            type="button"
                            className={`rh-v4-template${sourceKey === tpl.id ? ' is-on' : ''}`}
                            aria-pressed={sourceKey === tpl.id}
                            onClick={() => pickTemplate('demo', tpl.id, tpl.nazev, tpl.zakladniTyp)}
                            data-testid={`new-contract-card-${tpl.id}`}
                          >
                            <span className="rh-v4-template__name">{tpl.nazev}</span>
                            <span className="rh-v4-template__meta">
                              {t('webAdmin.contracts.new.fieldsCount', {
                                locale: LOCALE,
                                values: { count: templateFieldCount('demo', tpl.id) ?? tpl.requiredCount },
                              })}
                            </span>
                            <span className="rh-v4-template__hint rh-v4-template__hint--warn">
                              {t('webAdmin.contracts.new.demoHint', { locale: LOCALE })}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  {/* Krok UXP3 (P1-4): vlozky `[DOPLNÍ PRÁVNÍK…]` ohlasime UZ
                      PRI VYBERU VZORU. Rucni pruchod 10. 10. na ne narazil az
                      u zakazaneho „Založit podepisování" — po trech desitkach
                      vyplnenych poli. */}
                  {pickedDemoLawyerNotes.length > 0 && (
                    <div className="rh-v4-crow__why" data-testid="new-contract-lawyer-notes">
                      <p>
                        {t('webAdmin.contracts.new.lawyerNotesHeading', {
                          locale: LOCALE,
                          values: { count: pickedDemoLawyerNotes.length },
                        })}
                      </p>
                      <ul>
                        {pickedDemoLawyerNotes.map((note) => (
                          <li key={note}>{note}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {/* Mockup bod 8: vzory REOS nejsou právní rada. */}
                  <p className="rh-v4-crow__why" data-testid="new-contract-demo-disclaimer">
                    {t('webAdmin.contracts.new.demoDisclaimer', { locale: LOCALE })}
                  </p>
                </>
              )}

              {sourceKind === 'upload' && (
                <p className="rh-v4-crow__why" data-testid="new-contract-upload-note">
                  {t('webAdmin.contracts.new.sourceUploadNote', { locale: LOCALE })}{' '}
                  <Link to="/smlouvy-sablony" data-testid="new-contract-upload-link">
                    {t('webAdmin.contracts.list.officeTemplatesUpload', { locale: LOCALE })}
                  </Link>
                </p>
              )}

              <label className="rh-v4-field">
                <span className="rh-v4-field__label">
                  {t('webAdmin.contracts.new.titleLabel', { locale: LOCALE })}
                </span>
                <input
                  id="new-contract-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  data-testid="new-contract-title"
                />
              </label>
            </div>
          </V4Group>
        </div>

        {/* --------------------------------- co se stane po vytvoreni (engine) */}
        <div className="rh-v4-col">
          <V4Group testId="new-contract-plan">
            <div className="rh-v4-card">
              <div className="rh-v4-h2">{t('webAdmin.contracts.new.planHeading', { locale: LOCALE })}</div>
              {pickedPlan === null ? (
                <p className="rh-v4-crow__why" data-testid="new-contract-plan-empty">
                  {t('webAdmin.contracts.new.planEmpty', { locale: LOCALE })}
                </p>
              ) : (
                <>
                  <p className="rh-v4-crow__meta" data-testid="new-contract-plan-total">
                    {t('webAdmin.contracts.new.planTotal', {
                      locale: LOCALE,
                      values: { count: pickedPlan.total },
                    })}
                  </p>
                  {PLAN_GROUPS.map((group) => (
                    <div key={group.filler} className="rh-v4-plan__group">
                      <span
                        className={`rh-v4-plan__count rh-v4-plan__count--${group.filler}`}
                        data-testid={`new-contract-plan-${group.filler}`}
                      >
                        {t(group.labelKey, {
                          locale: LOCALE,
                          values: { count: pickedPlan.missingByFiller[group.filler] },
                        })}
                      </span>
                      {pickedPlan.labelsByFiller[group.filler].length > 0 && (
                        <span className="rh-v4-crow__why">
                          {pickedPlan.labelsByFiller[group.filler].join(' · ')}
                        </span>
                      )}
                    </div>
                  ))}
                  {pickedPlan.later > 0 && (
                    <span className="rh-v4-crow__why" data-testid="new-contract-plan-later">
                      {t('webAdmin.contracts.new.planLater', {
                        locale: LOCALE,
                        values: { count: pickedPlan.later },
                      })}
                    </span>
                  )}
                </>
              )}
            </div>
            <div className="rh-v4-sect__body">
              <button
                type="button"
                className="rh-v4-btn rh-v4-btn--filled rh-v4-btn--block"
                onClick={() => void submit()}
                disabled={sourceKey === '' || title.trim() === '' || !caseChosen}
                data-testid="new-contract-submit"
              >
                {t('webAdmin.contracts.new.submit', { locale: LOCALE })}
              </button>
              {!caseChosen && (
                <p className="rh-v4-crow__why" data-testid="new-contract-submit-blocked">
                  {t('webAdmin.contracts.new.caseSearchHint', { locale: LOCALE })}
                </p>
              )}
              <label className="rh-v4-check">
                <input
                  type="checkbox"
                  checked={sendLinks}
                  onChange={(e) => setSendLinks(e.target.checked)}
                  data-testid="new-contract-send-links"
                />
                <span>{t('webAdmin.contracts.new.sendLinks', { locale: LOCALE })}</span>
              </label>
              {/* Na preview se neodesila (`REOS_OUTBOUND_SEND=off`) — obrazovka
                  to rekne radeji nez aby tvrdila, ze odkaz odesel. */}
              <p className="rh-v4-crow__why" data-testid="new-contract-send-links-note">
                {t('webAdmin.contracts.new.sendLinksOffNote', { locale: LOCALE })}
              </p>
              <button type="button" className="rh-v4-btn rh-v4-btn--plain" onClick={onCancel}>
                {t('webAdmin.contracts.new.cancel', { locale: LOCALE })}
              </button>
              <SaveStatus
                status={status}
                texts={{
                  saving: t('webAdmin.contracts.saving', { locale: LOCALE }),
                  saved: t('webAdmin.contracts.saved', { locale: LOCALE }),
                  error: t('webAdmin.contracts.loadError', { locale: LOCALE }),
                }}
                {...(errorDetail !== undefined ? { errorDetail } : {})}
              />
            </div>
          </V4Group>
        </div>
      </div>
    </div>
  );
}

/**
 * Poradi typu dokumentu na obrazovce „Nová smlouva" (predloha: Nájemní ·
 * Zprostředkovatelská · Rezervační · Kupní …). Co je v seznamu, rozhoduje
 * SERVER (`levels.ts knownContractTypes`) — tady je jen poradi pro oko.
 */
const CONTRACT_TYPE_ORDER: readonly ContractType[] = [
  'najem-byt',
  'zprostredkovatelska',
  'rezervace',
  'kupni',
  'najem-nebyt',
];

/** Tri skupiny pravého panelu v poradi predlohy (REOS → klienti → ty). */
const PLAN_GROUPS: readonly { readonly filler: FieldFiller; readonly labelKey: MessageKey }[] = [
  { filler: 'katastr', labelKey: 'webAdmin.contracts.new.planReos' },
  { filler: 'klienti', labelKey: 'webAdmin.contracts.new.planClients' },
  { filler: 'makler', labelKey: 'webAdmin.contracts.new.planBroker' },
];

// --------------------------------------------------------------------- editor

function OriginTag({ origin }: { readonly origin: keyof typeof ORIGIN_LABEL_KEY }): React.JSX.Element {
  return (
    <span className={`reos-origin-tag ${originCssClass(origin)}`} data-origin={origin}>
      {t(ORIGIN_LABEL_KEY[origin], { locale: LOCALE })}
    </span>
  );
}

/**
 * Krok P8F5 body 2 a 3 — ROZDIL MEZI SMLOUVOU A ZAKAZKOU u jednoho pole.
 *
 * Dva ruzne stavy, zamerne odlisene: „v zakazce se to zmenilo" je OTAZKA
 * (Převzít / Ponechat), „liší se od zakázky" je KONSTATOVANI nad hodnotou,
 * kterou makler napsal sam — tam se nic neprepisuje a do karty jde hodnota
 * jen tlacitkem. Server posila jen `draft` smlouvy (bod 4), takze odeslana
 * ani podepsana smlouva tu nic nenabizi.
 */
function FactChangeNotice({
  change,
  fieldName,
  saveState,
  onTake,
  onKeep,
  onSaveToCase,
}: {
  readonly change: ContractFactChangeDto | null;
  readonly fieldName: string;
  readonly saveState: 'ok' | 'error' | null;
  readonly onTake: () => void;
  readonly onKeep: () => void;
  readonly onSaveToCase: () => void;
}): React.JSX.Element | null {
  if (change === null) return null;
  const factText = documentValueText(String(change.factValue ?? ''));
  const contractText = documentValueText(String(change.contractValue ?? ''));
  if (change.kind === 'zmena-v-zakazce') {
    return (
      <span
        className="reos-field-row__hint reos-fact-change"
        data-testid={`contract-fact-change-${change.fieldKey}`}
        data-kind={change.kind}
      >
        {t('webAdmin.contracts.editor.factChanged', {
          locale: LOCALE,
          values: { field: fieldName, from: contractText, to: factText },
        })}{' '}
        <button
          type="button"
          className="reos-button reos-button--small"
          onClick={onTake}
          data-testid={`contract-fact-take-${change.fieldKey}`}
        >
          {t('webAdmin.contracts.editor.factChangeTake', {
            locale: LOCALE,
            values: { to: factText },
          })}
        </button>{' '}
        <button
          type="button"
          className="reos-button reos-button--small reos-button--ghost"
          onClick={onKeep}
          data-testid={`contract-fact-keep-${change.fieldKey}`}
        >
          {t('webAdmin.contracts.editor.factChangeKeep', {
            locale: LOCALE,
            values: { from: contractText },
          })}
        </button>
      </span>
    );
  }
  return (
    <span
      className="reos-field-row__hint reos-fact-change"
      data-testid={`contract-fact-change-${change.fieldKey}`}
      data-kind={change.kind}
    >
      <span className="reos-origin-tag" data-testid={`contract-fact-differs-${change.fieldKey}`}>
        {t('webAdmin.contracts.editor.factDiffers', { locale: LOCALE })}
      </span>{' '}
      {t('webAdmin.contracts.editor.factDiffersHint', {
        locale: LOCALE,
        values: { contract: contractText, fact: factText },
      })}{' '}
      {change.saveToCase !== undefined && saveState !== 'ok' && (
        <button
          type="button"
          className="reos-button reos-button--small reos-button--ghost"
          onClick={onSaveToCase}
          data-testid={`contract-fact-save-to-case-${change.fieldKey}`}
        >
          {t('webAdmin.contracts.editor.factSaveToCase', { locale: LOCALE })}
        </button>
      )}
      {saveState === 'ok' && t('webAdmin.contracts.editor.factSaveToCaseDone', { locale: LOCALE })}
      {saveState === 'error' && t('webAdmin.contracts.editor.factSaveToCaseError', { locale: LOCALE })}
    </span>
  );
}

function OriginLegend(): React.JSX.Element {
  return (
    <div className="reos-legend" data-testid="contract-origin-legend">
      {ORIGIN_LEGEND.map((origin) => (
        <span key={origin} className="reos-legend__item" data-testid={`contract-origin-${origin}`}>
          <span className={`reos-legend__swatch ${originCssClass(origin)}`} aria-hidden="true" />
          {t(ORIGIN_LABEL_KEY[origin], { locale: LOCALE })}
        </span>
      ))}
    </div>
  );
}

// ------------------------------------------------- ovladaci prvky poli (T5)

/** Hodnota pole jako seznam — `seznam` se uklada jako pole retezcu. */
function asList(value: unknown): readonly string[] {
  if (Array.isArray(value)) return value.map((v) => String(v)).filter((v) => v !== '');
  if (typeof value === 'string' && value !== '') return value.split(', ').filter((v) => v !== '');
  return [];
}

/**
 * HTML typ vstupu podle typu pole ve slovniku (krok T5, bod 12).
 *
 * Krok EDIT2 bod 1 (nalez B1): `cislo` a `castka-kc` UZ NEJSOU nativni
 * `type="number"`. Prohlizec do nej nepusti mezeru ani desetinnou carku,
 * takze z `15 000,50` zustalo v poli `1500050` a tahle hodnota se BEZ
 * VAROVANI ulozila jako smluvni castka. Ciselna pole jdou pres
 * `NumericFieldInput` (textove pole + `parseCzechNumberInput`).
 *
 * Krok EDIT2 bod 12 (nalez K1): `datum` uz taky NENI nativni `type="date"` —
 * Chromium ho kresli podle lokalizace OS (`mm/dd/yyyy` i na ceskem uctu)
 * a aplikace to neprebije. Datum jde pres `DateField` (krok UXF3), jedinou
 * komponentu data v celem adminu.
 */
function inputTypeFor(fieldType: string | undefined): string {
  if (fieldType === 'email') return 'email';
  if (fieldType === 'telefon') return 'tel';
  return 'text';
}

/**
 * VOLNY SEZNAM POLOZEK (`najem.osoby[]`, `nemovitost.vady[]`,
 * `zavazky.bremena-mimo-lv[]`) — krok EDIT2 body 5 a 7.
 *
 * Nalez V1 zivého průchodu EDIT1: „+ přidat položku" ulozilo `[""]`, ale
 * `asList()` prazdnou polozku odfiltruje, takze se v DOM NIKDY neobjevil
 * novy radek. Tlacitko bylo fakticky mrtve a polozku neslo pres UI pridat.
 *
 * Prazdne polozky proto NEZIJI na serveru: rozepsane radky drzi tahle
 * komponenta (`drafts`) a ulozi se jen to, co ma hodnotu. Novy radek se
 * objevi hned a dostane fokus.
 *
 * Bod 7: poradi se meni tlacitky nahoru/dolu. Makler zadal polozky
 * v nespravnem poradi a musel je mazat a psat znovu.
 */
function FreeListField({
  value,
  onCommit,
  testId,
  labelledBy,
}: {
  readonly value: unknown;
  readonly onCommit: (next: unknown) => void;
  readonly testId: string;
  readonly labelledBy: string;
}): React.JSX.Element {
  const saved = asList(value);
  const savedKey = saved.join('\u0000');
  /**
   * Radky VCETNE rozepsanych a prazdnych. Hodnota ze serveru je prepise jen
   * tehdy, kdyz se SKUTECNE zmenila — prepis v renderu podle identity propsy,
   * ne v `useEffect` (jinak odpoved PATCHe prepise rozepsany radek; tentyz
   * zavod resil krok PRF4 v `DraftTextInput`).
   */
  const [rows, setRows] = React.useState<readonly string[]>(saved);
  const [seenKey, setSeenKey] = React.useState(savedKey);
  if (seenKey !== savedKey) {
    setSeenKey(savedKey);
    setRows(saved);
  }
  const focusLast = React.useRef(false);
  const lastRef = React.useRef<HTMLInputElement | null>(null);
  React.useEffect(() => {
    if (focusLast.current) {
      focusLast.current = false;
      lastRef.current?.focus();
    }
  });
  function commitRows(next: readonly string[]): void {
    setRows(next);
    const clean = next.map((v) => v.trim()).filter((v) => v !== '');
    if (clean.join('\u0000') !== savedKey) onCommit(clean);
  }
  return (
    <span className="reos-list-field" data-testid={testId} role="group" aria-labelledby={labelledBy}>
      {rows.map((item, index) => (
        <span key={`row-${index}`} className="reos-list-field__row">
          <input
            // `key` je POZICE, takze po presunu nahoru/dolu musi hodnotu
            // prepsat render, ne `defaultValue` (to React podruhe nepouzije).
            value={item}
            ref={index === rows.length - 1 ? lastRef : null}
            onChange={(e) => {
              const next = [...rows];
              next[index] = e.target.value;
              setRows(next);
            }}
            onBlur={() => commitRows(rows)}
            data-testid={`${testId}-item-${index}`}
          />
          <button
            type="button"
            className="rh-btn"
            disabled={index === 0}
            aria-label={t('webAdmin.contracts.editor.moveListItemUp', { locale: LOCALE })}
            onClick={() => commitRows(swap(rows, index, index - 1))}
            data-testid={`${testId}-up-${index}`}
          >
            ↑
          </button>
          <button
            type="button"
            className="rh-btn"
            disabled={index === rows.length - 1}
            aria-label={t('webAdmin.contracts.editor.moveListItemDown', { locale: LOCALE })}
            onClick={() => commitRows(swap(rows, index, index + 1))}
            data-testid={`${testId}-down-${index}`}
          >
            ↓
          </button>
          <button
            type="button"
            className="rh-btn"
            onClick={() => commitRows(rows.filter((_, i) => i !== index))}
            data-testid={`${testId}-remove-${index}`}
          >
            {t('webAdmin.contracts.editor.removeListItem', { locale: LOCALE })}
          </button>
        </span>
      ))}
      <button
        type="button"
        className="rh-btn"
        onClick={() => {
          focusLast.current = true;
          setRows([...rows, '']);
        }}
        data-testid={`${testId}-add`}
      >
        {t('webAdmin.contracts.editor.addListItem', { locale: LOCALE })}
      </button>
    </span>
  );
}

function swap(items: readonly string[], a: number, b: number): readonly string[] {
  if (a < 0 || b < 0 || a >= items.length || b >= items.length) return items;
  const next = [...items];
  const first = next[a] ?? '';
  next[a] = next[b] ?? '';
  next[b] = first;
  return next;
}

/**
 * CISELNE POLE S CESKYM ZAPISEM (krok EDIT2 bod 1).
 *
 * Pise se do nej text (`inputMode="decimal"` kvuli klavesnici na mobilu),
 * ulozi se KANONICKA hodnota (`15000.5`) a zobrazi se cesky zapis
 * (`15 000,50`). Co parser neprecte, se NEULOZI a pole u sebe ukaze chybu —
 * tiche ulozeni nesmyslu je presne vada, kterou zivy pruchod nasel.
 */
function NumericFieldInput({
  value,
  onCommit,
  onPreview,
  controlId,
  testId,
}: {
  readonly value: string;
  readonly onCommit: (next: string) => void;
  readonly onPreview?: ((next: string) => void) | undefined;
  readonly controlId: string;
  readonly testId: string;
}): React.JSX.Element {
  const [invalid, setInvalid] = React.useState(false);
  const errorId = `${testId}-error`;
  return (
    <>
      <DraftTextInput
        value={formatCzechNumber(value)}
        // `type="text"` je tu VYSLOVNE, aby bylo v DOM videt, ze pole uz
        // neni nativni `number` (krok EDIT2 bod 1).
        type="text"
        inputMode="decimal"
        onCommit={(next) => {
          // Krok UXD2: „rozepsane / nesrozumitelne / hotove" rozhoduje
          // `readCzechNumberInput`, aby tohle pole a `CzechNumberInput`
          // (bezne formulare) rozhodovaly stejne.
          const read = readCzechNumberInput(next);
          if (read.state === 'incomplete') return;
          setInvalid(read.state === 'invalid');
          if (read.state === 'ok') onCommit(read.value);
        }}
        onPreview={(next) => {
          const read = readCzechNumberInput(next);
          if (read.state === 'incomplete') return;
          setInvalid(read.state === 'invalid');
          if (read.state === 'ok') onPreview?.(read.value);
        }}
        id={controlId}
        {...(invalid ? { ariaDescribedBy: errorId } : {})}
        testId={testId}
      />
      {invalid && (
        <span className="reos-field-row__error" role="alert" id={errorId} data-testid={errorId}>
          {t('webAdmin.contracts.editor.numberInvalid', { locale: LOCALE })}
        </span>
      )}
    </>
  );
}

/**
 * Jedno pole editoru podle slovniku T1 v2 (krok T5, bod 12 auditu mockupu).
 *
 * Mockup ma u kazdeho pole prvek, ktery odpovida jeho povaze: prepinac
 * (`volba`), zaskrtavatka (`seznam` s nabidkou), volny seznam polozek
 * (`seznam` bez nabidky, napr. osoby v domacnosti), datum, cislo. Do T4 z
 * toho byl vzdy jeden textovy vstup — proto bod 12 zustaval ODCHYLKA.
 *
 * Hodnota se uklada pri ZMENE u prepinacu/zaskrtavatek (jedno kliknuti = jedno
 * rozhodnuti) a az pri opusteni u textovych vstupu (jinak by kazdy znak
 * pregeneroval nahled smlouvy).
 */
function ContractFieldControl({
  def,
  value,
  onCommit,
  onPreview,
  controlId,
  labelledBy,
  testId,
}: {
  readonly def: ContractFieldDictionaryEntryDto | undefined;
  readonly value: unknown;
  readonly onCommit: (next: unknown) => void;
  readonly onPreview?: ((next: string) => void) | undefined;
  /** `id` JEDNOHO vstupu — popisek radku je `<label for>` (krok UXS2 bod 4). */
  readonly controlId: string;
  /** Popisek skupiny voleb: `<label for>` na ni neplatí, `aria-labelledby` ano. */
  readonly labelledBy: string;
  readonly testId: string;
}): React.JSX.Element {
  const fieldType = def?.type;
  const options = def?.options ?? [];
  const allowCustom = def?.allowCustomOption === true;
  const [draft, setDraft] = React.useState(String(value ?? ''));
  React.useEffect(() => { setDraft(String(value ?? '')); }, [value]);

  // Prepinac s nabidkou (mockup `radios` / `sel2`).
  if (fieldType === 'volba' && options.length > 0) {
    const current = String(value ?? '');
    const isCustom = current !== '' && !options.includes(current);
    return (
      <span className="reos-chips" data-testid={testId} role="radiogroup" aria-labelledby={labelledBy}>
        {options.map((option) => (
          <label key={option} className={`reos-chip${current === option ? ' reos-chip--on' : ''}`}>
            <input
              type="radio"
              name={testId}
              checked={current === option}
              onChange={() => onCommit(option)}
              data-testid={`${testId}-option-${options.indexOf(option)}`}
            />
            {option}
          </label>
        ))}
        {allowCustom && (
          <input
            className="reos-chips__custom"
            placeholder={t('webAdmin.contracts.editor.customOptionPlaceholder', { locale: LOCALE })}
            value={isCustom ? draft : ''}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => { if (draft.trim() !== '' && draft !== current) onCommit(draft.trim()); }}
            data-testid={`${testId}-custom`}
          />
        )}
      </span>
    );
  }

  // Zaskrtavatka s nabidkou (mockup `checks`).
  if (fieldType === 'seznam' && options.length > 0) {
    const selected = asList(value);
    const custom = selected.filter((v) => !options.includes(v));
    return (
      <span className="reos-chips" data-testid={testId} role="group" aria-labelledby={labelledBy}>
        {options.map((option) => (
          <label key={option} className={`reos-chip${selected.includes(option) ? ' reos-chip--on' : ''}`}>
            <input
              type="checkbox"
              checked={selected.includes(option)}
              onChange={(e) =>
                onCommit(
                  e.target.checked
                    ? [...options.filter((o) => o === option || selected.includes(o)), ...custom]
                    : selected.filter((v) => v !== option),
                )
              }
              data-testid={`${testId}-option-${options.indexOf(option)}`}
            />
            {option}
          </label>
        ))}
        {allowCustom && (
          <input
            className="reos-chips__custom"
            placeholder={t('webAdmin.contracts.editor.customItemPlaceholder', { locale: LOCALE })}
            value={custom.join(', ')}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={(e) =>
              onCommit([
                ...selected.filter((v) => options.includes(v)),
                ...e.target.value.split(',').map((v) => v.trim()).filter((v) => v !== ''),
              ])
            }
            data-testid={`${testId}-custom`}
          />
        )}
      </span>
    );
  }

  // Volny seznam polozek bez nabidky (mockup: dalsi osoby v domacnosti).
  if (fieldType === 'seznam') {
    return (
      <FreeListField
        value={value}
        onCommit={onCommit}
        testId={testId}
        labelledBy={labelledBy}
      />
    );
  }

  // Krok EDIT2 bod 12: datum ceskym zapisem, nezavisle na lokalizaci OS.
  if (fieldType === 'datum') {
    return (
      <DateField
        value={String(value ?? '')}
        onValueChange={(next) => {
          onPreview?.(next);
          onCommit(next);
        }}
        id={controlId}
        testId={testId}
      />
    );
  }

  // Krok EDIT2 bod 1: cislo a castka maji vlastni prvek s ceskym parserem.
  if (isNumericFieldType(fieldType)) {
    return (
      <NumericFieldInput
        value={String(value ?? '')}
        onCommit={onCommit}
        {...(onPreview !== undefined ? { onPreview } : {})}
        controlId={controlId}
        testId={testId}
      />
    );
  }

  // Textove/datumove pole: uklada se prubezne pri psani, aby se
  // dokument vpravo menil rovnou (krok UXS1 bod 3).
  return (
    <DraftTextInput
      type={inputTypeFor(fieldType)}
      value={String(value ?? '')}
      onCommit={onCommit}
      {...(onPreview !== undefined ? { onPreview } : {})}
      id={controlId}
      testId={testId}
    />
  );
}

/**
 * Textovy vstup editoru s PRUBEZNYM ulozenim (krok UXS1 bod 3: „při psaní se
 * dokument okamžitě aktualizuje").
 *
 * Kazde ulozeni je PATCH cele smlouvy s novym renderem dokumentu, takze zapis
 * po KAZDEM znaku by posilal desitky pozadavku. Proto se uklada se zpozdenim
 * (`DRAFT_COMMIT_MS` po poslednim znaku) a jeste pri opusteni pole. Hodnota ze
 * serveru prepise rozepsany text jen tehdy, kdyz v poli nestoji kurzor —
 * jinak by odpoved PATCHe prepsala to, co clovek mezitim napsal.
 */
const DRAFT_COMMIT_MS = 450;

export function DraftTextInput({
  value,
  readOnly,
  onCommit,
  onPreview,
  onFocus,
  onBlur,
  type,
  id,
  ariaLabelledBy,
  ariaDescribedBy,
  testId,
  format,
  maskWhenBlurred,
  inputMode,
  autoComplete,
  name,
}: {
  readonly value: string;
  readonly readOnly?: boolean | undefined;
  readonly onCommit: (next: string) => void;
  /**
   * Krok UXS2 bod 1: psani se ma v dokumentu objevit DO 1 s. `onCommit`
   * cekal 450 ms a pak jeste na odpoved PATCHe, takze se volá i `onPreview`
   * — po KAZDEM znaku, bez site.
   */
  readonly onPreview?: ((next: string) => void) | undefined;
  readonly onFocus?: (() => void) | undefined;
  readonly onBlur?: (() => void) | undefined;
  readonly type?: string | undefined;
  readonly id?: string | undefined;
  readonly ariaLabelledBy?: string | undefined;
  /** Hlaska u pole (krok EDIT2 bod 1: nesrozumitelne cislo). */
  readonly ariaDescribedBy?: string | undefined;
  readonly testId: string;
  /** Normalizace psaneho textu (krok UXD1 bod 1: RC lomitko po 6 cislicich). */
  readonly format?: ((next: string) => string) | undefined;
  /** Zobrazeni, kdyz pole NEMA fokus (T1 maskovani RC) — jinak se edituje draft. */
  readonly maskWhenBlurred?: ((current: string) => string) | undefined;
  readonly inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  readonly autoComplete?: string | undefined;
  /**
   * Krok UXD1 bod 9: bez `name` nabizi Chrome autofill cizi cislo/adresu z
   * jine stranky (zivy nalez — makleri psali telefon jednoho pronajimatele
   * do pole druheho). `autoComplete="off"` samo o sobe prohlizec ignoruje.
   */
  readonly name?: string | undefined;
}): React.JSX.Element {
  const [draft, setDraft] = React.useState(value);
  const [hasFocus, setHasFocus] = React.useState(false);
  const focused = React.useRef(false);
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  // Prevzeti hodnoty ze serveru se dela V RENDERU podle identity propsy, NE
  // v `useEffect`. Efekt bezi az po prvnim vykresleni, takze mezi commitem a
  // jeho dobehnutim se da do pole napsat — a efekt pak napsane prepsal
  // hodnotou ze serveru (clovek, ktery zacne psat ve chvili, kdy se karta
  // objevi, o text prisel; pod plnou branou to shazovalo UXS2 „bod 1").
  const [seenValue, setSeenValue] = React.useState(value);
  if (seenValue !== value) {
    setSeenValue(value);
    if (!focused.current) setDraft(value);
  }
  React.useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    [],
  );
  function schedule(next: string): void {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      if (next !== value) onCommit(next);
    }, DRAFT_COMMIT_MS);
  }
  return (
    <input
      {...(type !== undefined ? { type } : {})}
      {...(id !== undefined ? { id } : {})}
      {...(ariaLabelledBy !== undefined ? { 'aria-labelledby': ariaLabelledBy } : {})}
      {...(ariaDescribedBy !== undefined ? { 'aria-describedby': ariaDescribedBy } : {})}
      {...(inputMode !== undefined ? { inputMode } : {})}
      {...(autoComplete !== undefined ? { autoComplete } : {})}
      {...(name !== undefined ? { name } : {})}
      readOnly={readOnly === true}
      value={!hasFocus && maskWhenBlurred !== undefined ? maskWhenBlurred(draft) : draft}
      onFocus={() => {
        focused.current = true;
        setHasFocus(true);
        onFocus?.();
      }}
      onChange={(e) => {
        const next = format !== undefined ? format(e.target.value) : e.target.value;
        setDraft(next);
        if (readOnly !== true) {
          onPreview?.(next);
          schedule(next);
        }
      }}
      onBlur={() => {
        focused.current = false;
        setHasFocus(false);
        if (timer.current !== null) {
          clearTimeout(timer.current);
          timer.current = null;
        }
        if (readOnly !== true && draft !== value) onCommit(draft);
        onBlur?.();
      }}
      data-testid={testId}
    />
  );
}

function PartyEditor({
  party,
  role,
  onSendLink,
  onFieldChange,
  onFieldPreview,
  onFieldFocus,
  onRemove,
  boldOf,
  onToggleBold,
}: {
  readonly party: Party;
  readonly role: PartyRole;
  readonly onSendLink: (role: PartyRole) => void;
  /** Zapis hodnoty pole strany (prubezne pri psani, viz `DraftTextInput`). */
  readonly onFieldChange: (role: PartyRole, index: number, field: PartyFieldKey, value: string) => void;
  /** Krok UXS2 bod 1: dokument se meni uz pri psani, bez cekani na server. */
  readonly onFieldPreview?:
    | ((role: PartyRole, index: number, field: PartyFieldKey, value: string) => void)
    | undefined;
  /** Kurzor v poli → zvyrazneni tehoz pole v dokumentu (krok UXS1 bod 3). */
  readonly onFieldFocus?: ((role: PartyRole, field: PartyFieldKey) => void) | undefined;
  /** `undefined` = prvni strana role, ta se odebrat nesmi (krok UXS2 bod 3). */
  readonly onRemove?: ((role: PartyRole, index: number) => void) | undefined;
  /** Krok UXD3 bod 4: stav a prepnuti tlacitka „B" u radku pole strany. */
  readonly boldOf: (fieldKey: string) => boolean;
  readonly onToggleBold: (fieldKey: string, next: boolean) => void;
}): React.JSX.Element {
  const roleLabel = t(PARTY_ROLE_LABEL_KEY[role], { locale: LOCALE });
  // Potvrzeni odebrani je V RADKU, ne `window.confirm` — modalni dialog
  // prohlizece nejde ostylovat ani otestovat a v mockupu zadny neni.
  const [confirmRemove, setConfirmRemove] = React.useState(false);
  return (
    <div className="reos-party" data-testid={`contract-party-${role}-${party.index}`}>
      <div className="reos-party__head">
        <span className="reos-party__name">
          {roleLabel} {party.index + 1}
        </span>
        {/* Stitek stavu ma na mobilu vlastni radek (CSS `.reos-party__state`) —
            na 390 px ho hlavicka orizla (zivy nalez R.B. 1. 10.). */}
        {party.confirmed && party.values.jmeno !== '' ? (
          <span
            className="reos-origin-tag reos-party__state reos-f--klient"
            data-testid={`contract-party-state-${role}-${party.index}`}
          >
            {t('webAdmin.contracts.editor.partyConfirmed', { locale: LOCALE })}
          </span>
        ) : (
          <span
            className="reos-origin-tag reos-party__state reos-f--chybi"
            data-testid={`contract-party-state-${role}-${party.index}`}
          >
            {/* Krok UXS2 bod 4: stitek nesl visici pomlcku („… odkaz odeslán —"),
                protoze datum odeslani editor nezna.
                Krok SML2 bod 7: stitek uz NETVRDI ODESLANI. Tahle karta zna jen
                `field_values.source` (kdo hodnotu zadal), o pozvankach nevi —
                a tvrdila „odkaz odeslán" i tam, kde se nic neodeslalo (nalez
                UXS2 §7.2). O odkazu mluvi JEDNO misto: panel Strany vpravo,
                ktery cte skutecne pozvanky (`partyLinkStatus`). */}
            {t('webAdmin.contracts.editor.partyWaiting', { locale: LOCALE })}
          </span>
        )}
      </div>
      {partyFieldKeysFor(role).map((f) => {
        const controlId = `contract-party-${role}-${party.index}-${f}`;
        // Klic pole V DOKUMENTU — tentyz tvar, jaky ma `data-f` u strany
        // (`pronajimatel[].jmeno`), takze „B" prepne vsechny jeho vyskyty.
        const docFieldKey = `${PARTY_FIELD_PREFIX[role]}.${f}`;
        const fieldLabelText = t(PARTY_FIELD_LABEL_KEY[f], { locale: LOCALE });
        return (
          <div className="reos-field-row" key={f}>
            {/* Popisek je svazany se vstupem (`<label for>`) — krok UXS2 bod 4:
                `get_by_label('Jméno a příjmení')` do ted nenaslo nic. */}
            {/* Krok UXD3 bod 4: prepinac tucneho stoji VLEVO u radku, MIMO
                `<label>` — uvnitr popisku by klik na „B" zaroven zameril
                vstup a tlacitko by delalo dve veci naraz. */}
            <span className="reos-field-row__head">
              <BoldFieldToggle
                testId={`contract-bold-${role}-${party.index}-${f}`}
                on={boldOf(docFieldKey)}
                onToggle={(next) => onToggleBold(docFieldKey, next)}
              />
              <label className="reos-field-row__label" htmlFor={controlId}>
                {fieldLabelText}
              </label>
            </span>
            <span className="reos-field-row__input">
              {/* Rodne cislo je citlive pole (T1) — v UI se MASKUJE mimo fokus,
                  aby neprosaklo na obrazovku ani do snimku obrazovky. Krok UXD1
                  bod 1: makler ho ZADAVA SAM (odesilani odkazu klientum je v
                  pilotu vypnute, takze dřív nemel kudy RC zadat); klient pres
                  magic link (KP1) ho dal muze doplnit/opravit beze zmeny. */}
              <DraftTextInput
                value={party.values[f]}
                onCommit={(next) => onFieldChange(role, party.index, f, next)}
                {...(onFieldPreview !== undefined
                  ? { onPreview: (next: string) => onFieldPreview(role, party.index, f, next) }
                  : {})}
                onFocus={() => onFieldFocus?.(role, f)}
                id={controlId}
                testId={controlId}
                autoComplete="off"
                name={controlId}
                {...(f === 'rc'
                  ? { format: formatRcInput, maskWhenBlurred: maskRc, inputMode: 'numeric' }
                  : {})}
              />
            </span>
            {f === 'rc' && rcFormatLooksInvalid(party.values.rc) ? (
              <span className="reos-field-row__hint" data-testid={`${controlId}-warning`}>
                {t('webAdmin.contracts.editor.rcFormatWarning', { locale: LOCALE })}
              </span>
            ) : null}
          </div>
        );
      })}
      <div className="reos-party__actions">
        <button
          type="button"
          className="rh-btn"
          onClick={() => onSendLink(role)}
          data-testid={`contract-party-link-${role}-${party.index}`}
        >
          {t('webAdmin.contracts.editor.sendLink', { locale: LOCALE })}
        </button>
        {onRemove === undefined ? (
          <span
            className="reos-field-row__hint"
            data-testid={`contract-party-cannot-remove-${role}-${party.index}`}
          >
            {t('webAdmin.contracts.editor.cannotRemove', { locale: LOCALE })}
          </span>
        ) : confirmRemove ? (
          <span className="reos-party__confirm" data-testid={`contract-party-confirm-${role}-${party.index}`}>
            <span>{t('webAdmin.contracts.editor.removePartyConfirm', { locale: LOCALE })}</span>
            <button
              type="button"
              className="rh-btn rh-btn--danger"
              onClick={() => {
                setConfirmRemove(false);
                onRemove(role, party.index);
              }}
              data-testid={`contract-party-remove-yes-${role}-${party.index}`}
            >
              {t('webAdmin.contracts.editor.removePartyYes', { locale: LOCALE })}
            </button>
            <button
              type="button"
              className="rh-btn"
              onClick={() => setConfirmRemove(false)}
              data-testid={`contract-party-remove-no-${role}-${party.index}`}
            >
              {t('webAdmin.contracts.new.cancel', { locale: LOCALE })}
            </button>
          </span>
        ) : (
          <button
            type="button"
            className="rh-btn"
            onClick={() => setConfirmRemove(true)}
            data-testid={`contract-party-remove-${role}-${party.index}`}
          >
            {t('webAdmin.contracts.editor.removeParty', { locale: LOCALE })}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Souhrn kontrol JEDNIM RADKEM: „24 chybí · 2 doplnit později · 1 další
 * kontrola" (krok UXS2 bod 2). Nulove casti se vynechavaji — „0 dalších
 * kontrol" je informace o nicem; oddelovac je sazba, ne text.
 */
function checksSummaryText(lines: readonly CheckLine[]): string {
  const counts = checksSummary(lines);
  const parts: [number, MessageKey][] = [
    [counts.missing, 'webAdmin.contracts.editor.checksSummaryMissing'],
    [counts.later, 'webAdmin.contracts.editor.checksSummaryLater'],
    [counts.other, 'webAdmin.contracts.editor.checksSummaryOther'],
  ];
  return parts
    .filter(([count]) => count > 0)
    .map(([count, key]) => t(key, { locale: LOCALE, values: { count } }))
    .join(' · ');
}

/**
 * Poradi polozek panelu kontrol (krok SML2 bod 6).
 *
 * Predloha ma v „Kontrolách smlouvy" PRAVIDLA — kauce nejvys trojnasobek
 * nájemného, PENB jako příloha, shoda s LV. Radky „Chybí: …" uz jednou rika
 * osnova vlevo (a presneji: s rozpadem podle zdroje). Pravidla proto stoji
 * v seznamu PRVNI: panel je sbaleny na pet polozek (UXS2 bod 2) a u smlouvy
 * s 24 chybejicimi udaji by se jinak pravidlo do tech peti nikdy nevesло.
 */
function orderedCheckLines(lines: readonly CheckLine[]): readonly CheckLine[] {
  const rank = (line: CheckLine): number =>
    line.kind === 'chybi' ? 1 : line.kind === 'pozdeji' ? 2 : 0;
  return [...lines].sort((a, b) => rank(a) - rank(b));
}

/**
 * TLACITKO „B" VLEVO U RADKU FORMULARE (krok UXD3 bod 4, R.B. 12. 10. 21:47
 * „možnost nastavení tučně vlevo").
 *
 * Vychozi poloha je volba smlouvy („Vyplněné údaje tučně"); klik ji pro TOTO
 * POLE prebije a plati na VSECHNY jeho vyskyty v dokumentu — jmeno
 * pronajimatele se nikdy nesazi na dvou mistech dvema zpusoby. Stav pocita
 * `boldForFieldInContract` (`lib/contract-doc.ts`), tedy totez pravidlo,
 * jake ma server v `boldForField`.
 */
function BoldFieldToggle({
  testId,
  on,
  onToggle,
}: {
  /**
   * Prepinac je VZDY jeden na RADEK formulare, ne na pole: dve strany teze
   * role maji tytez klice pole (`pronajimatel[].jmeno`), takze testid podle
   * klice by na smlouve se dvema pronajimateli existoval dvakrat a kazdy
   * e2e `getByTestId` by skoncil na „strict mode violation". Co prepinac
   * ZMENI, je dal cele POLE — obe karty se prepnou spolu.
   */
  readonly testId: string;
  readonly on: boolean;
  readonly onToggle: (next: boolean) => void;
}): React.JSX.Element {
  /*
   * Popisek NEOPAKUJE nazev pole, i kdyz by to znelo lip. Prepinac stoji
   * v tomtez radku jako popisek pole, takze kontext uz tam je — a jakmile
   * nazev pole stal i v `aria-label` tlacitka, `get_by_label('Název
   * kanceláře')` naslo DVA prvky a UXS2 bod 4 (popisek svazany se vstupem)
   * zcervenal. Rozlisit radky umi `data-testid`.
   */
  const title = t(
    on ? 'webAdmin.contracts.editor.boldFieldOn' : 'webAdmin.contracts.editor.boldFieldOff',
    { locale: LOCALE },
  );
  return (
    <button
      type="button"
      className={`reos-boldtoggle${on ? ' reos-boldtoggle--on' : ''}`}
      aria-pressed={on}
      title={title}
      aria-label={title}
      onClick={() => onToggle(!on)}
      data-testid={testId}
    >
      B
    </button>
  );
}

/** Sbalovaci oddil levneho panelu editoru (mockup: sekce po clancich). */
function EditorSection({
  title,
  children,
  open = true,
  testId,
}: {
  readonly title: string;
  readonly children: React.ReactNode;
  readonly open?: boolean;
  readonly testId?: string | undefined;
}): React.JSX.Element {
  return (
    <details className="reos-field-section" open={open} {...(testId !== undefined ? { 'data-testid': testId } : {})}>
      <summary className="reos-field-section__title">{title}</summary>
      {children}
    </details>
  );
}

/**
 * Dokument A4: HTML sklada server (stejny renderer jako tisk a verze), kazda
 * dosazena hodnota je obalena podle puvodu udaje.
 *
 * PROC `React.memo`: React nastavuje `dangerouslySetInnerHTML` pri KAZDEM
 * prekresleni, takze cely podstrom dokumentu se vyrobi znovu. Kdyz clovek
 * dopsal do pole a hned klikl do dokumentu, prekresleni (ulozeni na pozadi)
 * vymenilo uzel mezi `mousedown` a `mouseup` — prohlizec pak `click` vubec
 * nevyvolal a fokus zustal na `BODY`. Presne to je nalez R.B. 1. 10. („klik
 * nezaměří pole"); se starsi sablonou na nej slo narazit spolehlive, protoze
 * jeji strana ma pevne misto a kazdy znak vyvola ulozeni. Memo drzi uzly na
 * miste, dokud se nezmeni samo HTML dokumentu (nebo rezim nahledu).
 *
 * V rezimu „Upravit text smlouvy" je psani zapnute na CELEM dokumentu
 * (atribut na Reactem rizenem prvku, takze prezije prekresleni) a pole v nem
 * zustavaji nedelitelna — `contract.ts` jim dava `contenteditable="false"`.
 */
const ContractDocument = React.memo(function ContractDocument({
  innerRef,
  html,
  cleanView,
  editText,
}: {
  readonly innerRef: React.RefObject<HTMLDivElement | null>;
  readonly html: string;
  readonly cleanView: boolean;
  readonly editText: boolean;
}): React.JSX.Element {
  return (
    <div
      ref={innerRef}
      className={`reos-doc reos-preview--origin${cleanView ? ' reos-preview--clean' : ''}`}
      data-testid="contract-rendered-html"
      data-clean={cleanView ? '1' : '0'}
      data-edit={editText ? '1' : '0'}
      contentEditable={editText}
      suppressContentEditableWarning
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
});

/**
 * Zivy pruchod 13. 10. (V3): `window.open` smi prohlizec povolit jen jako
 * PRIMY dusledek uzivatelskeho gesta (klik) — kdyz se zavola az PO `await`
 * (tedy v jine mikrouloze, nez ve ktere probehl click handler), prohlizec ho
 * tise zablokuje a vrati prazdnou kartu `about:blank` bez chyby v konzoli.
 * Presne tak vypadalo puvodni `doPrint`: `await printContract(...)` pred
 * `window.open`. Oprava: okno se otevre SYNCHRONNE hned v click handleru,
 * HTML se do nej dopise, az kdyz dorazi ze serveru.
 */
function openPrintWindow(): Window | null {
  if (typeof window === 'undefined') return null;
  return window.open('', '_blank');
}

/**
 * Dopise HTML se SAZBOU dokumentu do uz otevreneho okna a spusti tisk.
 * Krok PRF5 bod 4: sazbu posila SERVER spolu s HTML (`documentCss`) — admin
 * uz zadnou svou kopii nema, takze tisk, editor i verejny web maji tutez.
 */
function writePrintWindow(win: Window, contractHtml: string, documentCss: string, title: string): void {
  win.document.write(printDocumentHtml(contractHtml, documentFileName(title), documentCss));
  win.document.close();
  win.focus();
  win.print();
}

/**
 * EDITOR JEDNE SMLOUVY — `/smlouvy/:id` (krok UXS1 bod 3 a 4).
 *
 * Levy panel = formular (strany, predmet, pole po clancich, sbalovaci sekce),
 * pravy panel = DOKUMENT A4 vykresleny tymz rendererem jako tisk a ulozena
 * verze (`renderContract` v `@reos/documents`, sazba `documentCss` z DTO).
 * Hodnoty v dokumentu jsou zvyraznene podle puvodu, klik na zvyraznene misto
 * zameri pole ve formulari a naopak; nad dokumentem je legenda a panel
 * „Kontroly před odesláním".
 *
 * Co to nahradilo (R.B. 1. 10.): vlevo dlouhy formular, vpravo HOLY text se
 * slugem sablony, cisly odstavcu v zavorkach a cervenymi „[…]".
 */
export function ContractEditorScreen({
  contractId,
  initialTab,
}: {
  readonly contractId: string;
  readonly initialTab?: EditorTabId | undefined;
}): React.JSX.Element {
  const [tab, setTab] = React.useState<EditorTabId>(initialTab ?? 'editor');
  /**
   * Krok SML5 bod 3 / DEMO7b — V UKAZKOVE KANCELARI SE NIC NEODESLALO.
   *
   * `GET /api/me` → `flags.demo` (tentyz zdroj jako „Odesláno (demo)" na
   * fakturaci). Stitek u strany to musi RICT: „odkaz odeslán" bez te
   * priznaky je tiche tvrzeni, ze klient zpravu dostal.
   */
  const demoOffice = useSession((s) => s.demo);
  const navigate = useNavigate();
  /**
   * Krok UXD1 bod 11: v rezimu soustredeni (focus mode) schova `AdminShell`
   * celou horni listu i postranni panel (CSS `[data-focus-mode='true']`),
   * takze „Odhlásit" tu dřiv nebylo videt vubec. Tatáž cesta jako
   * `LayoutShell.handleLogout` — zadna druha implementace.
   */
  const handleLogout = React.useCallback(() => {
    void (async () => {
      try {
        await signOut();
      } finally {
        resetSession();
        void navigate({ to: '/login' });
      }
    })();
  }, [navigate]);
  const [contract, setContract] = React.useState<ContractDetailDto | null>(null);
  const [catalogs, setCatalogs] = React.useState<ContractCatalogsDto | null>(null);
  const [customSubjects, setCustomSubjects] = React.useState<CustomSubjectDto[]>([]);
  const [versions, setVersions] = React.useState<ContractVersionDto[]>([]);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [status, setStatus] = React.useState<SaveStatusKind>('idle');
  // Mockup: tlacitko prepina mezi barevnym a cistym nahledem.
  const [cleanView, setCleanView] = React.useState(false);
  // Mockup: „Upravit text smlouvy" = psani PRIMO do dokumentu A4.
  const [editText, setEditText] = React.useState(false);
  /** Krok UXS2 bod 2: panel kontrol je sbaleny na pet polozek, „Zobrazit vše". */
  const [allChecks, setAllChecks] = React.useState(false);
  /**
   * Krok NAP7 bod A17 — vysledky trech AI akci. `null` = akce jeste nebezela;
   * rozhodnuti „AI je/neni dostupna" prinasi SERVER (`aiAvailable`), admin si
   * ho nedopocitava z prazdneho vysledku.
   */
  const [aiReview, setAiReview] = React.useState<ContractAiReviewResultDto | null>(null);
  const [aiExplanation, setAiExplanation] = React.useState<ContractAiExplanationResultDto | null>(
    null,
  );
  const [versionDiff, setVersionDiff] = React.useState<ContractVersionDiffResultDto | null>(null);
  const [aiBusy, setAiBusy] = React.useState<'review' | 'explain' | 'diff' | null>(null);
  /**
   * Krok UXD1 bod 2 — lista „kam co psat" nad levym formularem (zivy nalez
   * pilotu: maklerka nevedela, ktery sloupec se vyplnuje). Zavira se a stav
   * si pamatuje prohlizec (`stores/session.ts`, jedine povolene misto).
   */
  const [whereHint, setWhereHint] = React.useState(() => !readContractWhereHintDismissed());
  /**
   * Krok UXD1 bod 3 — v rezimu „Upravit text smlouvy" je pole v dokumentu
   * `contenteditable="false"`, takze klik do nej jen tise nic nedela. Tady
   * se pamatuje, na ktere pole clovek klikl: nad dokumentem se o nem objevi
   * napoveda, ktera po kliknuti prepne do „Vyplnit" se zamerenym polem.
   */
  const [lockedFieldHint, setLockedFieldHint] = React.useState<{
    readonly fieldKey: string;
    readonly index: string | null;
  } | null>(null);
  /**
   * Krok UXD1 body 3 a 5 — ODLOZENE ZAMERENI POLE. Zamerit pole jde teprve
   * ve chvili, kdy jeho ovladaci prvek je v DOM; to u „+ Další pronajímatel"
   * nastane az po odpovedi serveru a u prepnuti z „Upravit text" az po
   * prekresleni do rezimu „Vyplnit". Oba pripady proto zapisi CIL a zameri
   * ho jeden efekt — ne dve kopie teze posloupnosti.
   */
  const [pendingFocus, setPendingFocus] = React.useState<{
    readonly fieldKey: string;
    readonly index: string | null;
  } | null>(null);
  const [sendStatus, setSendStatus] = React.useState<SaveStatusKind>('idle');
  /** Krok UXP3 (P1-6): PRAVDA o tom, co se stranam skutecne odeslalo. */
  const [sendNote, setSendNote] = React.useState<string | null>(null);
  /**
   * Krok APP3 bod 4 (nalez A40): vysledek zapisu adres stran do adresare.
   * `conflicts` drzi rozpor, ktery server VEDOME neprepsal.
   */
  const [addressSync, setAddressSync] = React.useState<ContractPartyAddressSyncDto | null>(null);
  const [addressSyncNote, setAddressSyncNote] = React.useState<string | null>(null);
  const [templateLine, setTemplateLine] = React.useState<string | null>(null);
  /**
   * Krok NOC3 bod 6 (nalez N14): popisky VLASTNICH POLI vzoru kancelare
   * (`extraFields`). Jdou z TEHOZ volani, ktere uz kresli radku „Šablona:"
   * — zadny dalsi dotaz na server.
   */
  const [templateFields, setTemplateFields] = React.useState<ReadonlyMap<string, string>>(new Map());
  const [caseTitle, setCaseTitle] = React.useState<string | null>(null);
  /** Pole, na kterem stoji kurzor — svetlo v dokumentu i ve formulari. */
  const [activeField, setActiveField] = React.useState<string | null>(null);
  /** Poradi strany u pole opakovaneho bloku (`data-i` v dokumentu). */
  const [activeIndex, setActiveIndex] = React.useState<string | null>(null);
  const [focusedBlockId, setFocusedBlockId] = React.useState<string | null>(null);
  /**
   * Krok KLIKFIX1 (vada B4): ktera uprava textu se NEULOZILA a proc. `null` =
   * posledni ulozeni zahodilo nic, cili se nic nehlasi.
   */
  const [textEditSkipped, setTextEditSkipped] = React.useState<{
    readonly list: boolean;
    readonly field: boolean;
  } | null>(null);
  /**
   * Krok SML2 bod 1: treti rezim predlohy — „Náhled pro klienta". Je to
   * dokument BEZ pracovnich panelu a bez barev zdroju, tedy presne to, co
   * uvidi klient; proto se s nim zapina i cisty nahled.
   */
  const [clientPreview, setClientPreview] = React.useState(false);
  /**
   * „uloženo HH:MM" v hlavicce (predloha). Pri otevreni to je `updated_at`
   * smlouvy — hlavicka tak nemlci u smlouvy, se kterou dnes nikdo nehybal;
   * pri kazdem dalsim ulozeni se prepise na `clock.now()`.
   */
  const [savedAt, setSavedAt] = React.useState<string | null>(null);
  /** Krok SML2 bod 7: stav odkazu strany podle SKUTECNYCH pozvanek. */
  const [invitations, setInvitations] = React.useState<readonly PartyInvitationRef[]>([]);
  /** Krok SML2 bod 5: fakta zakazky pro „návrh z dat zakázky". */
  const [propertyFacts, setPropertyFacts] = React.useState<readonly PropertyFact[]>([]);
  /**
   * Krok P8F5 body 2 a 3: co se v zakazce zmenilo od prevzeti udaje do teto
   * smlouvy (72 → 75) a ktera rucni hodnota se od zakazky lisi. Pocita SERVER
   * (`GET /v1/contracts/:id/fact-changes`) a jen u konceptu — u odeslane a
   * podepsane smlouvy vraci prazdny seznam, takze se tu nic nenabizi (bod 4).
   */
  const [factChanges, setFactChanges] = React.useState<readonly ContractFactChangeDto[]>([]);
  /** Stav tlacitka „Uložit i do zakázky" per pole: `null` = nic nerika. */
  const [savedToCase, setSavedToCase] = React.useState<Readonly<Record<string, 'ok' | 'error'>>>({});
  /** Vysledek posledniho „Doplnit z katastru" — co doplnil a co ne a proc. */
  const [prefillResult, setPrefillResult] =
    React.useState<{ readonly filled: number; readonly skipped: readonly string[] } | null>(null);
  const [prefillBusy, setPrefillBusy] = React.useState(false);
  /**
   * Krok SML2b bod 3: „Připojit k zakázce" u smlouvy BEZ ZAKAZKY. Seznam
   * zakazek se nacita jen tehdy, kdyz smlouva zadnou nema — u smlouvy
   * v zakazce by to byl dotaz pro nic.
   */
  const [attaching, setAttaching] = React.useState(false);
  const [attachQuery, setAttachQuery] = React.useState('');
  const [caseIndex, setCaseIndex] = React.useState<ReadonlyMap<string, string> | null>(null);
  const previewRef = React.useRef<HTMLDivElement | null>(null);
  const fieldsRef = React.useRef<HTMLDivElement | null>(null);
  /**
   * Rozepsane hodnoty, ktere server jeste nevratil: `klic#poradi` → text
   * (krok UXS2 bod 1). Drzi je ref, ne stav — nejde o vykreslení Reactu,
   * ale o prepis serveroveho HTML v dokumentu.
   */
  const localEdits = React.useRef(new Map<string, string>());
  const lastRenderedHtml = React.useRef<string | null>(null);
  /** Bezi ukladani rucnich uprav textu — brani dvojimu zapisu teze verze. */
  const savingBlocks = React.useRef(false);
  /**
   * Krok KLIKFIX1 (vada B4): id odstavcu, do kterych clovek v rezimu
   * „Upravit text" psal. Ref, ne stav — psani nema prekreslovat dokument.
   */
  const editedBlocks = React.useRef(new Set<string>());
  /**
   * Krok SML3 bod 2: prepinac „Zobrazit změny" (predloha ho ma ZASKRTNUTY).
   * Dokument se pak sazi se sazbou diffu, kterou posila server
   * (`renderedHtmlWithChanges`) — admin si zadny diff nepocita.
   */
  const [showChanges, setShowChanges] = React.useState(true);
  /** Krok SML3 bod 3: knihovna klauzuli kancelare + hledani v ni. */
  const [clauses, setClauses] = React.useState<readonly ContractClauseDto[]>([]);
  const [clauseQuery, setClauseQuery] = React.useState('');
  /** Jmena kolegu k `templateChanges[].by` — stejny zdroj jako avatary (R4). */
  const [members, setMembers] = React.useState<ReadonlyMap<string, string>>(new Map());
  /** Vysledek „Uložit jako nový mustr": nazev a verze, kterou server zalozil. */
  const [savedTemplate, setSavedTemplate] = React.useState<string | null>(null);
  /**
   * STAV TLACITKA „Stáhnout PDF" (krok QAFIX7). PDF sazi server (Gotenberg)
   * a trva to sekundy — do QAFIX7 o tom obrazovka NERIKALA NIC: makleřka
   * klikla, nic se nedelo a kdyz se spojeni pretrhlo, dozvedela se to jen
   * z obecneho pasu chyb nahore. Stav je proto VIDET u tlacitka samotneho
   * („PDF se připravuje…" / hlaska o selhani) a `data-state` ho rika i strojove.
   */
  const [pdfState, setPdfState] = React.useState<'idle' | 'preparing' | 'error'>('idle');

  const reload = React.useCallback(async (): Promise<void> => {
    try {
      const [detail, vs] = await Promise.all([
        getContract(contractId),
        listContractVersions(contractId),
      ]);
      setContract(detail);
      setVersions(vs);
      setSavedAt(formatClockTime(new Date(detail.updatedAt)));
    } catch (err) {
      setError(errorMessage(err) ?? t('webAdmin.contracts.editor.loadError', { locale: LOCALE }));
    }
  }, [contractId]);

  React.useEffect(() => {
    void reload();
    void listCustomSubjects().then(setCustomSubjects);
  }, [reload]);

  /*
   * Krok NOC4 bod 6 (nalez N25): katalog sluzeb se pta ZA TUHLE SMLOUVU —
   * nebytovy najem ma vlastni, kratky seznam. Dokud smlouva nenabehne, pta se
   * bez typu (bytovy katalog), stejne jako obrazovka „Nová smlouva".
   */
  const catalogContractType = contract?.contractType;
  React.useEffect(() => {
    void getContractCatalogs(catalogContractType).then(setCatalogs, () => undefined);
  }, [catalogContractType]);

  // Hlavicka: „Šablona: Nájemní smlouva – mustr kanceláře v1".
  React.useEffect(() => {
    if (contract === null || contract.sourceKey === '') return;
    let cancelled = false;
    void Promise.all([listOfficeTemplates(), listDemoTemplates()])
      .then(([office, demo]) => {
        if (cancelled) return;
        const found = office.find((x) => x.id === contract.sourceKey);
        const demoTpl = demo.find((x) => x.id === contract.sourceKey);
        setTemplateFields(
          new Map((found?.extraFields ?? []).map((field) => [field.key, field.label])),
        );
        if (found !== undefined) {
          setTemplateLine(
            t('webAdmin.contracts.templateLine.label', {
              locale: LOCALE,
              values: { name: found.name, version: found.version },
            }),
          );
        } else if (demoTpl !== undefined) {
          setTemplateLine(
            t('webAdmin.contracts.templateLine.label', {
              locale: LOCALE,
              values: { name: demoTpl.nazev, version: 1 },
            }),
          );
        }
      })
      .catch(() => setTemplateLine(null));
    return () => {
      cancelled = true;
    };
  }, [contract]);

  /**
   * Krok SML3 bod 3: knihovna klauzuli se nacita, TEPRVE kdyz je otevreny
   * rezim „Upravit text" — u vyplnovani smlouvy by to byl dotaz pro nic.
   * Hledani jde na server (`?q=`), aby pravidlo hledani bylo jedno.
   */
  React.useEffect(() => {
    if (!editText) return;
    let cancelled = false;
    void listContractClauses(clauseQuery).then(
      (items) => {
        if (!cancelled) setClauses(items);
      },
      () => {
        if (!cancelled) setClauses([]);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [editText, clauseQuery]);

  /**
   * Jmena ke zmenam proti mustru (predloha: „Richard · 23:38"). Bere se
   * `GET /v1/members` — tentyz zdroj, z jakeho jsou avatary na kanbanu; do DB
   * se jmeno u zmeny neuklada, aby se nekonzervovalo jmeno, ktere se mezitim
   * zmenilo.
   */
  React.useEffect(() => {
    if (!editText) return;
    let cancelled = false;
    void listMembers().then(
      (items) => {
        if (!cancelled) setMembers(new Map(items.map((m) => [m.userId, m.name])));
      },
      () => {
        /* bez jmen se u zmeny napise jen cas — seznam zmen tim neprijde */
      },
    );
    return () => {
      cancelled = true;
    };
  }, [editText]);

  // Drobecky „Zakázky › <adresa zakázky> › Smlouvy" (mockup `.crumb`);
  // u smlouvy BEZ zakazky „Smlouvy › <název smlouvy>" (SML2b bod 4).
  const caseId = contract?.caseId ?? null;
  /*
   * Krok KLIKFIX1 (oprava e2e 1, 30. 10. 2026) — DROBECEK VYCERPAL CELOU KVOTU.
   *
   * Nazev JEDNE zakazky se tu bral ze `fetchCaseAddressIndex()`, tedy ze SKENU
   * CELE KANCELARE (`CASE_SCAN_MAX_PAGES` = 100 dotazu po 100 zakazkach). Beh
   * S12/mobil proti preview 30. 10. ukazuje v trace 100 dotazu
   * `GET /v1/cases?limit=100&cursor=…` za tri sekundy, ktere srazily
   * `x-ratelimit-remaining` z 1200 na 0 — a vsechno, co na editoru nasledovalo,
   * dostalo 429: pozvanky, lhuty i `POST …/prefill-cadastre`. „Doplnit z
   * katastru" tak tise neudelalo nic a `contract-prefill-note` se nevykreslila.
   * Neni to vada sady: tohle potka i makléřku, jen co kancelar naroste.
   *
   * Nazev jedne zakazky ma SSOT uz od kroku P8F6 — `caseNameWithUnit` (tri
   * dotazy na jednu zakazku), kterou tytez drobecky pouziva v „Nové smlouvě"
   * o ctyri tisice radku vys. Index zustava tam, kde se prekladaji nazvy VIC
   * znamych id (prehled smluv, naklady).
   */
  React.useEffect(() => {
    if (caseId === null) return;
    let cancelled = false;
    void caseNameWithUnit(caseId).then(
      (name) => {
        if (!cancelled) setCaseTitle(name);
      },
      // Selhani nazvu nesmi shodit editor — drobecek zbyde na „Zakázka".
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
  }, [caseId]);

  // Krok SML2b bod 3: zakazky k privazani. Tentyz zdroj, z ktereho vybira
  // prehled `/smlouvy` (ADM1 bod 2) — jeden seznam, jedna nazvoslovna pravda.
  React.useEffect(() => {
    if (!attaching) return;
    let cancelled = false;
    void fetchCaseAddressIndex()
      .then(({ byCaseId }) => {
        if (!cancelled) setCaseIndex(byCaseId);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [attaching]);

  /**
   * Krok SML2 bod 7: stav strany ze SKUTECNYCH pozvanek. Do SML2 editor
   * o pozvankach nevedel a stitek tvrdil „odkaz odeslán" i tam, kde se nic
   * neodeslalo (nalez UXS2 §7.2). Kdyz volani selze, seznam zustane prazdny
   * a strana se chova jako bez pozvanky — zadna vymyslena informace.
   */
  React.useEffect(() => {
    let cancelled = false;
    void listClientInvitations({ contractId })
      .then((items) => {
        if (!cancelled) setInvitations(items);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [contractId]);

  /**
   * Krok SML2 bod 5: „návrh z dat zakázky" — fakta nemovitosti (inzerat,
   * prověření). Nacitaji se jen kdyz zakazka nemovitost ma; bez nich panel
   * vpravo o navrhu vubec nemluvi.
   */
  const propertyId = contract?.propertyId ?? null;
  React.useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      const id = propertyId ?? (caseId === null ? null : ((await getCase(caseId)).propertyId ?? null));
      if (id === null) return;
      const facts = await fetchPropertyFacts(id);
      if (!cancelled) setPropertyFacts(facts);
    }
    void load().catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [propertyId, caseId]);

  /*
   * Krok P8F5 body 2–4: zmeny zakazky proti prevzatym udajum. Sleduje
   * `updatedAt` smlouvy, takze po kazdem ulozeni (prevzit, ponechat, rucni
   * oprava) se seznam prepocita — jinak by hlaska „72 → 75" zustala na
   * obrazovce i po kliknuti na „Převzít". Stav smlouvy je v zavislosti
   * zamerne: po odeslani k podpisu se nabidka musi PRESTAT ptat (bod 4).
   */
  const contractStatus = contract?.status ?? null;
  const contractUpdatedAt = contract?.updatedAt ?? null;
  React.useEffect(() => {
    if (contractStatus === null) return;
    if (contractStatus !== 'draft') {
      setFactChanges([]);
      return;
    }
    let cancelled = false;
    void getContractFactChanges(contractId)
      .then((changes) => {
        if (!cancelled) setFactChanges(changes);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [contractId, contractStatus, contractUpdatedAt]);

  async function patch(input: Parameters<typeof updateContract>[1]): Promise<void> {
    setStatus('saving');
    try {
      const updated = await updateContract(contractId, input);
      setContract(updated);
      setStatus('saved');
      // Hlavicka predlohy pise „uloženo 23:41". Cas bere `clock.now()`
      // z @reos/config (CLAUDE.md §3: zadne `new Date()`).
      setSavedAt(formatClockTime(systemClock.now()));
    } catch (err) {
      setStatus('error');
      setError(errorMessage(err));
    }
  }

  /**
   * Krok P8F5 bod 2 — „Převzít 75 m²". Hodnota se do smlouvy zapise a pamet
   * prevzeti se posune na nove cislo, takze tatáz zmena uz podruhe neupozorni.
   * Zapis jde beznym `PATCH` pole: zadna zvlastni mutacni cesta, a tedy ani
   * zadne druhe misto, ktere by umelo smlouvu zmenit jinak.
   */
  async function takeFactValue(fieldKey: string): Promise<void> {
    const change = factChanges.find((c) => c.fieldKey === fieldKey);
    const fromFact = contract?.fieldValues[fieldKey]?.fromFact;
    if (change === undefined || fromFact === undefined) return;
    await patch({
      fieldValues: {
        [fieldKey]: {
          value: change.factValue,
          source: contract?.fieldValues[fieldKey]?.source ?? 'broker',
          fromFact: {
            factKeys: [...fromFact.factKeys],
            takenValue: change.factValue,
            reviewedValue: change.factValue,
            choice: 'prevzit',
          },
        },
      },
    });
  }

  /**
   * Krok P8F5 bod 2 — „Ponechat 72". Hodnota ve smlouve zustava, jen se
   * zapamatuje, ze clovek TOHLE cislo ze zakazky uz videl a odmitl. Dalsi
   * zmena (75 → 78) upozorni znovu, protoze se lisi od posouzene hodnoty.
   */
  async function keepFactValue(fieldKey: string): Promise<void> {
    const change = factChanges.find((c) => c.fieldKey === fieldKey);
    const entry = contract?.fieldValues[fieldKey];
    const fromFact = entry?.fromFact;
    if (change === undefined || entry === undefined || fromFact === undefined) return;
    await patch({
      fieldValues: {
        [fieldKey]: {
          value: entry.value,
          source: entry.source,
          fromFact: {
            factKeys: [...fromFact.factKeys],
            takenValue: fromFact.takenValue,
            reviewedValue: change.factValue,
            choice: 'ponechat',
          },
        },
      },
    });
  }

  /**
   * Krok P8F5 bod 3 — „Uložit i do zakázky". JEDINA cesta, kterou se hodnota
   * ze smlouvy dostane na kartu nemovitosti: na klepnuti maklere, nikdy tise.
   * `factKey` i prevedenou hodnotu posila server (`saveToCase`) — admin si je
   * neskladá, mapa pole→fakt zije v `@reos/schemas` a ma jednu kopii.
   */
  async function saveFieldToCase(fieldKey: string): Promise<void> {
    const change = factChanges.find((c) => c.fieldKey === fieldKey);
    const target = change?.saveToCase;
    const id = contract?.propertyId ?? null;
    if (target === undefined || id === null) return;
    try {
      await savePropertyFactFromContract(id, target.factKey, target.value);
      setSavedToCase((prev) => ({ ...prev, [fieldKey]: 'ok' }));
      setFactChanges((prev) => prev.filter((c) => c.fieldKey !== fieldKey));
    } catch {
      setSavedToCase((prev) => ({ ...prev, [fieldKey]: 'error' }));
    }
  }

  /**
   * Prepinac rezimu predlohy (krok SML2 bod 1). Drzi se STAVU, ktery uz
   * editor ma (`editText`, `cleanView`), aby dnesni tlacitka „Upravit text
   * smlouvy" a „Čistý náhled" ukazovala totez — dva nezavisle stavy by se
   * rozesly a prepinac by lhal.
   */
  const mode: EditorMode = editText ? 'text' : clientPreview ? 'client' : 'fill';

  /**
   * Krok SML3 bod 2 a 4: zmeny proti mustru a zamek textu — OBOJI ZE SERVERU.
   * Starsi odpoved serveru je nenese (`undefined`); tehdy se editor chova jako
   * pred timhle krokem: zadne zmeny k ukazani, text neni zamceny. Admin si
   * ani jedno nedopocitava — druha kopie pravidla by se s branou rozesla.
   */
  const changes: readonly ContractTextChangeDto[] = contract?.templateChanges ?? [];
  const textLocked = contract?.textEditable === false;
  /**
   * HTML dokumentu: v rezimu zmen se sazi diff proti mustru, ktery posila
   * server. Kdyz se smlouva od mustru nelisi, server ho neposila (`null`)
   * a sazi se obvykly barevny nahled.
   */
  /*
   * Krok NOC4 bod 4 (nalez N23) — „NÁHLED PRO KLIENTA" JE TO, CO KLIENT
   * OPRAVDU DOSTANE.
   *
   * Do NOC4 sazel i tenhle rezim BAREVNY render editoru
   * (`renderedHtmlWithOrigins`) a jen CSS schovalo panely. Prazdne pole v nem
   * ale nese POJMENOVANOU MEZERU pro maklere („Termín předání · doplní se
   * později"), zatimco PDF i verze k podpisu tiskne pravni formulaci
   * („dle dohody"). Klient, kteremu makler posle odkaz na nahled, tak videl
   * text, ktery ve smlouve nikdy nebude. Nahled pro klienta proto bere TENTYZ
   * cisty render, z jakeho vznika PDF (`renderedHtml`) — jedna pravda pro
   * vsechny tri cesty.
   */
  const documentHtml =
    editText && showChanges && (contract?.renderedHtmlWithChanges ?? null) !== null
      ? (contract?.renderedHtmlWithChanges ?? '')
      : clientPreview
        ? (contract?.renderedHtml ?? '')
        : (contract?.renderedHtmlWithOrigins ?? '');
  /** Hledani v knihovne klauzuli — „+ Vložit klauzuli" z panelu nastroju tam skoci. */
  const clauseSearchRef = React.useRef<HTMLInputElement | null>(null);

  function setMode(next: EditorMode): void {
    setEditText(next === 'text');
    setClientPreview(next === 'client');
    if (next === 'client') setCleanView(true);
    if (next === 'fill') setCleanView(false);
  }

  /**
   * Krok UXD3 bod 3 a 4 — VYPLNENE UDAJE TUCNE.
   *
   * `boldOf` je poloha prepinace, ne sazba: sazi SERVER (`boldForField`
   * v @reos/documents) a dokument se vrati s PATCHem. Prepinac se ale musi
   * prepnout HNED, jinak by klik vypadal, ze nic neudelal — proto se stav
   * smlouvy prepise lokalne a server ho pak stejne prepise svym.
   */
  const boldOf = (fieldKey: string): boolean =>
    contract === null ? false : boldForFieldInContract(fieldKey, contract);

  function onToggleBoldAll(next: boolean): void {
    if (contract === null) return;
    setContract({ ...contract, boldFilledFields: next });
    void patch({ boldFilledFields: next });
  }

  function onToggleBoldField(fieldKey: string, next: boolean): void {
    if (contract === null) return;
    // Kdyz se pole vrati k volbe smlouvy, VYJIMKA SE RUSI (`null`) — jinak
    // by v ni zustal klic, ktery uz nic neznamena, a pozdejsi prepnuti celé
    // volby by se u toho pole neprojevilo.
    const base = contract.boldFilledFields === true;
    const overrides: Record<string, boolean> = { ...(contract.boldFieldOverrides ?? {}) };
    if (next === base) delete overrides[fieldKey];
    else overrides[fieldKey] = next;
    setContract({ ...contract, boldFieldOverrides: overrides });
    void patch({ boldFieldOverrides: { [fieldKey]: next === base ? null : next } });
  }

  /**
   * Krok SML2 bod 3: „Doplnit N údajů z katastru". Hodnoty hleda server
   * (fakta zakazky zna jen on); editor vypise, co doplnil a co ne a proc.
   */
  async function onPrefillCadastre(): Promise<void> {
    setPrefillBusy(true);
    setStatus('saving');
    try {
      const result = await prefillContractFromCadastre(contractId);
      setContract(result.contract);
      setStatus('saved');
      setSavedAt(formatClockTime(systemClock.now()));
      setPrefillResult({
        filled: result.filled.length,
        skipped: result.skipped
          .filter((s) => s.reason === 'bez-faktu')
          .map((s) => fieldLabel(catalogs, s.fieldKey, templateFields)),
      });
    } catch (err) {
      setStatus('error');
      setError(errorMessage(err));
    } finally {
      setPrefillBusy(false);
    }
  }

  async function onSubjectChange(value: string): Promise<void> {
    if (value.startsWith('custom:')) {
      const id = value.slice('custom:'.length);
      const found = customSubjects.find((c) => c.id === id);
      if (found !== undefined) {
        const pad = found.forms.pad as [string, string, string, string, string, string, string];
        await patch({ customSubject: { ...found.forms, pad } });
      }
      return;
    }
    if (value === '') return;
    await patch({ subjectType: value as 'byt' | 'dum' | 'garaz' | 'sklad' | 'prodejna' | 'pozemek' });
  }

  async function onServiceStateChange(key: string, next: ServiceState): Promise<void> {
    if (contract === null) return;
    await patch({ serviceStates: { ...contract.serviceStates, [key]: next } });
  }

  /** Mockup: pole strany jde prepsat v editoru (jmeno, bydliste, e-mail, telefon). */
  async function onPartyFieldChange(
    role: PartyRole,
    index: number,
    field: PartyFieldKey,
    value: string,
  ): Promise<void> {
    if (contract === null) return;
    await patch({ fieldValues: partyFieldPatch(contract.fieldValues, role, index, field, value) });
  }

  /** Mockup: „+ pronajimatel" / „+ najemce" — prazdna strana na konec role. */
  async function onAddParty(role: PartyRole): Promise<void> {
    if (contract === null) return;
    // Krok UXD1 bod 5: nova karta je POSLEDNI strana te role (`addPartyPatch`
    // pripoji polozku na konec seznamu), takze jeji poradi je dnesni pocet
    // stran role. Zamereni dela efekt, az karta bude v DOM.
    const nextIndex = partiesOf(contract.fieldValues, [role]).length;
    await patch({ fieldValues: addPartyPatch(contract.fieldValues, role) });
    setPendingFocus({ fieldKey: `${PARTY_FIELD_PREFIX[role]}.jmeno`, index: String(nextIndex) });
  }

  /** Krok UXS2 bod 3: „Odebrat" u kazde strany kromě prvni. */
  async function onRemoveParty(role: PartyRole, index: number): Promise<void> {
    if (contract === null) return;
    const fieldValues = removePartyPatch(contract.fieldValues, role, index);
    if (fieldValues === null) return;
    await patch({ fieldValues });
  }

  /** Text bloku ze sablony (pro porovnani, jestli rucni uprava neco zmenila). */
  function templateTextOf(blockId: string): string {
    for (const article of contract?.articles ?? []) {
      for (const block of article.blocks) if (block.id === blockId) return block.text;
    }
    return '';
  }

  async function revertOverride(blockId: string): Promise<void> {
    setStatus('saving');
    try {
      const updated = await revertBlockOverride(contractId, blockId);
      setContract(updated);
      setVersions(await listContractVersions(contractId));
      setStatus('saved');
    } catch (err) {
      setStatus('error');
      setError(errorMessage(err));
    }
  }

  async function saveVersion(): Promise<void> {
    await createContractVersion(contractId);
    setVersions(await listContractVersions(contractId));
  }

  /* --------------------------------------- AI akce nad smlouvou (NAP7 A17) */

  /**
   * Vsechny tri akce bezi JEN NA KLIKNUTI (nikdy pri otevreni editoru) —
   * model se neplati za to, ze nekdo otevrel smlouvu. Chyba se ukazuje
   * v pruhu chyb editoru, „AI neni dostupna" NENI chyba (prinasi ji vysledek).
   */
  async function runAiReview(): Promise<void> {
    setAiBusy('review');
    try {
      setAiReview(await reviewContractAgainstSources(contractId));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setAiBusy(null);
    }
  }

  async function runAiExplain(): Promise<void> {
    setAiBusy('explain');
    try {
      setAiExplanation(await explainContractToClient(contractId));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setAiBusy(null);
    }
  }

  async function runVersionDiff(versionNo: number): Promise<void> {
    setAiBusy('diff');
    try {
      setVersionDiff(await diffContractVersion(contractId, versionNo));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setAiBusy(null);
    }
  }

  /* ------------------------------------------- uprava textu (krok SML3) */

  /**
   * „Vrátit na mustr" (bod 2) — JEDNA akce pro prepsany, vlozeny i smazany
   * odstavec. Druh zmeny urcuje server; admin posila jen id odstavce.
   */
  async function revertToTemplate(blockId: string): Promise<void> {
    setStatus('saving');
    try {
      setContract(await revertBlockToTemplate(contractId, blockId));
      setVersions(await listContractVersions(contractId));
      setStatus('saved');
    } catch (err) {
      setStatus('error');
      setError(errorMessage(err));
    }
  }

  /** Odebrani odstavce z textu TETO smlouvy (mustr zustava beze zmeny). */
  async function removeBlock(blockId: string): Promise<void> {
    setStatus('saving');
    try {
      setContract(await removeContractBlock(contractId, blockId));
      setStatus('saved');
    } catch (err) {
      setStatus('error');
      setError(errorMessage(err));
    }
  }

  /**
   * „+ Vložit" u klauzule (bod 3): novy odstavec na MISTO KURZORU —
   * tedy za odstavec, ve kterem clovek prave stoji (`focusedBlockId`).
   * Cislovani clanku prepocita server (pozice bloku, ne cislo v textu).
   */
  /**
   * Odstavec, ve kterem stoji KURZOR v dokumentu.
   *
   * Bere se z CARETU (`window.getSelection`), ne z udalosti fokusu: klik do
   * odstavce v `contenteditable` casto zameri az samotny kontejner dokumentu,
   * takze `focusedBlockId` zustava prazdne — a „Vložit" by pak rikalo „klikni
   * nejdřív do odstavce" i cloveku, ktery v nem prave stoji (nalez ziveho
   * overeni SML3). Fokus zustava jako zaloha.
   */
  function cursorBlockId(): string | null {
    const root = previewRef.current;
    const selection = typeof window === 'undefined' ? null : window.getSelection();
    const node = selection?.anchorNode ?? null;
    if (root !== null && node !== null && root.contains(node)) {
      const element = node instanceof HTMLElement ? node : node.parentElement;
      const block = element?.closest<HTMLElement>('li[id], p[id], h1[id]') ?? null;
      // Preskrtnuty odstavec ve smlouve NESTOJI — vkladat za nej nelze.
      if (block !== null && block.getAttribute('data-smazano') !== '1') return block.id;
    }
    return focusedBlockId;
  }

  async function insertClause(clauseId: string): Promise<void> {
    if (contract === null) return;
    const target = articleOfBlock(contract.articles, cursorBlockId());
    if (target === null) {
      setError(t('webAdmin.contracts.text.insertNeedsCursor', { locale: LOCALE }));
      return;
    }
    setStatus('saving');
    try {
      const updated = await insertContractClause(contractId, {
        clauseId,
        articleId: target.articleId,
        ...(target.blockId === null ? {} : { afterBlockId: target.blockId }),
      });
      setContract(updated);
      setStatus('saved');
      setError(undefined);
    } catch (err) {
      setStatus('error');
      setError(errorMessage(err));
    }
  }

  /**
   * „Uložit jako nový mustr" (bod 1): z textu teto smlouvy vznikne NOVA VERZE
   * sablony kancelare. Stara verze zustava pro smlouvy, ktere z ni vznikly —
   * proto se tahle smlouva dal meri proti SVEMU mustru a seznam zmen
   * nezmizi.
   */
  async function saveAsTemplate(): Promise<void> {
    setStatus('saving');
    try {
      const tpl = await saveContractAsTemplate(contractId);
      setSavedTemplate(
        t('webAdmin.contracts.text.savedTemplate', {
          locale: LOCALE,
          values: { name: tpl.name, version: tpl.version },
        }),
      );
      setStatus('saved');
    } catch (err) {
      setStatus('error');
      setError(errorMessage(err));
    }
  }

  /** „Ukázat" u zmeny — posune dokument na odstavec a zvyrazni ho. */
  function showChangedBlock(blockId: string): void {
    const root = previewRef.current;
    const el = root?.querySelector<HTMLElement>(`[id="${CSS.escape(blockId)}"]`) ?? null;
    if (el === null) return;
    setFocusedBlockId(blockId);
    el.scrollIntoView({ block: 'center' });
  }

  /** Vlozi text na misto kurzoru v dokumentu („+ Vložit pole" z panelu nastroju). */
  function insertAtCursor(text: string): void {
    const root = previewRef.current;
    if (root === null) return;
    const selection = window.getSelection();
    const range = selection?.rangeCount === 1 ? selection.getRangeAt(0) : null;
    if (range === null || !root.contains(range.commonAncestorContainer)) {
      setError(t('webAdmin.contracts.text.insertNeedsCursor', { locale: LOCALE }));
      return;
    }
    range.deleteContents();
    range.insertNode(document.createTextNode(text));
    void saveEditedBlocks();
  }

  /**
   * Panel nastroju (bod 5). Tucne/kurziva/seznam dela prohlizec nad
   * `contenteditable` (`execCommand` je jedina cesta, kterou dnes prohlizece
   * k formatovani v contenteditable daji) — v jsdom neexistuje, proto guard.
   */
  function applyFormat(command: 'bold' | 'insertOrderedList'): void {
    previewRef.current?.focus();
    const exec = (document as Document & { execCommand?: (c: string) => boolean }).execCommand;
    if (typeof exec !== 'function') return;
    exec.call(document, command);
    void saveEditedBlocks();
  }

  async function doPrint(): Promise<void> {
    const win = openPrintWindow();
    const result = await printContract(contractId);
    if (win === null) return;
    writePrintWindow(win, result.html, result.documentCss, contract?.title ?? '');
  }

  /**
   * Zivy pruchod 13. 10. (V3): „Stáhnout PDF" dřív volalo tentyz `doPrint`
   * (tiskove okno) — ted dava SKUTECNY soubor ze serveru (Gotenberg, cesta
   * `/v1/contracts/:id/print.pdf`), presne ROZHODNUTI z briefu step-PRF2.
   */
  async function doDownloadPdf(): Promise<void> {
    // Dvojklik by poslal Gotenbergu druhou sazbu teze smlouvy; dokud prvni
    // bezi, tlacitko je zhasnute (`disabled`) a tenhle guard je druha pojistka.
    if (pdfState === 'preparing') return;
    setPdfState('preparing');
    try {
      await downloadContractPdf(contractId, `${documentFileName(contract?.title ?? '')}.pdf`);
      setPdfState('idle');
    } catch (err) {
      setPdfState('error');
      setError(errorMessage(err) ?? t('webAdmin.contracts.editor.pdfDownloadError', { locale: LOCALE }));
    }
  }

  async function sendToReview(): Promise<void> {
    setSendStatus('saving');
    setSendNote(null);
    try {
      // „Odeslat stranám ke kontrole" byl do UXP3 JEN prechod do `ready_to_send`
      // (ROZHODNUTI T3-3) — odkaz se neposilal a obrazovka presto hlasila
      // uspech. Ted vznikne pozvanka kazde strane, ktera ji nema a ma e-mail,
      // a vysledek se rekne tak, jak je (krok UXP3, P1-6).
      const current = contract;
      if (current === null) throw new Error('contract not loaded');
      const result = await transitionAndInviteParties({
        contractId,
        current,
        next: 'ready_to_send',
        invitations,
      });
      setContract(result.contract);
      setSendNote(result.note);
      if (result.created > 0) {
        await listClientInvitations({ contractId }).then(setInvitations, () => undefined);
      }
      // Pri neuspesnem prechodu nese pravdu POZNAMKA — obecne „Data se
      // nepodařilo načíst." by o akci zapisu lhalo (oprava e2e 2).
      setSendStatus(result.ok ? 'saved' : 'idle');
    } catch (err) {
      setSendStatus('error');
      setError(errorMessage(err));
    }
  }

  /**
   * Krok APP3 bod 4 (nalez A40) — ADRESA ZE SMLOUVY DO KARTY KLIENTA.
   *
   * Faktura bere adresu odberatele z adresare, makléřka ji ale zada casto
   * poprve az ve smlouve u strany — doklad pak vysel bez adresy. Jedno
   * kliknuti ji do adresare zapise; adresu, kterou adresar uz MA jinou,
   * server NEPREPISE a vrati rozpor. Prepsat jde teprve druhym, vyslovnym
   * kliknutim (`confirm: true`) — rozhodnuti o osobnim udaji klienta nesmi
   * padnout tise.
   */
  async function syncPartyAddresses(confirm: boolean): Promise<void> {
    setAddressSyncNote(null);
    try {
      const result = await syncContractPartyAddresses(contractId, confirm);
      setAddressSync(result);
      if (result.conflicts.length === 0) {
        setAddressSyncNote(
          result.written.length > 0
            ? t('webAdmin.contracts.editor.addressSyncWritten', {
                locale: LOCALE,
                values: { count: result.written.length },
              })
            : t('webAdmin.contracts.editor.addressSyncNothing', { locale: LOCALE }),
        );
      }
    } catch {
      setAddressSync(null);
      setAddressSyncNote(t('webAdmin.contracts.editor.addressSyncFailed', { locale: LOCALE }));
    }
  }

  const expandedBlocks = React.useMemo(
    () => expandedBlockIds(contract?.articles ?? []),
    [contract],
  );

  /**
   * Zvyrazneni pole, na kterem stoji kurzor ve formulari (a naopak).
   *
   * Zamerne BEZ seznamu zavislosti: obsah dokumentu dodava server jako HTML
   * (`dangerouslySetInnerHTML`), takze jeho uzly se pri prekresleni mohou
   * vyrobit znovu — a s nimi by zmizela trida nastavena jednorazove. Pruchod
   * par desitkami uzlu po kazdem renderu je levnejsi nez hledani, kdy presne
   * se uzly vymenily.
   */
  React.useEffect(() => {
    const root = previewRef.current;
    if (root === null) return;
    // Rozepsane hodnoty se vnucuji PRED zvyraznenim: React prepsal innerHTML
    // zpatky na serverovou podobu, takze jmeno, ktere clovek prave pise, by
    // v dokumentu zmizelo (krok UXS2 bod 1).
    applyLocalEdits(root);
    root.querySelectorAll('[data-f]').forEach((el) => {
      el.classList.toggle('reos-f--active', el.getAttribute('data-f') === activeField);
    });
    // Odpoved serveru uz dokument prekreslila i s novou hodnotou — rozepsane
    // hodnoty dal nema smysl vnucovat (jinak by prezily i pripadnou opravu
    // hodnoty na serveru). Pri dalsim znaku se zapisi znova.
    const html = contract?.renderedHtmlWithOrigins ?? null;
    if (lastRenderedHtml.current !== html) {
      lastRenderedHtml.current = html;
      localEdits.current.clear();
    }
  });

  /**
   * Krok UXD1 bod 5 — po pridani strany skoc na JEJI „Jméno a příjmení".
   *
   * Zamerne BEZ `pendingPartyFocus` jako jedine zavislosti: karta vznikne
   * teprve az odpoved serveru prepise `contract`, takze efekt musi zkusit
   * zamereni po KAZDEM renderu, dokud ovladaci prvek v DOM neni. Jakmile
   * uspeje, cil zapomene — jinak by skakal pri kazdem dalsim prekresleni.
   */
  React.useEffect(() => {
    if (pendingFocus === null) return;
    const root = fieldsRef.current;
    if (root === null) return;
    const testId = fieldControlTestId(pendingFocus.fieldKey, pendingFocus.index);
    if (root.querySelector<HTMLElement>(`[data-testid="${testId}"]`) === null) return;
    focusFieldControl(pendingFocus.fieldKey, pendingFocus.index);
    setPendingFocus(null);
  });

  /** Odstavec, ve kterem clovek pravě pise (kvuli „Vrátit na šablonu"). */
  function onDocumentFocusIn(event: React.FocusEvent<HTMLDivElement>): void {
    if (!editText) return;
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const block = target.closest<HTMLElement>('[id]');
    setFocusedBlockId(block === null || block === previewRef.current ? null : block.id);
  }

  /**
   * Krok SML3: zapamatuj si odstavec, ve kterem stoji KURZOR.
   *
   * Proc to nejde precist az pri kliknuti na „Vložit": kliknutim na tlacitko
   * prohlizec vyber PRESUNE na tlacitko, takze caret v dokumentu je v tu
   * chvili uz pryc. Pozice se proto bere pri kazdem kliknuti a psani
   * v dokumentu — a „Vložit" pak vklada tam, kde clovek naposled stal
   * (nalez ziveho overeni SML3: vkladani hlasilo „klikni nejdřív do
   * odstavce" i uprostred odstavce).
   */
  function cursorBlock(): HTMLElement | null {
    const root = previewRef.current;
    const selection = typeof window === 'undefined' ? null : window.getSelection();
    const node = selection?.anchorNode ?? null;
    if (root === null || node === null || !root.contains(node)) return null;
    const element = node instanceof HTMLElement ? node : node.parentElement;
    return element?.closest<HTMLElement>('li[id], p[id], h1[id]') ?? null;
  }

  function rememberCursorBlock(): void {
    if (!editText) return;
    const block = cursorBlock();
    if (block !== null && block.getAttribute('data-smazano') !== '1') setFocusedBlockId(block.id);
  }

  /**
   * Krok KLIKFIX1 (vada B4) — ODSTAVCE, DO KTERYCH CLOVEK OPRAVDU PSAL.
   *
   * Klikaci test 29. 10. prepsal v odstavci III.1 chybejici pole parcely
   * rucnim textem a „Uložit úpravy jako novou verzi" NEUDELALO NIC: zadny
   * pozadavek na server, zadna hlaska. Duvod je `saveEditedBlocks` nize —
   * uprava uvnitr pole (`data-t`) se pri prevodu zpatky na text sablony
   * nahradi `{{tokenem}}`, takze se zmena „neprojevi" a odstavec vypadne ze
   * seznamu zmenenych; totez dela odstavec vznikly ze seznamu. Obe cesty
   * mlcely. Abychom o nich mohli rict pravdu, musime vedet, do kterych
   * odstavcu clovek PSAL — jinak by se hlaska objevila u kazdeho ulozeni.
   */
  function rememberEditedBlock(): void {
    if (!editText) return;
    const block = cursorBlock();
    if (block !== null) editedBlocks.current.add(block.id);
  }

  /**
   * Ulozeni rucnich uprav z dokumentu (bod 4 zadani).
   *
   * Projde odstavce dokumentu, kazdy prevede zpatky na TEXT SABLONY
   * (`blockTemplateTextFrom` — pole zustavaji `{{tokeny}}`, tedy ziva) a ty
   * zmenene ulozi jako `manualOverride` + NOVOU VERZI smlouvy. Odstavce
   * vznikle rozmnozenim opakovaneho bloku (strany) se NEUKLADAJI: rucni text
   * by zmrazil pocet stran, ktery patri formulari vlevo.
   */
  async function saveEditedBlocks(): Promise<void> {
    const root = previewRef.current;
    // Tlacitko „Uložit úpravy" dokument zaroven ROZOSTRI, takze se obsluha
    // spusti dvakrat — a dve soubezna ulozeni si saham po TEMZE cisle verze
    // (`contract_versions.version_no`), coz skonci chybou 500. Zamek je tady,
    // ne u tlacitka: rozostreni je plnohodnotna cesta k ulozeni.
    if (root === null || contract === null || savingBlocks.current) return;
    const changed: { readonly id: string; readonly text: string }[] = [];
    // Krok KLIKFIX1 (vada B4): proc se uprava odstavce NEULOZILA. Jen pro
    // odstavce, do kterych clovek psal (`editedBlocks`) — u ostatnich je
    // „nic se nezmenilo" spravna odpoved a hlaska by lhala.
    const skipped = { list: false, field: false };
    root.querySelectorAll<HTMLElement>('li[id], p[id], h1[id]').forEach((el) => {
      if (expandedBlocks.has(el.id)) {
        if (editedBlocks.current.has(el.id)) skipped.list = true;
        return;
      }
      // Krok SML3: preskrtnuty odstavec je text MUSTRU, ne smlouvy — ulozit
      // ho zpatky jako rucni prepis by znamenalo vratit do smlouvy odstavec,
      // ktery z ni clovek odebral (a server by na nej stejne rekl 404).
      if (el.getAttribute('data-smazano') === '1') return;
      const next = blockTemplateTextFrom(el).trim();
      const current = (contract.manualOverrides[el.id] ?? templateTextOf(el.id)).trim();
      if (next !== '' && next !== current) {
        changed.push({ id: el.id, text: next });
        return;
      }
      // Psal do odstavce, a text sablony presto vyjde stejny: napsal to DO
      // pole, odkud to `blockTemplateTextFrom` vraci jako `{{token}}`.
      if (editedBlocks.current.has(el.id)) skipped.field = true;
    });
    setTextEditSkipped(skipped.list || skipped.field ? skipped : null);
    if (changed.length === 0) {
      editedBlocks.current.clear();
      return;
    }
    savingBlocks.current = true;
    setStatus('saving');
    try {
      let updated = contract;
      for (const block of changed) {
        updated = await setBlockOverride(contractId, block.id, block.text);
      }
      await createContractVersion(
        contractId,
        t('webAdmin.contracts.editor.versionNoteManual', { locale: LOCALE }),
      );
      setContract(updated);
      setVersions(await listContractVersions(contractId));
      setStatus('saved');
      editedBlocks.current.clear();
    } catch (err) {
      setStatus('error');
      setError(textSaveErrorMessage(err));
    } finally {
      savingBlocks.current = false;
    }
  }

  /**
   * Krok TOKYFIX1 (prekazka P6/2) — ROZHODNUTI VOLBY V TEXTU Z FORMULARE.
   *
   * Volba („Kauce se platí … má/nemá") zadne pole slovniku nema, takze ji do
   * tohoto kroku slo rozhodnout jen rucnim prepsanim odstavce v „Upravit
   * text" — mereni TOKY1 (14. 10. 2026) na tom zastavilo cely postup P6.
   *
   * Uklada se TOUTEZ cestou jako rucni uprava textu (`setBlockOverride`,
   * tedy audit i zneplatneni podpisu na serveru); novy text sklada SERVER
   * (`blockTextTemplate` + `placeholder`), admin v textu nic nehleda.
   *
   * ROZHODNUTI TOKYFIX1-1: novou VERZI smlouvy tohle neuklada — verze patri
   * k „Uložit úpravy" v rezimu textu, kde clovek odstavec skutecne prepisuje.
   */
  async function resolveTextChoice(choice: UnresolvedTextChoiceDto, value: string): Promise<void> {
    const trimmed = value.trim();
    if (trimmed === '') return;
    setStatus('saving');
    try {
      setContract(
        await setBlockOverride(
          contractId,
          choice.blockId,
          choice.blockTextTemplate.replace(choice.placeholder, trimmed),
        ),
      );
      setStatus('saved');
    } catch (err) {
      setStatus('error');
      setError(textSaveErrorMessage(err));
    }
  }

  /**
   * Zameri pole formulare podle klice slovniku (+ poradi strany).
   *
   * Pouziva to KLIK V DOKUMENTU (krok UXS1) i klik na polozku panelu kontrol
   * (krok UXS2 bod 2) — jedna cesta, aby „skok na pole" znamenal na obou
   * mistech totez, vcetne rozbaleni sbaleneho oddilu.
   */
  function focusFieldControl(fieldKey: string, index: string | null): void {
    setActiveField(fieldKey);
    setActiveIndex(index);
    const root = fieldsRef.current;
    if (root === null) return;
    const testId = fieldControlTestId(fieldKey, index);
    const found = root.querySelector<HTMLElement>(`[data-testid="${testId}"]`);
    if (found === null) return;
    found.closest('details')?.setAttribute('open', '');
    const control =
      found instanceof HTMLInputElement ||
      found instanceof HTMLSelectElement ||
      found instanceof HTMLTextAreaElement
        ? found
        : found.querySelector<HTMLElement>('input, select, textarea');
    const focusTarget = control ?? found;
    focusTarget.focus();
    focusTarget.scrollIntoView({ block: 'center' });
  }

  /**
   * Vyber pole z OSNOVY nebo z „Další chybějící" (krok SML2 bod 2 a 5):
   * pole se vybere v panelu vpravo a dokument se na nej posune. Zamereni
   * ovladaciho prvku zustava tataz cesta jako u kliku v dokumentu
   * (`focusFieldControl`), aby „skok na pole" znamenal vsude totez.
   */
  function selectField(rawKey: string, index: string | null = null): void {
    // Seznam role (`pronajimatel[]`) sam vstup nema — vybere se jmeno prvni
    // strany te role. TATAZ funkce, jakou pouziva radek kontrol.
    const fieldKey = selectableFieldOf(rawKey);
    const partyIndex = fieldKey === rawKey ? index : (index ?? '0');
    focusFieldControl(fieldKey, partyIndex);
    const root = previewRef.current;
    if (root === null) return;
    const selector =
      partyIndex === null
        ? `[data-f="${CSS.escape(fieldKey)}"]`
        : `[data-f="${CSS.escape(fieldKey)}"][data-i="${CSS.escape(partyIndex)}"]`;
    root.querySelector<HTMLElement>(selector)?.scrollIntoView({ block: 'center' });
  }

  /** Prvni chybejici pole clanku — klik na clanek v osnove (predloha). */
  function selectArticle(article: ContractFillPlanArticleDto): void {
    const first = article.missingKeys[0];
    if (first !== undefined) {
      selectField(first);
      return;
    }
    // Clanek bez chybejiciho pole: jen posun dokumentu na jeho nadpis.
    previewRef.current
      ?.querySelector<HTMLElement>(`[id="${CSS.escape(article.id)}"]`)
      ?.scrollIntoView({ block: 'center' });
  }

  /** Klik na zvyraznene pole v dokumentu zameri odpovidajici pole formulare. */
  function onDocumentClick(event: React.MouseEvent<HTMLDivElement>): void {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const marked = target.closest<HTMLElement>('[data-f]');
    if (marked === null) return;
    const key = marked.getAttribute('data-f');
    if (key === null) return;
    // Krok UXD1 bod 3: v rezimu „Upravit text" je pole `contenteditable="false"`,
    // takze klik do nej nic nedelal a vypadalo to jako rozbity dokument. Ted
    // o sobe rekne, kde se meni — a nabidne prepnuti na sve pole.
    if (editText) {
      setLockedFieldHint({ fieldKey: key, index: marked.getAttribute('data-i') });
      return;
    }
    focusFieldControl(key, marked.getAttribute('data-i'));
  }

  /**
   * LOKALNI nahled zmeny pole v dokumentu (krok UXS2 bod 1: „do 1 s").
   *
   * Dokument je HTML ze SERVERU (`dangerouslySetInnerHTML`), takze nova
   * hodnota se v nem objevi teprve po PATCHi a jeho odpovedi — to je 450 ms
   * utlumu psani plus cely okruh po siti. Nez odpoved prijde, prepiseme
   * odpovidajici znacky PRIMO.
   *
   * Zapis se MUSI drzet v `localEdits` a vnucovat po kazdem renderu: React
   * nastavuje `dangerouslySetInnerHTML` pokazde, takze prvni prekresleni
   * obrazovky (treba jen zmena stavu ukladani) by prepsany text zahodilo.
   * Je to tentyz duvod, proc se po kazdem renderu znovu nastavuji i tridy
   * zvyrazneni pole — viz efekt vyse.
   */
  function previewFieldInDocument(fieldKey: string, index: number | null, value: string): void {
    const root = previewRef.current;
    if (root === null) return;
    localEdits.current.set(`${fieldKey}#${index === null ? '*' : String(index)}`, value);
    applyLocalEdits(root);
  }

  function applyLocalEdits(root: HTMLElement): void {
    if (localEdits.current.size === 0) return;
    localEdits.current.forEach((value, mapKey) => {
      const hash = mapKey.lastIndexOf('#');
      const fieldKey = mapKey.slice(0, hash);
      const index = mapKey.slice(hash + 1);
      const empty = value.trim() === '';
      root.querySelectorAll<HTMLElement>('[data-f]').forEach((el) => {
        if (el.getAttribute('data-f') !== fieldKey) return;
        const elIndex = el.getAttribute('data-i');
        if (index !== '*' && elIndex !== null && elIndex !== index) return;
        // Text prazdneho pole (`[chybí]` / `[doplní se později]`) jsou DATA
        // DOKUMENTU (`originPlaceholder` v @reos/documents), ne text admina —
        // proto se schova ze serveroveho vykresleni a vrati se, kdyz clovek
        // hodnotu smaze (CLAUDE.md §1 bod 5: zadny hardcoded text v UI).
        if (el.dataset['ph'] === undefined && el.classList.contains('reos-f--chybi')) {
          el.dataset['ph'] = el.textContent ?? '';
        }
        /*
         * Krok NOC4 bod 4 (nalez N23): hodnota se do dokumentu sazi TAK, JAK
         * ji vysazi server — datum cesky (`2026-10-14` → „14. 10. 2026"),
         * jinak beze zmeny. Prevod je sdileny (`formatCzechDate`
         * z `@reos/i18n`, tentyz, jaky pouziva `czechDateText` v enginu
         * smlouvy), takze zivy nahled a PDF se nemohou rozejit. `localEdits`
         * se po odpovedi serveru nemazou, takze ISO datum tu zustavalo
         * natrvalo, dokud makler stranku nenacetl znovu.
         */
        el.textContent = empty ? (el.dataset['ph'] ?? '') : documentValueText(value);
        // Barva: vyplnene pole uz neni „chybí". Presny puvod (klient/katastr)
        // dorovna odpoved serveru — ta prijde v radu stovek ms.
        el.classList.toggle('reos-f--chybi', empty);
        el.classList.toggle('reos-f--makler', !empty);
      });
    });
  }

  /*
   * SMLOUVA SE NENACETLA — stav MA SVOU ZNACKU (krok QAFIX7).
   *
   * Konvence adminu pro pad JEDNE obrazovky je testid `*-load-error`
   * (`property-load-error`, `case-load-error`, …; sada ji cte selektorem
   * `CHYBOVA_OBRAZOVKA` v `test/e2e-preview/helpers.ts`). Tady znacka
   * CHYBELA, takze smazana nebo cizi smlouva vypadala na obrazovce i ve
   * stope jako PRAZDNO: mereni P6/desktop 21. 10. 2026 6:01:59 dostalo na
   * `GET /v1/contracts/<id>` 404 (koncept mezitim smazal uklid souběžného
   * behu sady), obrazovka ukazala jen vetu bez znacky a pruchod cekal 60 s,
   * aby nahlasil „element(s) not found" — o duvodu ani slovo.
   */
  if (error !== undefined && contract === null) {
    return (
      <p role="alert" data-testid="contract-load-error">
        {error.trim() === '' ? t('webAdmin.contracts.editor.loadError', { locale: LOCALE }) : error}
      </p>
    );
  }
  if (contract === null) return <p>{t('webAdmin.contracts.loading', { locale: LOCALE })}</p>;

  // Opakovane bloky stran (`najemce[]`) a jejich pod-pole maji vlastni oddil
  // „Smluvni strany" podle mockupu — v obecnem seznamu poli by byly podruhe
  // a jako jeden textovy vstup pro cely blok (zivy nalez T4).
  // Krok UXS2 bod 4: `najem.predmet-typ` ma vlastni oddil „Předmět smlouvy"
  // s vyberem; v obecnem seznamu poli byl podruhe a jako textovy vstup, takze
  // slo tentyz udaj zadat dvema zpusoby.
  /*
   * Krok EDIT2 bod 3 (nalez B3) — POVINNE POLE MA VZDY RADEK VE „Vyplnit".
   *
   * Do EDIT2 se formular skladal z `unfilled` (pole, ktera sablona POUZIVA)
   * a z vyplnenych hodnot. Povinne pole slovniku, ktere v textu vzoru
   * nestoji, tedy ovladaci prvek NEMELO — a `GET /v1/contracts/:id` ho
   * pritom hlasilo jako „Chybí povinné pole". U vzoru MV to bylo 7 poli
   * a smlouva nemohla NIKDY dosahnout `complete: true` (zivy pruchod EDIT1).
   *
   * Pravidlo je ted jedno: co server hlasi jako povinne, to formular nabizi
   * (`contract-field-row-<klic>`), nebo je to pole odsunute na „doplnit
   * později" (`deferPhase`, pak podpis neblokuje).
   *
   * VYJIMKA: pole POLOZKY opakovaneho bloku (`nemovitost.parcely[].cislo`).
   * Ta se zadavaji ve svem bloku a vlastni radek by byl DRUHA cesta k teze
   * hodnote — tentyz duvod, proc tu neni `najem.predmet-typ` ani pole stran
   * (UXS2 bod 4). Blok sam v seznamu je, kdyz je prazdny.
   */
  /**
   * Krok SMLQA1: opakovane bloky, ktere NEJSOU strana (u zprostredkovatelske
   * smlouvy `nemovitost.parcely[]`). Polozkova pole se berou ze SLOVNIKU,
   * takze detail smlouvy nemusi posilat `repeatItemTemplate` vzoru.
   */
  const repeatBlockKeys = nonPartyRepeatBlockKeys(contract, catalogs?.fields);
  const fieldKeys = [
    ...new Set([
      ...contract.unfilled,
      ...Object.keys(contract.fieldValues),
      ...(contract.requiredKeys ?? []).filter((key) => !key.includes('[].')),
    ]),
  ]
    .filter((key) => !isPartyFieldKey(key) && key !== SUBJECT_TYPE_FIELD_KEY)
    /*
     * Krok SMLQA1 — OPAKOVANY BLOK MA VLASTNI OVLADAC, NE TEXTOVY RADEK.
     *
     * `nemovitost.parcely[]` je ve `unfilled` jako kazde jine prazdne pole,
     * takze tu vznikal OBYCEJNY textovy vstup. Co do nej clovek napsal, se
     * ulozilo jako skalar, engine v nem polozky nenasel a dokument dal sazel
     * mezeru — smlouvu neslo poslat k podpisu (zivy pruchod 29. 10. 2026).
     * Blok ma od SMLQA1 svuj oddil niz; dva ovladace na jednu hodnotu by
     * znamenaly, ze jeden z nich tise prepisuje druhy.
     */
    .filter((key) => !repeatBlockKeys.includes(key));
  const subjectValue =
    contract.customSubject !== null
      ? `custom:${customSubjects.find((c) => c.key === contract.customSubject?.key)?.id ?? ''}`
      : (contract.subjectType ?? '');
  const partyRoles = partyRolesFor(contract.contractType, contract);
  const parties = partiesOf(contract.fieldValues, partyRoles);
  const checks = orderedCheckLines(checkLines(contract, (key) => fieldLabel(catalogs, key, templateFields)));
  const nonFlat = catalogs?.subjectTypes.find((s) => s.key === contract.subjectType)?.nebyt === true;
  // Osnova a rozpad chybejicich poli podle zdroje POCITA SERVER (`fillPlan`)
  // — editor si zdroj pole nedopocitava (zadani SML2 bod 2).
  //
  // Zaloha pro STARSI ODPOVED SERVERU (detail z cache, nebo API o nasazeni
  // pozadu za adminem): prazdna osnova. Tentyz duvod jako `detail.fieldOrigins
  // ?? {}` v `contractCounts` od kroku T4 — chybejici nove pole nesmi shodit
  // celou obrazovku editoru.
  const plan = contract.fillPlan ?? EMPTY_FILL_PLAN;
  /**
   * Krok APP3 bod 1 (nalez A31): stav v hlavicce a jeho rozpor s osnovou
   * jednou funkci — tutez pouziva obrazovka „Odeslat k podpisu".
   */
  const editorHeaderState = contractHeaderState({
    status: contract.status,
    missing: plan.missing,
  });
  /**
   * Poradi chybejicich poli pro „Předchozí / Další chybějící" — uz PREVEDENE
   * na pole, ktera jde vybrat (`selectableFieldOf`). Bez prevodu by seznam
   * nesl HOLY klic role (`pronajimatel[]`), kdezto vybrane pole by bylo
   * `pronajimatel[].jmeno` — `stepMissingField` by se v seznamu nenasel
   * a „Další chybějící" by se porad vracelo na prvni pole (zive overeni SML2).
   */
  const missingOrder = missingFieldOrder(plan).map(selectableFieldOf);
  const requiredKeys = new Set(contract.requiredKeys ?? []);
  const selectedDef = activeField === null ? undefined : catalogs?.fields?.find((f) => f.key === activeField);
  const selectedSuggestion =
    activeField === null ? null : fieldSuggestion(activeField, propertyFacts);
  const selectedUsage =
    activeField === null ? [] : fieldUsageRomans(contract.articles, plan, activeField);
  const selectedArticle =
    activeField === null
      ? undefined
      : plan.articles.find((a) => a.missingKeys.includes(activeField));
  /** Hodnota vybraneho pole — u pole strany z jejiho seznamu, jinak ze slovniku. */
  const activeIndexNumber = activeIndex === null || activeIndex === '' ? null : Number(activeIndex);
  const selectedParty =
    activeField === null || activeIndexNumber === null
      ? undefined
      : parties.find(
          (party) =>
            PARTY_FIELD_PREFIX[party.role] === activeField.replace(/\.[^.]+$/, '') &&
            party.index === activeIndexNumber,
        );
  const selectedPartyField = activeField === null ? undefined : activeField.split('.').at(-1);
  const selectedFieldValue =
    selectedParty !== undefined && selectedPartyField !== undefined
      ? selectedParty.values[selectedPartyField as PartyFieldKey]
      : activeField === null
        ? undefined
        : contract.fieldValues[activeField]?.value;
  /** Kdo pole doplni — ROZHODL SERVER (`fillPlan.fillerByKey`), ne UI. */
  const fillerOfField = (key: string): FieldFiller => plan.fillerByKey[key] ?? 'makler';
  /** Clanek, ze ktereho vznikne predavaci protokol (predloha: „z čl. VII"). */
  const handoverRoman =
    plan.articles.find((a) => /před[áa]n/i.test(a.title))?.roman ?? null;
  const penbClass =
    typeof contract.fieldValues['nemovitost.penb-trida']?.value === 'string' &&
    contract.fieldValues['nemovitost.penb-trida'].value !== ''
      ? String(contract.fieldValues['nemovitost.penb-trida'].value)
      : null;
  /**
   * Krok UXD3b (nalez SML6): MA TAHLE SMLOUVA PENB JAKO PRILOHU?
   *
   * Dva pripady, oba z odpovedi SERVERU — UI zadny vlastni seznam typu smluv
   * nema (jinak by se s `PENB_REQUIRED_FOR` v `checks.ts` rozesel):
   * (a) server hlasi chybejici prilohu PENB (= u tohohle typu smlouvy je
   *     povinna, tedy najem bytu) → radek nabizi „Vyžádat od majitele";
   * (b) trida PENB je znama → radek ji vypise u jakekoli smlouvy, protoze je
   *     to udaj, ktery smlouva o nemovitosti opravdu nese.
   * U zprostredkovatelske smlouvy o prodeji bez znameho prukazu tedy radek
   * nesviti vubec.
   */
  const penbRelevant =
    penbClass !== null || contract.checks.some((c) => c.code === 'chybi-penb-priloha');

  /**
   * Zapis hodnoty VYBRANEHO pole z panelu vpravo. Jde TOUTEZ cestou jako
   * formular (`patch` / `partyFieldPatch`), takze obe mista ukladaji stejne
   * — panel vpravo neni druha pravda o hodnote, jen druhy vstup do te same.
   */
  const commitSelectedField = (next: unknown): void => {
    if (activeField === null) return;
    if (selectedParty !== undefined && selectedPartyField !== undefined) {
      void onPartyFieldChange(
        selectedParty.role,
        selectedParty.index,
        selectedPartyField as PartyFieldKey,
        String(next ?? ''),
      );
      return;
    }
    const source = brokerEditSource();
    setContract({
      ...contract,
      fieldValues: { ...contract.fieldValues, [activeField]: { value: next, source } },
    });
    void patch({ fieldValues: { [activeField]: { value: next, source } } });
  };

  /** Karta jedne strany — vykresli se v grupe podle role (krok UXD1 bod 5). */
  const renderPartyEditor = (p: Party): React.JSX.Element => (
    <PartyEditor
      key={`${p.role}-${p.index}`}
      party={p}
      role={p.role}
      onSendLink={() => setTab('client')}
      onFieldChange={(role, index, field, value) => {
        void onPartyFieldChange(role, index, field, value);
      }}
      onFieldPreview={(role, index, field, value) =>
        previewFieldInDocument(`${PARTY_FIELD_PREFIX[role]}.${field}`, index, value)
      }
      onFieldFocus={(role, field) => {
        // Zivy nalez 12. 10. (R.B.): fokus na strane 2 zapisoval do strany 0,
        // protoze `activeIndex` zustaval z posledniho kliku v dokumentu.
        setActiveField(`${PARTY_FIELD_PREFIX[role]}.${field}`);
        setActiveIndex(String(p.index));
      }}
      // Prvni strana role se odebrat nesmi (mockup: „nelze odebrat").
      boldOf={boldOf}
      onToggleBold={onToggleBoldField}
      {...(p.index > 0
        ? {
            onRemove: (role: PartyRole, index: number) => {
              void onRemoveParty(role, index);
            },
          }
        : {})}
    />
  );

  /**
   * Krok TOKYFIX1 (prekazka P6/2): nevyresene volby v textu ze SERVERU
   * (`signing.choices`) — tataz rada vzoru, jakou hlasi brana podpisu.
   */
  const textChoices = contract.signing?.choices ?? [];

  const formPanel = (
    <div ref={fieldsRef} data-testid="contract-editor-fields">
      {/* Mockup: „Smluvni strany" — Pronajimatel 1 / Nájemce 1, nelze
          odebrat posledniho, + dalsi, Poslat odkaz. */}
      <EditorSection
        title={t('webAdmin.contracts.editor.partiesHeading', { locale: LOCALE })}
        testId="contract-section-parties"
      >
        {/*
          Krok UXD1 bod 5 (R.B. 12. 10. 21:38: „není tam + pronajímatel, ale
          pak až skočí další pronajímatel"): strany jsou SESKUPENE PO ROLICH
          a tlacitko „+ Další pronajímatel" stoji HNED POD POSLEDNIM
          pronajimatelem. Do UXD1 stala vsechna tlacitka az pod vsemi stranami
          (tedy pod najemcem) a nova strana se pak objevila nekde vys, nez kam
          clovek klikl.
        */}
        {partyRoles.map((role) => (
          <div className="reos-party-group" key={role} data-testid={`contract-party-group-${role}`}>
            {parties.filter((p) => p.role === role).map((p) => renderPartyEditor(p))}
            <div className="reos-party__add">
              <button
                type="button"
                className="rh-btn"
                onClick={() => void onAddParty(role)}
                data-testid={`contract-party-add-${role}`}
              >
                {t('webAdmin.contracts.editor.addPartyNext', {
                  locale: LOCALE,
                  values: {
                    // Role je v katalogu s velkym pismenem („Pronajímatel") —
                    // uvnitr vety patri mala. Cestina i anglictina maji nazvy
                    // roli jako obecna jmena, takze staci sklonovani locale.
                    role: t(PARTY_ROLE_LABEL_KEY[role], { locale: LOCALE }).toLocaleLowerCase(
                      LOCALE,
                    ),
                  },
                })}
              </button>
            </div>
          </div>
        ))}
      </EditorSection>

      {/* Mockup: „Predmet smlouvy" — byt/dum/garaz/sklad/prodejna + vlastni. */}
      <EditorSection
        title={t('webAdmin.contracts.editor.subjectHeading', { locale: LOCALE })}
        testId="contract-section-subject"
      >
        <div className="reos-field-row">
          {/* Krok UXD3 bod 4: i predmet smlouvy ma svuj prepinac tucneho —
              skloneny predmet je v dokumentu odvozeny token, takze „B" tu
              plati na pole `najem.predmet-typ` tam, kde stoji jako hodnota. */}
          <span className="reos-field-row__head">
            <BoldFieldToggle
              testId={`contract-bold-${SUBJECT_TYPE_FIELD_KEY}`}
              on={boldOf(SUBJECT_TYPE_FIELD_KEY)}
              onToggle={(next) => onToggleBoldField(SUBJECT_TYPE_FIELD_KEY, next)}
            />
            <label className="reos-field-row__label" htmlFor="contract-subject-select">
              {t('webAdmin.contracts.editor.subjectLabel', { locale: LOCALE })}
            </label>
          </span>
          <span className="reos-field-row__input">
            <select
              id="contract-subject-select"
              value={subjectValue}
              onChange={(e) => void onSubjectChange(e.target.value)}
              data-testid="contract-subject-select"
            >
              <option value="">—</option>
              {(catalogs?.subjectTypes ?? []).map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
              {customSubjects.map((c) => (
                <option key={c.id} value={`custom:${c.id}`}>
                  {c.forms.label}
                </option>
              ))}
            </select>
          </span>
        </div>
        <p className="reos-template-card__meta mt-1" data-testid="contract-subject-hint">
          {t('webAdmin.contracts.editor.subjectHint', { locale: LOCALE })}
        </p>
        {nonFlat && (
          <p className="reos-disclaimer" data-testid="contract-subject-warning">
            {t('webAdmin.contracts.editor.subjectNonFlatWarning', { locale: LOCALE })}
          </p>
        )}
      </EditorSection>

      {/*
        Krok TOKYFIX1 (prekazka P6/2) — VOLBY V TEXTU JAKO POLE FORMULARE.
        Co je volba, rozhoduje SERVER (`signing.choices`); formular ji jen
        nabidne u jejiho clanku. Oddil je videt jen tehdy, kdyz nejaka
        nevyresena volba je — jinak by v kazde smlouve stal prazdny.
      */}
      {textChoices.length > 0 && (
        <EditorSection
          title={t('webAdmin.contracts.editor.choicesHeading', { locale: LOCALE })}
          testId="contract-section-choices"
        >
          <p className="reos-field-row__hint" data-testid="contract-choices-hint">
            {t('webAdmin.contracts.editor.choicesHint', { locale: LOCALE })}
          </p>
          {textChoices.map((choice) => {
            const key = `volba-${choice.blockId}-${String(choice.occurrence)}`;
            const controlId = `contract-choice-${key}`;
            return (
              <div className="reos-field-row" key={key} data-testid={`contract-field-row-${key}`}>
                <span className="reos-field-row__head">
                  <label className="reos-field-row__label" htmlFor={controlId}>
                    {t('webAdmin.contracts.editor.choiceLabel', {
                      locale: LOCALE,
                      values: { article: articleSectionTitle(choice.articleTitle), choice: choice.raw },
                    })}
                  </label>
                </span>
                <span className="reos-field-row__input">
                  {choice.kind === 'volba' && (
                    <select
                      id={controlId}
                      defaultValue=""
                      onChange={(e) => void resolveTextChoice(choice, e.target.value)}
                      data-testid={`contract-choice-${key}`}
                    >
                      <option value="">
                        {t('webAdmin.contracts.editor.choicePick', { locale: LOCALE })}
                      </option>
                      {choice.options.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  )}
                  {choice.kind === 'pocet' && (
                    <input
                      id={controlId}
                      type="number"
                      min={1}
                      // Uklada se az po odchodu z pole — jinak by kazda
                      // stisknuta cislice byla jednou verzi textu.
                      onBlur={(e) =>
                        void resolveTextChoice(
                          choice,
                          e.target.value.trim() === ''
                            ? ''
                            : `${e.target.value.trim()} ${choice.unit}`,
                        )
                      }
                      data-testid={`contract-choice-${key}`}
                    />
                  )}
                  {choice.kind === 'text' && (
                    <button
                      type="button"
                      className="rh-btn"
                      onClick={() => {
                        setEditText(true);
                        setFocusedBlockId(choice.blockId);
                      }}
                      data-testid={`contract-choice-${key}`}
                    >
                      {t('webAdmin.contracts.editor.choiceInText', { locale: LOCALE })}
                    </button>
                  )}
                </span>
                <span className="reos-field-row__hint" data-testid={`contract-choice-context-${key}`}>
                  {choice.blockText}
                </span>
              </div>
            );
          })}
        </EditorSection>
      )}

      {/*
        Krok SMLQA1 — OPAKOVANE POLOZKY („+ přidat").
        Zadani: „vyplnit KAŽDÉ pole včetně … opakovaných položek („+ přidat")".
        Strany maji vlastni panel vys; tohle je zbytek opakovanych bloku vzoru
        (u zprostredkovatelske smlouvy parcely), ktery do SMLQA1 zadny ovladac
        nemel. Oddil je videt jen tehdy, kdyz smlouva takovy blok ma.
      */}
      {repeatBlockKeys.map((listKey) => {
        const itemFields = repeatItemFieldKeys(catalogs?.fields, listKey);
        const rows = repeatRowCount(contract.fieldValues, listKey);
        const items = repeatItemsOf(contract.fieldValues, listKey);
        const commit = (patchInput: ReturnType<typeof addRepeatItemPatch> | null): void => {
          if (patchInput === null) return;
          setContract({ ...contract, fieldValues: { ...contract.fieldValues, ...patchInput } });
          void patch({ fieldValues: patchInput });
        };
        return (
          <EditorSection
            key={listKey}
            title={fieldLabel(catalogs, listKey, templateFields)}
            testId={`contract-section-repeat-${listKey}`}
          >
            {Array.from({ length: rows }, (_v, index) => (
              <div
                className="reos-party"
                key={`${listKey}-${String(index)}`}
                data-testid={`contract-repeat-item-${listKey}-${String(index)}`}
              >
                <div className="reos-party__head">
                  <strong>
                    {t('webAdmin.contracts.editor.repeatItemHeading', {
                      locale: LOCALE,
                      values: {
                        label: fieldLabel(catalogs, listKey, templateFields),
                        index: index + 1,
                      },
                    })}
                  </strong>
                  {/* Prvni polozka se odebrat nesmi — blok bez polozky by zase
                      nemel kam psat (tentyz duvod jako u prvni strany). */}
                  {index > 0 && (
                    <button
                      type="button"
                      className="rh-btn rh-btn--plain"
                      onClick={() =>
                        commit(removeRepeatItemPatch(contract.fieldValues, listKey, index))
                      }
                      data-testid={`contract-repeat-remove-${listKey}-${String(index)}`}
                    >
                      {t('webAdmin.contracts.editor.repeatItemRemove', { locale: LOCALE })}
                    </button>
                  )}
                </div>
                {itemFields.map((field) => {
                  const def = catalogs?.fields?.find((f) => f.key === `${listKey}.${field}`);
                  const controlId = `contract-repeat-${listKey}-${String(index)}-${field}`;
                  return (
                    <div className="reos-field-row" key={field}>
                      <span className="reos-field-row__head">
                        <label className="reos-field-row__label" htmlFor={controlId}>
                          {fieldLabel(catalogs, `${listKey}.${field}`, templateFields)}
                        </label>
                      </span>
                      <span className="reos-field-row__input">
                        <ContractFieldControl
                          def={def}
                          value={items[index]?.[field] ?? ''}
                          onCommit={(next) =>
                            commit(
                              repeatItemFieldPatch(
                                contract.fieldValues,
                                listKey,
                                index,
                                field,
                                String(next ?? ''),
                              ),
                            )
                          }
                          controlId={controlId}
                          labelledBy={controlId}
                          testId={controlId}
                        />
                      </span>
                    </div>
                  );
                })}
              </div>
            ))}
            <div className="reos-party__add">
              <button
                type="button"
                className="rh-btn"
                onClick={() => commit(addRepeatItemPatch(contract.fieldValues, listKey))}
                data-testid={`contract-repeat-add-${listKey}`}
              >
                {t('webAdmin.contracts.editor.repeatItemAdd', {
                  locale: LOCALE,
                  values: { label: fieldLabel(catalogs, listKey, templateFields).toLocaleLowerCase(LOCALE) },
                })}
              </button>
            </div>
          </EditorSection>
        );
      })}

      {/* Zbyle smluvni polozky podle slovniku, sdružene do ODDILU
          podle clanku smlouvy, ve kterem se pole pouziva (mockup D12),
          s popiskem ze slovniku a stitkem puvodu. */}
      {groupFieldsByArticle(contract.articles, fieldKeys).map((group) => (
        <EditorSection
          key={group.articleId ?? '__rest'}
          title={
            articleSectionTitle(group.title) === ''
              ? t('webAdmin.contracts.editor.fieldsHeading', { locale: LOCALE })
              : articleSectionTitle(group.title)
          }
          testId={`contract-group-${group.articleId ?? 'rest'}`}
        >
          {group.fields.map((key) => {
            const origin = contract.fieldOrigins[key] ?? 'makler';
            const def = catalogs?.fields?.find((f) => f.key === key);
            // Krok UXS2 bod 4: popisek je svazany se vstupem. Jeden vstup
            // unese `<label for>`, skupina voleb jen `aria-labelledby`.
            const controlId = `contract-field-${key}`;
            const labelId = `contract-field-label-${key}`;
            const single = fieldControlKind(def) === 'input';
            const LabelTag = single ? 'label' : 'span';
            return (
              <div
                className={`reos-field-row${activeField === key ? ' reos-field-row--active' : ''}`}
                key={key}
                data-testid={`contract-field-row-${key}`}
              >
                {/* Krok UXD3 bod 4: prepinac tucneho vlevo u radku — v JEDNE
                    rade s popiskem, aby i v uzkem levem sloupci (kde se radek
                    sazi pod sebe) stal „B" VEDLE nazvu pole, ne nad nim. */}
                <span className="reos-field-row__head">
                  <BoldFieldToggle
                    testId={`contract-bold-${key}`}
                    on={boldOf(key)}
                    onToggle={(next) => onToggleBoldField(key, next)}
                  />
                  <LabelTag
                    className="reos-field-row__label"
                    id={labelId}
                    {...(single ? { htmlFor: controlId } : {})}
                  >
                    {fieldLabel(catalogs, key, templateFields)} <OriginTag origin={origin} />
                  </LabelTag>
                </span>
                <span
                  className="reos-field-row__input"
                  /*
                   * Krok SML2 bod 4 a 5: vybrane pole je VYBER, ne kurzor.
                   * Do SML2 se tu na `onBlur` nastavovalo `null`, protoze
                   * zvyrazneni v dokumentu melo sledovat kurzor. Jakmile ale
                   * vybrane pole zije i v panelu VPRAVO, prvni kliknuti do
                   * jeho vstupu odrostri tenhle a vyber by zmizel presne
                   * v okamziku, kdy clovek zacne psat (zive overeni SML2 to
                   * naslo jako timeout na `contract-selected-field`).
                   * Vyber proto trva, dokud clovek nevybere jine pole.
                   */
                  onFocus={() => setActiveField(key)}
                >
                  {/* Krok T5 (bod 12): prvek podle typu pole ve slovniku —
                      prepinac, zaskrtavatka, datum, cislo, seznam. */}
                  <ContractFieldControl
                    def={def}
                    value={contract.fieldValues[key]?.value}
                    controlId={controlId}
                    labelledBy={labelId}
                    onPreview={(next) => previewFieldInDocument(key, null, next)}
                    onCommit={(next) => {
                      const before = contract.fieldValues[key];
                      const source = brokerEditSource();
                      // Krok P8F5 bod 3: pamet prevzeti („z ceho hodnota
                      // vznikla") drzi i mistni stav, aby hlaska o rozporu
                      // se zakazkou nezmizela a nevratila se, nez dojde
                      // odpoved serveru. Pravdu o ni stejne rika SERVER
                      // (`mergeFactMemory`), tohle je jen plynulost.
                      const memory = before?.fromFact === undefined ? {} : { fromFact: before.fromFact };
                      // Prepinac se musi prepnout HNED. Bez teto radky
                      // by zustal ve stare poloze, dokud se nevrati PATCH
                      // s novym renderem — a klik by vypadal, ze nic
                      // neudelal. Server pak stav stejne prepise.
                      setContract({
                        ...contract,
                        fieldValues: {
                          ...contract.fieldValues,
                          [key]: { value: next, source, ...memory },
                        },
                      });
                      void patch({ fieldValues: { [key]: { value: next, source } } });
                    }}
                    testId={`contract-field-${key}`}
                  />
                  {/* Mockup: u kauce je vedle castky nemenitelne pole „Slovy". */}
                  {key === 'najem.kauce' && (
                    <span className="reos-field-row__hint" data-testid="contract-field-kauce-slovy">
                      {t('webAdmin.contracts.editor.amountInWords', { locale: LOCALE })}
                    </span>
                  )}
                  {/* Krok P8F5 body 2 a 3: zmena v zakazce / rozpor se zakazkou. */}
                  <FactChangeNotice
                    change={factChanges.find((c) => c.fieldKey === key) ?? null}
                    fieldName={fieldLabel(catalogs, key, templateFields)}
                    saveState={savedToCase[key] ?? null}
                    onTake={() => void takeFactValue(key)}
                    onKeep={() => void keepFactValue(key)}
                    onSaveToCase={() => void saveFieldToCase(key)}
                  />
                </span>
              </div>
            );
          })}
        </EditorSection>
      ))}

      {/*
        Zivy pruchod 13. 10. (V2): sekce „Sluzby a podminene odstavce"
        (vytapeni, voda, vytah…) davala smysl jen u najmu bytu/nebytu —
        u prodeje/rezervace/zprostredkovani (vcetne prodeje pozemku) se
        tyto odstavce v mustru nepouzivaji, sekce byla jen matouci sum.
      */}
      {(contract.contractType === 'najem-byt' || contract.contractType === 'najem-nebyt') && (
      <EditorSection
        title={t('webAdmin.contracts.editor.servicesHeading', { locale: LOCALE })}
        open={false}
        testId="contract-section-services"
      >
        {(catalogs?.services ?? []).map((svc) => (
          <div className="reos-field-row" key={svc.key}>
            <label className="reos-field-row__label" htmlFor={`contract-service-${svc.key}`}>
              {svc.label}
            </label>
            <span className="reos-field-row__input">
              <select
                id={`contract-service-${svc.key}`}
                value={contract.serviceStates[svc.key] ?? svc.defaultState}
                onChange={(e) => void onServiceStateChange(svc.key, e.target.value as ServiceState)}
                data-testid={`contract-service-${svc.key}`}
              >
                {SERVICE_STATES.map((st) => (
                  <option key={st} value={st}>
                    {t(SERVICE_STATE_KEY[st], { locale: LOCALE })}
                  </option>
                ))}
              </select>
            </span>
          </div>
        ))}
      </EditorSection>
      )}

      {/* Krok T5 (bod 6): lhuty ze smlouvy jako ukoly zakazky s terminem. */}
      <EditorSection
        title={t('webAdmin.contracts.editor.deadlinesHeading', { locale: LOCALE })}
        open={false}
        testId="contract-section-deadlines"
      >
        <ContractDeadlinesSection contractId={contractId} />
      </EditorSection>

      <EditorSection
        title={t('webAdmin.contracts.editor.versionsHeading', { locale: LOCALE })}
        open={false}
        testId="contract-section-versions"
      >
        <button
          type="button"
          className="rh-btn"
          onClick={() => void saveVersion()}
          data-testid="contract-save-version"
        >
          {t('webAdmin.contracts.editor.saveVersionButton', { locale: LOCALE })}
        </button>
        {versions.length === 0 ? (
          <p>{t('webAdmin.contracts.editor.versionsEmpty', { locale: LOCALE })}</p>
        ) : (
          <ul data-testid="contract-versions">
            {versions.map((v) => (
              <li key={v.id}>
                {v.versionNo}. {v.changeNote ?? ''}{' '}
                {/* Krok NAP7 bod A17: diff hodnot proti PREDCHOZI verzi. U prvni
                    verze tlacitko zustava — server vrati „neni s cim porovnat",
                    takze obrazovka netvrdi, ze se nic nezmenilo. */}
                <button
                  type="button"
                  className="rh-v4-btn rh-v4-btn--plain"
                  onClick={() => void runVersionDiff(v.versionNo)}
                  disabled={aiBusy !== null}
                  data-testid={`contract-version-diff-${v.versionNo}`}
                >
                  {aiBusy === 'diff'
                    ? t('webAdmin.contracts.editor.versionDiffRunning', { locale: LOCALE })
                    : t('webAdmin.contracts.editor.versionDiffButton', { locale: LOCALE })}
                </button>
              </li>
            ))}
          </ul>
        )}
        {versionDiff === null ? null : (
          <div data-testid="contract-version-diff">
            {versionDiff.previousVersionNo === null ? (
              <p>{t('webAdmin.contracts.editor.versionDiffFirst', { locale: LOCALE })}</p>
            ) : !versionDiff.available ? (
              <p>{t('webAdmin.contracts.editor.versionDiffUnavailable', { locale: LOCALE })}</p>
            ) : versionDiff.changes.length === 0 ? (
              <p>{t('webAdmin.contracts.editor.versionDiffNone', { locale: LOCALE })}</p>
            ) : (
              <>
                <p>
                  {t('webAdmin.contracts.editor.versionDiffHeading', {
                    locale: LOCALE,
                    values: { previous: String(versionDiff.previousVersionNo) },
                  })}
                </p>
                <ul data-testid="contract-version-diff-changes">
                  {versionDiff.changes.map((change) => (
                    <li
                      key={change.key}
                      {...(change.isPriceField ? { 'data-price': '1' } : {})}
                      data-testid={`contract-version-diff-change-${change.key}`}
                    >
                      {humanFieldKey(change.key, catalogs)}: {change.ours ?? '—'} →{' '}
                      {change.theirs ?? '—'}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </EditorSection>

      {/* Krok NAP7 bod A17: jadro AI kontroly smlouvy existovalo od kroku AI7
          a nemelo volajiciho — tohle je jeho jedina cesta k maklerovi. */}
      <EditorSection
        title={t('webAdmin.contracts.editor.aiHeading', { locale: LOCALE })}
        open={false}
        testId="contract-section-ai"
      >
        <button
          type="button"
          className="rh-btn"
          onClick={() => void runAiReview()}
          disabled={aiBusy !== null}
          data-testid="contract-ai-review"
        >
          {aiBusy === 'review'
            ? t('webAdmin.contracts.editor.aiReviewRunning', { locale: LOCALE })
            : t('webAdmin.contracts.editor.aiReviewButton', { locale: LOCALE })}
        </button>
        <button
          type="button"
          className="rh-v4-btn rh-v4-btn--plain"
          onClick={() => void runAiExplain()}
          disabled={aiBusy !== null}
          data-testid="contract-ai-explain"
        >
          {aiBusy === 'explain'
            ? t('webAdmin.contracts.editor.aiExplainRunning', { locale: LOCALE })
            : t('webAdmin.contracts.editor.aiExplainButton', { locale: LOCALE })}
        </button>

        {aiReview === null ? null : (
          <div data-testid="contract-ai-review-result">
            {!aiReview.aiAvailable ? (
              <p data-testid="contract-ai-unavailable">
                {t('webAdmin.contracts.editor.aiUnavailable', { locale: LOCALE })}
              </p>
            ) : null}
            {aiReview.noSources ? (
              <p data-testid="contract-ai-review-no-sources">
                {t('webAdmin.contracts.editor.aiReviewNoSources', { locale: LOCALE })}
              </p>
            ) : null}
            {aiReview.checks.length === 0 && aiReview.contradictions.length === 0 ? (
              <p data-testid="contract-ai-review-none">
                {t('webAdmin.contracts.editor.aiReviewNone', { locale: LOCALE })}
              </p>
            ) : (
              <ul data-testid="contract-ai-review-findings">
                {/* Odkaz na POLE: klik otevre tutez cestu jako panel kontrol
                    (`focusField`), takze nalez neukazuje do prazdna. */}
                {aiReview.checks.map((check) => (
                  <li key={`${check.code}-${check.fieldKey ?? ''}`}>
                    {check.fieldKey === null || check.fieldKey === undefined ? (
                      check.message
                    ) : (
                      <button
                        type="button"
                        className="rh-v4-btn rh-v4-btn--plain"
                        onClick={() => focusFieldControl(check.fieldKey ?? '', null)}
                        data-testid={`contract-ai-review-field-${check.fieldKey}`}
                      >
                        {check.message}
                      </button>
                    )}
                  </li>
                ))}
                {aiReview.contradictions.map((c) => (
                  <li
                    key={`${c.factKeyA}-${c.factKeyB}`}
                    data-testid={`contract-ai-contradiction-${c.factKeyA}`}
                  >
                    {humanFieldKey(c.factKeyA, catalogs)} ×{' '}
                    {humanFieldKey(c.factKeyB, catalogs)}: {c.explanation}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {aiExplanation === null ? null : (
          <div data-testid="contract-ai-explanation">
            {!aiExplanation.aiAvailable ? (
              <p data-testid="contract-ai-explain-unavailable">
                {t('webAdmin.contracts.editor.aiExplainUnavailable', { locale: LOCALE })}
              </p>
            ) : aiExplanation.explanation === null ? (
              <p>{t('webAdmin.contracts.editor.aiExplainEmpty', { locale: LOCALE })}</p>
            ) : (
              <>
                <p data-testid="contract-ai-explanation-text">{aiExplanation.explanation}</p>
                {aiExplanation.disclaimer === null ? null : (
                  <p className="reos-disclaimer" data-testid="contract-ai-explanation-disclaimer">
                    {aiExplanation.disclaimer}
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </EditorSection>
    </div>
  );

  /**
   * Krok SML3 bod 1: FIALOVY PRUH rezimu „Upravit text" (predloha
   * `SmlouvaText.dc.html`) — jedna veta o tom, co uprava znamena, a jedina
   * vedlejsi akce „Uložit jako nový mustr".
   *
   * Kdyz je text ZAMCENY (bod 4), stoji na jeho miste duvod a cesta ven:
   * zrusit podepisovani. Rozhodnuti „jde to menit?" dela server
   * (`textEditable`), ne tahle obrazovka.
   */
  const textModeBar =
    !editText ? null : textLocked ? (
      <div className="reos-text-lock" data-testid="contract-text-locked">
        <span>
          {t(
            contract.textLockReason === 'podepsano'
              ? 'webAdmin.contracts.text.lockedSigned'
              : 'webAdmin.contracts.text.lockedSigning',
            { locale: LOCALE },
          )}
        </span>
        <button
          type="button"
          className="rh-btn"
          onClick={() => setTab('signature')}
          data-testid="contract-text-locked-stop"
        >
          {t('webAdmin.contracts.text.stopAndEdit', { locale: LOCALE })}
        </button>
      </div>
    ) : (
      <div className="reos-text-bar" data-testid="contract-text-bar">
        <span>{t('webAdmin.contracts.text.ownTextOnly', { locale: LOCALE })}</span>
        <button
          type="button"
          className="reos-checks__jump"
          onClick={() => void saveAsTemplate()}
          data-testid="contract-save-as-template"
        >
          {t('webAdmin.contracts.text.saveAsTemplate', { locale: LOCALE })}
        </button>
        {savedTemplate !== null && (
          <span className="reos-field-row__hint" data-testid="contract-saved-as-template">
            {savedTemplate}
          </span>
        )}
      </div>
    );

  /**
   * Panel nastroju nad dokumentem (krok SML3 bod 5): tucne, kurziva,
   * cislovany seznam, „+ Vložit pole" ze slovniku T1, „+ Vložit klauzuli"
   * a prepinac „Zobrazit změny".
   */
  const textToolbar =
    !editText || textLocked ? null : (
      <div
        className="reos-text-tools"
        role="toolbar"
        aria-label={t('webAdmin.contracts.text.toolbar', { locale: LOCALE })}
        data-testid="contract-text-toolbar"
      >
        <button
          type="button"
          className="reos-text-tools__btn"
          aria-label={t('webAdmin.contracts.text.bold', { locale: LOCALE })}
          onClick={() => applyFormat('bold')}
          data-testid="contract-text-bold"
        >
          <b>B</b>
        </button>
        {/* Krok UXD1 bod 7: tlacitko I (kurziva) je PRYC. `blockTemplateTextFrom`
            umi do sablony ulozit tucne (`**…**`), kurzivu ne — zapis pro ni
            renderer smlouvy nema, takze se po ulozeni vzdy ztratila. Nabizet
            format, ktery se neulozi, je horsi nez ho nemit. */}
        <button
          type="button"
          className="reos-text-tools__btn"
          aria-label={t('webAdmin.contracts.text.orderedList', { locale: LOCALE })}
          onClick={() => applyFormat('insertOrderedList')}
          data-testid="contract-text-list"
        >
          1.
        </button>
        {/* „+ Vložit pole" nabizi POLE SLOVNIKU T1 (tentyz katalog, z jakeho
            jsou popisky ve formulari) — do textu se vlozi jeho `{{token}}`,
            takze zustava zive a dosazuje se jako kazde jine pole. */}
        <label className="reos-text-tools__field">
          {t('webAdmin.contracts.text.insertField', { locale: LOCALE })}
          <select
            value=""
            onChange={(e) => {
              if (e.target.value === '') return;
              insertAtCursor(`{{${e.target.value}}}`);
              e.target.value = '';
            }}
            data-testid="contract-text-insert-field"
          >
            <option value="">
              {t('webAdmin.contracts.text.insertFieldPlaceholder', { locale: LOCALE })}
            </option>
            {(catalogs?.fields ?? []).map((field) => (
              <option key={field.key} value={field.key}>
                {field.label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="reos-checks__jump"
          onClick={() => clauseSearchRef.current?.focus()}
          data-testid="contract-text-insert-clause"
        >
          {t('webAdmin.contracts.text.insertClause', { locale: LOCALE })}
        </button>
        <label className="reos-text-tools__toggle">
          <input
            type="checkbox"
            checked={showChanges}
            onChange={(e) => setShowChanges(e.target.checked)}
            data-testid="contract-show-changes"
          />
          {t('webAdmin.contracts.text.showChanges', { locale: LOCALE })}
        </label>
      </div>
    );

  /**
   * VYBRANE POLE (krok SML2 bod 5) — navrh z dat zakazky, kde se pole pouziva
   * a prechod na dalsi chybejici.
   *
   * Krok UXD1 bod 6 (R.B. 12. 10. 21:39: „proč je tam napravo ten sloupec
   * vůbec"): editor ma uz jen DVE casti — vlevo se vyplnuje, vpravo je nahled
   * smlouvy. Tenhle blok proto stoji V LEVEM SLOUPCI, nad osnovou: je to
   * pomucka k vyplnovani (navrh z katastru, „Další chybějící"), a vyplnuje se
   * JEDINE vlevo. Treti sloupec `contract-side` zmizel; stav odkazu stran
   * a odeslani se presunuly do zalozky „Pro klienta", kontroly nad dokument.
   */
  const selectedFieldPanel = (
    <aside className="reos-ed__focused" data-testid="contract-focused-field">
      <section className="reos-ed__block">
        <span className="reos-field-section__title">
          {selectedArticle === undefined || selectedArticle.roman === ''
            ? t('webAdmin.contracts.editor.selectedFieldHeading', { locale: LOCALE })
            : t('webAdmin.contracts.editor.selectedFieldArticle', {
                locale: LOCALE,
                values: { article: selectedArticle.roman },
              })}
        </span>
        {activeField === null ? (
          <p className="reos-field-row__hint" data-testid="contract-selected-none">
            {t('webAdmin.contracts.editor.selectedFieldNone', { locale: LOCALE })}
          </p>
        ) : (
          <>
            <label
              className="reos-ed__field-label"
              id="contract-selected-label"
              htmlFor="contract-selected-input"
            >
              {fieldLabel(catalogs, activeField, templateFields)}
            </label>
            <span className="reos-ed__field-input">
              <ContractFieldControl
                def={selectedDef}
                value={selectedFieldValue}
                controlId="contract-selected-input"
                labelledBy="contract-selected-label"
                onPreview={(next) => previewFieldInDocument(activeField, activeIndexNumber, next)}
                onCommit={(next) => commitSelectedField(next)}
                testId="contract-selected-field"
              />
            </span>
            {selectedSuggestion !== null && (
              <div className="reos-ed__suggest" data-testid="contract-suggestion">
                <span className="reos-ed__suggest-label">
                  {t('webAdmin.contracts.editor.suggestionHeading', { locale: LOCALE })}
                </span>
                <span className="reos-ed__suggest-row">
                  <b className="num">{selectedSuggestion}</b>
                  <button
                    type="button"
                    className="rh-btn rh-btn--primary"
                    onClick={() => commitSelectedField(selectedSuggestion)}
                    data-testid="contract-suggestion-use"
                  >
                    {t('webAdmin.contracts.editor.suggestionUse', { locale: LOCALE })}
                  </button>
                </span>
              </div>
            )}
            <div className="reos-ed__field-meta">
              <span data-testid="contract-selected-filler">
                {t(FILLER_NOTE_KEY[fillerOfField(activeField)], { locale: LOCALE })}
                {' · '}
                {t(
                  requiredKeys.has(activeField)
                    ? 'webAdmin.contracts.editor.requiredForSend'
                    : 'webAdmin.contracts.editor.notRequiredForSend',
                  { locale: LOCALE },
                )}
              </span>
              <span data-testid="contract-selected-usage">
                {selectedUsage.length === 0
                  ? t('webAdmin.contracts.editor.usedInNone', { locale: LOCALE })
                  : t('webAdmin.contracts.editor.usedIn', {
                      locale: LOCALE,
                      values: { articles: selectedUsage.join(', ') },
                    })}
              </span>
            </div>
          </>
        )}
        <div className="reos-ed__field-nav">
          <button
            type="button"
            className="rh-btn"
            disabled={stepMissingField(missingOrder, activeField, -1) === null}
            onClick={() => {
              const prev = stepMissingField(missingOrder, activeField, -1);
              if (prev !== null) selectField(prev);
            }}
            data-testid="contract-prev-missing"
          >
            {t('webAdmin.contracts.editor.prevMissing', { locale: LOCALE })}
          </button>
          <button
            type="button"
            className="rh-btn"
            disabled={stepMissingField(missingOrder, activeField, 1) === null}
            onClick={() => {
              const next = stepMissingField(missingOrder, activeField, 1);
              if (next !== null) selectField(next);
            }}
            data-testid="contract-next-missing"
          >
            {t('webAdmin.contracts.editor.nextMissing', { locale: LOCALE })}
          </button>
        </div>
      </section>
    </aside>
  );

  /**
   * KONTROLY NAD DOKUMENTEM (krok UXD1 bod 6) — kompaktni lista „24 chybí ▾".
   *
   * Pravidla jsou v domene (`@reos/documents`), UI je JEN zobrazuje: server
   * posila `checks` tou samou funkci, kterou vyhodnoti pri odeslani.
   *
   * Do UXD1 stal tenhle seznam v tretim sloupci vpravo (`contract-side`).
   * Patri ale k dokumentu — rika, co ve smlouve jeste chybi — a jako sbalena
   * lista nad nim zabere jeden radek. Rozbaleny obsah zustava TENTYZ, vcetne
   * „Zobrazit vše" a skoku na pole ve formulari (krok UXS2 bod 2).
   */
  const checksBar = (
    <details className="reos-checks-bar" data-testid="contract-missing-bar">
      <summary className="reos-checks-bar__summary" data-testid="contract-missing-summary">
        {checks.length === 0
          ? t('webAdmin.contracts.editor.checksNone', { locale: LOCALE })
          : checksSummaryText(checks)}
      </summary>
      <div className="reos-checks" data-testid="contract-checks-panel">
        <p className="reos-field-section__title">
          {t('webAdmin.contracts.editor.checksHeading', { locale: LOCALE })}
        </p>
        {checks.length === 0 ? (
          <p data-testid="contract-checks">
            {t('webAdmin.contracts.editor.checksNone', { locale: LOCALE })}
          </p>
        ) : (
          <>
            <p className="reos-checks__summary" data-testid="contract-checks-summary">
              {checksSummaryText(checks)}
            </p>
            <ul data-testid="contract-check-lines">
              {(allChecks ? checks : checks.slice(0, CHECKS_PREVIEW_LIMIT)).map((line, i) => {
                const text =
                  line.key === 'webAdmin.contracts.editor.checkMissingPrefix' ||
                  line.key === 'webAdmin.contracts.editor.checkLaterPrefix'
                    ? `${t(line.key, { locale: LOCALE })} ${line.message}`
                    : line.key !== null
                      ? t(line.key, { locale: LOCALE })
                      : line.message;
                return (
                  <li key={`${line.kind}-${i}`} className={`reos-check--${line.kind}`}>
                    {line.fieldKey === null ? (
                      text
                    ) : (
                      <button
                        type="button"
                        className="reos-checks__jump"
                        onClick={() => focusFieldControl(line.fieldKey ?? '', null)}
                        data-testid={`contract-check-jump-${line.fieldKey}`}
                      >
                        {text}{' '}
                        {/* Krok EDIT2 bod 3: radek kontrol nesmi byt jen
                            pocet — jmenuje pole a nabizi cestu k nemu. */}
                        <span className="reos-checks__jump-hint" aria-hidden="true">
                          {t('webAdmin.contracts.editor.checkGoToField', { locale: LOCALE })}
                        </span>
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
            {checks.length > CHECKS_PREVIEW_LIMIT && (
              <button
                type="button"
                className="reos-checks__more"
                aria-expanded={allChecks}
                onClick={() => setAllChecks((v) => !v)}
                data-testid="contract-checks-toggle"
              >
                {t(
                  allChecks
                    ? 'webAdmin.contracts.editor.checksShowLess'
                    : 'webAdmin.contracts.editor.checksShowAll',
                  { locale: LOCALE, values: { count: checks.length } },
                )}
              </button>
            )}
          </>
        )}
      </div>
    </details>
  );

  const documentPanel = (
    <div className="reos-doc-panel" data-testid="contract-editor-preview">
      {/* Sazba dokumentu je JEDNA konstanta pro obrazovku i tisk. */}
      {/* Krok PRF5 bod 4: sazba dokumentu ze serveru (`documentCss`) — tataz,
          kterou dostane tiskove okno i klientsky nahled na verejnem webu. */}
      <style>{contract?.documentCss ?? ''}</style>
      {/* Krok UXD1 bod 2 (zivy nalez pilotu: „neví, kam má co psát — řeší
          pravý a levý sloupec"): oba sloupce maji ZAHLAVI. Vpravo je nahled,
          ne druhy formular. */}
      <p className="reos-ed__colhead" data-testid="contract-col-document">
        {t('webAdmin.contracts.editor.colDocument', { locale: LOCALE })}
      </p>
      {/* Kontroly jako kompaktni lista nad dokumentem (bod 6). */}
      {!editText && checksBar}
      {/* Krok UXD1 bod 3: klik na pole v rezimu „Upravit text" rekne, kde se
          udaj meni, a prepne tam. */}
      {editText && lockedFieldHint !== null && (
        <button
          type="button"
          className="reos-ed__locked-hint"
          data-testid="contract-locked-field-hint"
          onClick={() => {
            setMode('fill');
            setPendingFocus(lockedFieldHint);
            setLockedFieldHint(null);
          }}
        >
          {t('webAdmin.contracts.editor.lockedFieldHint', { locale: LOCALE })}
        </button>
      )}
      <OriginLegend />
      {textModeBar}
      {textToolbar}
      {/* Hlaska rezimu textu (napr. „klikni nejdřív do odstavce"): editor
          jinak chybu ukazuje jen misto cele obrazovky, kdyz se smlouva
          nenactla — v rezimu textu by tedy zmizela bez slova. */}
      {editText && error !== undefined && (
        <p role="alert" className="rh-a2-error" data-testid="contract-text-error">
          {error}
        </p>
      )}
      {/* Krok KLIKFIX1 (vada B4): ulozeni, ktere neco zahodilo, uz nemlci. */}
      {editText && textEditSkipped !== null && (
        <p role="alert" className="rh-a2-error" data-testid="contract-edit-text-skipped">
          {[
            textEditSkipped.field
              ? t('webAdmin.contracts.editor.editTextSkippedField', { locale: LOCALE })
              : null,
            textEditSkipped.list
              ? t('webAdmin.contracts.editor.editTextSkippedList', { locale: LOCALE })
              : null,
          ]
            .filter((line): line is string => line !== null)
            .join(' ')}
        </p>
      )}
      {editText && !textLocked && (
        <div className="reos-doc-panel__edit-bar" data-testid="contract-edit-text-bar">
          <span>{t('webAdmin.contracts.editor.editTextHint', { locale: LOCALE })}</span>
          <button
            type="button"
            className="rh-btn rh-btn--primary"
            onClick={() => void saveEditedBlocks()}
            data-testid="contract-edit-text-save"
          >
            {t('webAdmin.contracts.editor.editTextSave', { locale: LOCALE })}
          </button>
          {focusedBlockId !== null && contract.manualOverrides[focusedBlockId] !== undefined && (
            <button
              type="button"
              className="rh-btn"
              onClick={() => void revertOverride(focusedBlockId)}
              data-testid="contract-edit-text-revert"
            >
              {t('webAdmin.contracts.editor.manualEditRevert', { locale: LOCALE })}
            </button>
          )}
          {/* Odebrani odstavce z teto smlouvy (bod 2). V rezimu zmen po nem
              zustane preskrtnuty text a „Vrátit na mustr" ho vrati. */}
          {focusedBlockId !== null && (
            <button
              type="button"
              className="rh-btn"
              onClick={() => void removeBlock(focusedBlockId)}
              data-testid="contract-text-remove-block"
            >
              {t('webAdmin.contracts.text.removeBlock', { locale: LOCALE })}
            </button>
          )}
        </div>
      )}
      {/* Obsluha je NAD dokumentem, ne na nem: dokument samotny je memoizovany
          (viz `ContractDocument`) a obsluha se pri kazdem renderu sklada znovu,
          takze by memo zahodila. Udalosti probublaji sem. */}
      <div
        onClick={(event) => {
          onDocumentClick(event);
          rememberCursorBlock();
        }}
        onKeyUp={rememberCursorBlock}
        // Krok KLIKFIX1 (vada B4): psani do odstavce si pamatujeme, aby
        // ulozeni umelo rict, kterou upravu zahodilo a proc.
        onInput={rememberEditedBlock}
        onFocus={onDocumentFocusIn}
        onBlur={() => {
          if (editText) void saveEditedBlocks();
        }}
      >
        <ContractDocument
          innerRef={previewRef}
          html={documentHtml}
          cleanView={cleanView}
          editText={editText && !textLocked}
        />
      </div>
    </div>
  );

  /* -------------------------------------------------- panely krok SML2 */

  /**
   * OSNOVA VLEVO (krok SML2 bod 2) — clanky z enginu smlouvy s poctem
   * chybejicich podle zdroje, ukazatel „hotovo X z Y" s rozpadem a tlacitko
   * „Doplnit N údajů z katastru". Pod osnovou zustava CELY formular
   * („Všechna pole smlouvy") — nic z editoru neubylo, jen uz nestoji dva
   * sloupce stejne vaznych prazdnych poli (R.B. 3. 10.).
   */
  /*
   * Krok PROP1 bod 3: tlacitko uz nedoplnuje jen katastr, ale CELOU zakazku —
   * k polim z katastru pribyla pole, pro ktera ma zakazka fakta z naboru nebo
   * od majitele (`caseFactKeys`). Ta osnova pocita maklerovi, protoze je
   * makler dopsat MUZE — doplneni ze zakazky mu je jen usetri.
   * Popisek tlacitka zustava „z katastru": je to merene misto chranene
   * orchestratorem a prejmenovani ze zadani ceka na POVOLENO R.B.
   * (REPORT-step-PROP1.md § ZBÝVÁ).
   */
  const prefillCount = plan.missingByFiller.katastr + plan.caseFactKeys.length;
  const outlinePanel = (
    <div className="reos-ed__outline" data-testid="contract-outline">
      {/* Krok UXD1 bod 2 — ZAHLAVI SLOUPCE a lista „kam co psát". Zivy nalez
          pilotu (R.B. 12. 10. 21:36): makleřka „neví, kam má co psát — řeší
          pravý a levý sloupec". */}
      <p className="reos-ed__colhead" data-testid="contract-col-form">
        {t('webAdmin.contracts.editor.colForm', { locale: LOCALE })}
      </p>
      {/* Krok UXD3 bod 3 (R.B. 12. 10. 21:47 „možnost nastavení tučně
          vlevo"): volba platna pro CELOU smlouvu — prebiji vzor kancelare.
          Jednotlive pole z ni vybocuje tlacitkem „B" u svého radku. */}
      <div className="reos-ed__bold" data-testid="contract-bold-all-row">
        <label className="reos-ed__bold-label">
          <input
            type="checkbox"
            checked={contract.boldFilledFields === true}
            onChange={(e) => onToggleBoldAll(e.target.checked)}
            data-testid="contract-bold-all"
          />
          {t('webAdmin.contracts.editor.boldFilledFields', { locale: LOCALE })}
        </label>
        <span className="reos-field-row__hint">
          {t('webAdmin.contracts.editor.boldFilledFieldsHint', { locale: LOCALE })}
        </span>
      </div>
      {whereHint && (
        <div className="reos-ed__wherebar" data-testid="contract-where-hint">
          <span>{t('webAdmin.contracts.editor.whereHint', { locale: LOCALE })}</span>
          <button
            type="button"
            className="reos-ed__wherebar-close"
            aria-label={t('webAdmin.contracts.editor.whereHintClose', { locale: LOCALE })}
            onClick={() => {
              setWhereHint(false);
              writeContractWhereHintDismissed();
            }}
            data-testid="contract-where-hint-close"
          >
            ×
          </button>
        </div>
      )}
      {/* Pomucka k vyplnovani (vybrane pole, navrh z katastru, „Další
          chybějící") — od UXD1 bodu 6 v LEVEM sloupci, protoze vyplnovat se
          smi jen vlevo. */}
      {selectedFieldPanel}
      <div className="reos-ed__progress">
        {/* Krok EDIT2 bod 9 (nalez V4): „Hotovo 3 z 44" scitalo i pole
            ODSUNUTA na „doplnit později", ktera podpis neblokuji — makler
            z nej nemohl poznat, kolik prace ho jeste deli od odeslani.
            Jmenovatel je proto jen to, co podpis blokuje (vyplnene +
            chybejici); odsunuta pole stoji vedle, zvlast. */}
        <span className="reos-ed__progress-text" data-testid="contract-progress">
          {t('webAdmin.contracts.editor.progress', {
            locale: LOCALE,
            values: { filled: plan.filled, total: plan.filled + plan.missing },
          })}
        </span>
        {plan.later > 0 && (
          <span className="reos-ed__progress-later" data-testid="contract-progress-later">
            {t('webAdmin.contracts.editor.progressLater', {
              locale: LOCALE,
              values: { count: plan.later },
            })}
          </span>
        )}
        <span className="reos-ed__bar" aria-hidden="true">
          <span className="reos-ed__bar-part reos-ed__bar-part--done" style={{ flexGrow: plan.filled }} />
          {FILLER_ORDER.map((filler) => (
            <span
              key={filler}
              className={`reos-ed__bar-part reos-ed__bar-part--${filler}`}
              style={{ flexGrow: plan.missingByFiller[filler] }}
            />
          ))}
        </span>
        <span className="reos-ed__legend">
          {FILLER_ORDER.filter((f) => plan.missingByFiller[f] > 0).map((filler) => (
            <span key={filler} data-testid={`contract-filler-${filler}`}>
              <span className={`reos-ed__swatch reos-ed__bar-part--${filler}`} aria-hidden="true" />
              {t(FILLER_LEGEND_KEY[filler], {
                locale: LOCALE,
                values: { count: plan.missingByFiller[filler] },
              })}
            </span>
          ))}
        </span>
      </div>

      {/* Krok SML2b bod 3: „Doplnit N údajů z katastru" jen u smlouvy
          V ZAKAZCE. Bez zakazky nema server odkud fakta vzit (port cte
          prehled nabidky zakazky a nemovitost zakazky), takze tlacitko
          slibovalo praci, kterou REOS udelat nemuze — R.B. 3. 10. Misto nej
          stoji jedna veta a privazani k zakazce, cim se tlacitko zapne. */}
      {caseId === null ? (
        <div className="reos-ed__no-case">
          <p className="reos-ed__cadastre-note" data-testid="contract-no-case-note">
            {t('webAdmin.contracts.editor.noCaseNote', { locale: LOCALE })}
          </p>
          {attaching ? (
            <div className="reos-ed__no-case-pick">
              {/* Hledani i vyber jsou tytez jako v prehledu `/smlouvy`
                  (ADM1 bod 2) — jeden zpusob, jak smlouvu privazat. */}
              <input
                value={attachQuery}
                onChange={(e) => setAttachQuery(e.target.value)}
                placeholder={t('webAdmin.contracts.new.casePlaceholder', { locale: LOCALE })}
                data-testid="contract-attach-search"
              />
              {attachQuery.trim() !== '' && caseIndex !== null && (
                <ul className="reos-ed__no-case-list">
                  {[...caseIndex.entries()]
                    .filter(([, name]) =>
                      name.toLowerCase().includes(attachQuery.trim().toLowerCase()),
                    )
                    .slice(0, 10)
                    .map(([id, name]) => (
                      <li key={id}>
                        <button
                          type="button"
                          className="reos-ed__no-case-option"
                          onClick={() => {
                            setAttaching(false);
                            setAttachQuery('');
                            void patch({ caseId: id });
                          }}
                          data-testid={`contract-attach-option-${id}`}
                        >
                          {name}
                        </button>
                      </li>
                    ))}
                </ul>
              )}
              <button
                type="button"
                className="rh-btn"
                onClick={() => {
                  setAttaching(false);
                  setAttachQuery('');
                }}
              >
                {t('webAdmin.contracts.editor.attachCaseCancel', { locale: LOCALE })}
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="rh-btn reos-ed__cadastre"
              onClick={() => setAttaching(true)}
              data-testid="contract-attach-case"
            >
              {t('webAdmin.contracts.editor.attachCase', { locale: LOCALE })}
            </button>
          )}
        </div>
      ) : (
        <button
          type="button"
          className="rh-btn reos-ed__cadastre"
          disabled={prefillCount === 0 || prefillBusy}
          onClick={() => void onPrefillCadastre()}
          data-testid="contract-prefill-cadastre"
        >
          {prefillCount === 0
            ? t('webAdmin.contracts.editor.prefillCadastreEmpty', { locale: LOCALE })
            : t('webAdmin.contracts.editor.prefillCadastre', {
                locale: LOCALE,
                values: { count: prefillCount },
              })}
        </button>
      )}
      {prefillResult !== null && (
        <p className="reos-ed__cadastre-note" role="status" data-testid="contract-prefill-note">
          {prefillResult.filled > 0
            ? t('webAdmin.contracts.editor.prefillCadastreResult', {
                locale: LOCALE,
                values: { count: prefillResult.filled },
              })
            : t('webAdmin.contracts.editor.prefillCadastreNothing', { locale: LOCALE })}
          {prefillResult.skipped.length > 0 &&
            ` ${t('webAdmin.contracts.editor.prefillCadastreSkipped', {
              locale: LOCALE,
              values: { fields: prefillResult.skipped.join(', ') },
            })}`}
        </p>
      )}

      <ol className="reos-ed__articles" aria-label={t('webAdmin.contracts.editor.outlineLabel', { locale: LOCALE })}>
        {plan.articles.map((article) => {
          // Nejvyznamnejsi zdroj clanku POCITA SERVER (`fillPlan.articles[].filler`).
          const filler = article.filler;
          return (
            <li key={article.id}>
              <button
                type="button"
                className={`reos-ed__article${
                  selectedArticle?.id === article.id ? ' reos-ed__article--on' : ''
                }`}
                onClick={() => selectArticle(article)}
                data-testid={`contract-outline-${article.id}`}
              >
                <span className="reos-ed__article-name">
                  {article.roman === ''
                    ? article.title === ''
                      ? // Zahlavi smlouvy: nazev clanku je nazev souboru z importu,
                        // takze popisek dodava UI (viz `contractFillPlan`).
                        t('webAdmin.contracts.editor.outlinePreamble', { locale: LOCALE })
                      : article.title
                    : `${article.roman}. ${article.title}`}
                </span>
                {article.missing === 0 ? (
                  /* `role="img"` ze stejneho duvodu jako u avataru: `aria-label`
                     na `<span>` bez role je axe `aria-prohibited-attr`. */
                  <span role="img" className="reos-ed__article-done" aria-label={t('webAdmin.contracts.editor.outlineDone', { locale: LOCALE })}>
                    ✓
                  </span>
                ) : (
                  <span
                    className={`reos-ed__article-count reos-ed__article-count--${filler ?? 'makler'}`}
                    data-testid={`contract-outline-count-${article.id}`}
                  >
                    {article.missing}
                    {filler === null ? '' : ` · ${t(FILLER_SHORT_KEY[filler], { locale: LOCALE })}`}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ol>

      {/* Prilohy podle predlohy (krok SML2 bod 8). */}
      <div className="reos-ed__attachments" data-testid="contract-attachments">
        <span className="reos-field-section__title">
          {t('webAdmin.contracts.editor.attachmentsHeading', { locale: LOCALE })}
        </span>
        <span className="reos-ed__attachment">
          {t('webAdmin.contracts.editor.attachmentProtocol', { locale: LOCALE })}
          <span className="reos-field-row__hint" data-testid="contract-attachment-protocol">
            {handoverRoman === null
              ? t('webAdmin.contracts.editor.attachmentProtocolNone', { locale: LOCALE })
              : t('webAdmin.contracts.editor.attachmentProtocolFrom', {
                  locale: LOCALE,
                  values: { article: handoverRoman },
                })}
          </span>
        </span>
        {/*
          Krok UXD3b (nalez SML6) — PENB JEN TAM, KDE DAVA SMYSL.
          Zprostredkovatelska smlouva o prodeji ukazovala „Energetický štítek
          (PENB): Vyžádat od majitele", prestoze prukaz je prilohou NAJMU BYTU
          (z. 406/2000 Sb. § 7a odst. 2) a zprostredkovani se netyka. Kdo tu
          povinnost ma, ROZHODUJE SERVER — `penbRelevant` cte jeho kontrolu
          (`chybi-penb-priloha`, `PENB_REQUIRED_FOR` v `checks.ts`) a znamou
          tridu; UI si druhy seznam typu smluv nedrzi.
        */}
        {penbRelevant && (
        <span className="reos-ed__attachment">
          {t('webAdmin.contracts.editor.attachmentPenb', { locale: LOCALE })}
          {penbClass === null ? (
            <button
              type="button"
              className="reos-checks__jump"
              onClick={() => setTab('client')}
              data-testid="contract-attachment-penb-request"
            >
              {t('webAdmin.contracts.editor.attachmentPenbRequest', { locale: LOCALE })}
            </button>
          ) : (
            <span className="reos-field-row__hint" data-testid="contract-attachment-penb">
              {t('webAdmin.contracts.editor.attachmentPenbHave', {
                locale: LOCALE,
                values: { value: penbClass },
              })}
            </span>
          )}
        </span>
        )}
      </div>

      {/*
        Cely dnesni formular zustava — jen sbaleny. Dva duvody, oba vecne:
        pole mimo text smlouvy (sluzby, lhuty, verze) nemaji v dokumentu
        mezeru, na kterou by slo kliknout, a `focusFieldControl` potrebuje
        ovladaci prvek v DOM, aby „skok na pole" z dokumentu i z kontrol
        nekoncil na `BODY` (nalez UXS1).
      */}
      {/*
        ROZHODNUTI SML2-B: oddil je VYCHOZE OTEVRENY. Predloha ma v levem
        sloupci jen osnovu, ale schovat formular za jedno kliknuti by znamenalo
        (a) skryt pole, ktera v textu smlouvy ZADNOU mezeru nemaji (sluzby,
        lhuty, verze) — na ta by se neslo dostat z dokumentu, a (b) rozbit
        vsechny zive sady, ktere pole editoru vyplnuji. Osnova je nad nim,
        takze prvni, co clovek vidi, je porad ta; sbalit si ho muze sam.
      */}
      <details className="reos-ed__allfields" open data-testid="contract-all-fields">
        <summary className="reos-field-section__title">
          {t('webAdmin.contracts.editor.allFieldsHeading', { locale: LOCALE })}
        </summary>
        {formPanel}
      </details>
    </div>
  );

  /**
   * PANEL VPRAVO V REZIMU „UPRAVIT TEXT" (krok SML3 body 2 a 3, predloha
   * `SmlouvaText.dc.html`): seznam zmen proti mustru a pod nim knihovna
   * klauzuli kancelare s hledanim.
   *
   * Nahrazuje panel vyplnovani (vybrane pole, kontroly, strany) — v rezimu
   * textu clovek nevyplnuje pole, a dva panely vedle sebe se na 1440 px
   * vedle dokumentu A4 nevejdou (tyz duvod jako u osnovy v SML2).
   */
  const textSidePanel = (
    <aside className="reos-ed__side" data-testid="contract-text-side">
      <section className="reos-ed__block">
        <span className="reos-field-section__title" data-testid="contract-changes-heading">
          {t('webAdmin.contracts.text.changesHeading', {
            locale: LOCALE,
            values: { count: changes.length },
          })}
        </span>
        {changes.length === 0 ? (
          <p data-testid="contract-changes-empty">
            {t('webAdmin.contracts.text.changesEmpty', { locale: LOCALE })}
          </p>
        ) : (
          <ul className="reos-changes" data-testid="contract-changes">
            {changes.map((change) => (
              <li
                key={change.blockId}
                className={`reos-changes__item reos-changes__item--${change.kind}`}
                data-testid={`contract-change-${change.blockId}`}
              >
                <span className="reos-changes__head">
                  <b data-testid={`contract-change-pos-${change.blockId}`}>
                    {t('webAdmin.contracts.text.changePosition', {
                      locale: LOCALE,
                      values: { position: change.position },
                    })}
                  </b>
                  <span className="reos-changes__who">
                    {textChangeAuthorLine(change, members, LOCALE)}
                  </span>
                </span>
                <span className="reos-changes__what">{textChangeSummary(change, LOCALE)}</span>
                <span className="reos-changes__actions">
                  <button
                    type="button"
                    className="reos-checks__jump"
                    onClick={() => showChangedBlock(change.blockId)}
                    data-testid={`contract-change-show-${change.blockId}`}
                  >
                    {t('webAdmin.contracts.text.changeShow', { locale: LOCALE })}
                  </button>
                  {!textLocked && (
                    <button
                      type="button"
                      className="reos-checks__jump reos-changes__revert"
                      onClick={() => void revertToTemplate(change.blockId)}
                      data-testid={`contract-change-revert-${change.blockId}`}
                    >
                      {t(
                        change.kind === 'vlozeno'
                          ? 'webAdmin.contracts.text.changeRemove'
                          : 'webAdmin.contracts.text.changeRevert',
                        { locale: LOCALE },
                      )}
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="reos-ed__block">
        <span className="reos-field-section__title">
          {t('webAdmin.contracts.text.libraryHeading', { locale: LOCALE })}
        </span>
        <label className="reos-changes__search">
          {t('webAdmin.contracts.text.librarySearch', { locale: LOCALE })}
          <input
            ref={clauseSearchRef}
            type="search"
            value={clauseQuery}
            onChange={(e) => setClauseQuery(e.target.value)}
            placeholder={t('webAdmin.contracts.text.librarySearchHint', { locale: LOCALE })}
            data-testid="contract-clause-search"
          />
        </label>
        {clauses.length === 0 ? (
          <p data-testid="contract-clauses-empty">
            {t('webAdmin.contracts.text.libraryEmpty', { locale: LOCALE })}
          </p>
        ) : (
          <ul className="reos-clauses" data-testid="contract-clauses">
            {clauses.map((clause) => (
              <li key={clause.id} className="reos-clauses__item">
                <span className="reos-clauses__text">
                  <span className="reos-clauses__title">{clause.title}</span>
                  {clause.note !== null && clause.note !== '' && (
                    <span className="reos-clauses__note">{clause.note}</span>
                  )}
                </span>
                <button
                  type="button"
                  className="reos-checks__jump"
                  disabled={textLocked}
                  onClick={() => void insertClause(clause.id)}
                  data-testid={`contract-clause-insert-${clause.id}`}
                >
                  {t('webAdmin.contracts.text.clauseInsert', { locale: LOCALE })}
                </button>
              </li>
            ))}
          </ul>
        )}
        <Link
          to="/smlouvy-sablony"
          className="reos-checks__jump"
          data-testid="contract-clause-library-link"
        >
          {t('webAdmin.contracts.text.libraryManage', { locale: LOCALE })}
        </Link>
      </section>
    </aside>
  );

  /**
   * STRANY A ODESLANI (krok SML2 bod 7) — stav kazde strany ze SKUTECNYCH
   * pozvanek a odeslani smlouvy stranam ke kontrole.
   *
   * Krok UXD1 bod 6: stoji v zalozce „Pro klienta", tedy TAM, KDE SE ODKAZ
   * POSILA. V editoru (vyplnovani) to byl treti sloupec, ktery v pilotu
   * s vypnutym odesilanim nemel co delat a mateil — vyplnovani se stavem
   * odkazu nema nic spolecneho.
   */
  const partiesStatePanel = (
    <section className="reos-ed__block" data-testid="contract-parties-state">
      <span className="reos-field-section__title">
        {t('webAdmin.contracts.editor.partiesHeading', { locale: LOCALE })}
      </span>
      {parties.map((party) => {
        const link = partyLinkStatus(party, invitations);
        return (
          <span className="reos-ed__party" key={`${party.role}-${party.index}`}>
            <span>{partyRoleName(parties, party, LOCALE)}</span>
            <span
              className={`reos-ed__party-state reos-ed__party-state--${link.state}`}
              data-testid={`contract-party-link-state-${party.role}-${party.index}`}
            >
              {t(PARTY_LINK_STATE_KEY[link.state], {
                locale: LOCALE,
                values: { done: link.done, total: link.total },
              })}
              {demoOffice && link.state !== 'no-invite' && (
                <span data-testid={`contract-party-demo-sent-${party.role}-${party.index}`}>
                  {' '}
                  {t('demo.sent_label', { locale: LOCALE })}
                </span>
              )}
            </span>
          </span>
        );
      })}
      {/* Krok UXD1 bod 5: „+ Přidat stranu (spolunájemce, ručitel)" TADY uz
            NENI. Potichu pridavalo stranu POSLEDNI role (u najmu najemce),
            takze „+ Přidat stranu" zalozilo najemce i tam, kde clovek chtel
            druheho pronajimatele — a nova karta se objevila jinde, nez se
            klikalo (R.B. 12. 10. 21:38). Strany se pridavaji VE FORMULARI
            vlevo, tlacitkem pod posledni kartou te sve role. */}
      {/* ODESLANI: dokud ma smlouva chybu, server ji neprijme — tlacitko to
            uz netvrdi jinak (krok SML2 bod 1). */}
      <button
        type="button"
        className="rh-btn rh-btn--primary"
        // Starsi odpoved serveru `complete` nenese; tehdy se tlacitko
        // nezakazuje — admin nema jak vedet, co server rozhodne, a brana
        // je na serveru (viz „Nálezy" v REPORT-step-SML2.md).
        disabled={contract.complete === false}
        onClick={() => void sendToReview()}
        data-testid="contract-send-review"
      >
        {t('webAdmin.contracts.editor.sendReview', { locale: LOCALE })}
      </button>
      {sendNote !== null && (
        <span
          className="reos-field-row__hint"
          role="status"
          data-testid="contract-send-parties-note"
        >
          {sendNote}
        </span>
      )}
      {contract.complete === false && (
        <span className="reos-field-row__hint" data-testid="contract-send-blocked">
          {/*
              Krok UXP4b bod 4 (nalez P2-9) — JEDEN POCET CHYBEJICICH UDAJU.
              Do UXP4b tu stal pocet VSECH blokujicich kontrol (`blockingCount`)
              pod vetou „N chybějících povinných údajů", zatimco odznak vedle
              „K podpisu" ukazoval `plan.missing`. Cisla si odporovala (7 vs. 2)
              a vetsi z nich navic pocitalo i porusena PRAVIDLA (kauce nad
              trojnasobek, parcela mimo LV), ktera chybejici udaj nejsou.
              Pocet je proto JEDEN — tentyz, jaky nese odznak — a kdyz
              odeslani brani pravidlo, rekne se to misto cisla.
            */}
          {plan.missing > 0
            ? t('webAdmin.contracts.editor.sendBlocked', {
                locale: LOCALE,
                values: { count: plan.missing },
              })
            : t('webAdmin.contracts.editor.sendBlockedChecks', { locale: LOCALE })}
        </span>
      )}
      {/*
        Krok APP3 bod 4 (nalez A40): adresa zadana jen ve smlouve se zapise
        klientovi do adresare — a faktura si ji odtud vezme sama. Rozpor
        s adresarem se NEPREPISE tise: panel ho vypise a prepis chce druhe,
        vyslovne kliknuti.
      */}
      <button
        type="button"
        className="rh-btn"
        onClick={() => void syncPartyAddresses(false)}
        data-testid="contract-address-sync"
      >
        {t('webAdmin.contracts.editor.addressSyncButton', { locale: LOCALE })}
      </button>
      <span className="reos-field-row__hint">
        {t('webAdmin.contracts.editor.addressSyncHint', { locale: LOCALE })}
      </span>
      {addressSyncNote !== null && (
        <span className="reos-field-row__hint" role="status" data-testid="contract-address-sync-note">
          {addressSyncNote}
        </span>
      )}
      {(addressSync?.conflicts ?? []).length > 0 && (
        <span className="reos-field-row__hint" role="alert" data-testid="contract-address-sync-conflict">
          {(addressSync?.conflicts ?? []).map((conflict) => (
            <span key={`${conflict.role}-${conflict.index}`}>
              {t('webAdmin.contracts.editor.addressSyncConflict', {
                locale: LOCALE,
                values: {
                  role: t(PARTY_ROLE_LABEL_KEY[conflict.partyKey], { locale: LOCALE }),
                  existing: conflict.existing,
                  address: conflict.address,
                },
              })}{' '}
            </span>
          ))}
          <button
            type="button"
            className="rh-btn"
            onClick={() => void syncPartyAddresses(true)}
            data-testid="contract-address-sync-overwrite"
          >
            {t('webAdmin.contracts.editor.addressSyncOverwrite', { locale: LOCALE })}
          </button>
        </span>
      )}
    </section>
  );

  /**
   * Dokument a vedle nej panel textovych nastroju.
   *
   * Krok SML3: v rezimu „Upravit text" je vpravo panel ZMEN A KLAUZULI —
   * predloha `SmlouvaText.dc.html`.
   *
   * Krok UXD1 bod 6: v rezimu „Vyplnit" uz vpravo NENI zadny panel. Editor
   * ma dve casti: vlevo se vyplnuje, vpravo je nahled smlouvy — uvolnene
   * misto dostal dokument. Panel v rezimu textu ZUSTAVA: je to jedine misto,
   * kudy jde odstavec vratit na mustr nebo vlozit klauzuli kancelare, a v tom
   * rezimu zadny formular vlevo nestoji (CSS ho schovava), takze jsou vedle
   * sebe dve casti i tam (ROZHODNUTI UXD1-A).
   */
  const stagePanel = (
    <div className="reos-ed__stage">
      {documentPanel}
      {editText ? textSidePanel : null}
    </div>
  );

  return (
    <div
      className={`reos-ed${clientPreview ? ' reos-ed--preview' : ''}`}
      data-testid="contract-editor"
      data-mode={mode}
    >
      {/*
        REZIM SOUSTREDENI (krok SML2 bod 1, predloha `Smlouva.dc.html`):
        hlavicka editoru nese zpet, nemovitost, nazev, stav, cas ulozeni,
        prepinac rezimu a primarni „K podpisu" s poctem zbyvajicich udaju.
        Hlavni menu aplikace je na teto adrese schovane — dela to shell podle
        `isFocusModeRoute` (`lib/nav.ts`), ne tahle obrazovka.
      */}
      <header className="reos-ed__head" data-testid="contracts-head">
        <Link to="/smlouvy" className="reos-ed__back" aria-label={t('webAdmin.contracts.editor.focusBack', { locale: LOCALE })}>
          ‹
        </Link>
        {/* Krok UXD1 bod 11 (R.B. 12. 10. 22:22: „vpravo nahoře") — v rezimu
            soustredeni schova shell celou horni listu, takze „Odhlásit"
            muselo prijit sem, viditelne vpravo nahore, ne do nejake nabidky. */}
        <button
          type="button"
          className="reos-ed__logout"
          onClick={handleLogout}
          data-testid="contracts-head-logout"
        >
          <LogOut size={16} strokeWidth={1.75} aria-hidden="true" />
          <span>{t('webAdmin.layout.logout', { locale: LOCALE })}</span>
        </button>
        <div className="reos-ed__ident">
          <span className="reos-ed__sub" data-testid="contracts-head-template">
            {[caseTitle, templateLine].filter((v) => v !== null && v !== '').join(' · ')}
          </span>
          <div className="reos-ed__titleline">
            <h1 className="reos-ed__title" data-testid="contracts-head-title">
              {contract.title}
            </h1>
            <span className="reos-ed__status" data-testid="contract-status-badge">
              {t(editorHeaderState.statusKey, { locale: LOCALE })}
            </span>
            {/*
              Krok APP3 bod 1 (nalez A31): „Podepisuje se" u smlouvy, ve ktere
              zbyva deset udaju, byl rozpor, ktery hlavicka zamlcela — stav
              a pocty mely kazdy svuj zdroj. Cislo je z OSNOVY serveru, tedy
              tentyz zdroj jako „Hotovo X z Y" a „zbývá N" nize.
            */}
            {editorHeaderState.gap !== null && (
              <span className="reos-ed__status-gap" data-testid="contract-status-gap">
                {t('webAdmin.contracts.editor.statusGap', {
                  locale: LOCALE,
                  values: { count: editorHeaderState.gap },
                })}
              </span>
            )}
            {/* Krok SML3 bod 2: POCET ZMEN PROTI MUSTRU v hlavicce (predloha
                „2 změny proti mustru"). Cislo pocita server — tytez zmeny,
                jake vypisuje panel vpravo a jake rika podpisova karta. */}
            {changes.length > 0 && (
              <span className="reos-ed__changes-badge" data-testid="contract-changes-badge">
                {t('webAdmin.contracts.text.changesBadge', {
                  locale: LOCALE,
                  values: { count: changes.length },
                })}
              </span>
            )}
            {savedAt !== null && (
              <span className="reos-ed__saved" data-testid="contract-saved-at">
                {t('webAdmin.contracts.editor.savedAt', { locale: LOCALE, values: { time: savedAt } })}
              </span>
            )}
            {/* Drobecky zustavaji — odkaz na zakazku je jediná cesta zpet do
                prodeje, kdyz je menu schovane.
                Krok SML2b bod 4: smlouva BEZ ZAKAZKY mela v drobecku „Zakázky ›
                Smlouvy", tedy odkaz do seznamu zakazek, se kterym nema nic
                spolecneho, a svuj vlastni nazev nikde. Bez zakazky je drobecek
                „Smlouvy › <nazev smlouvy>". */}
            <span className="reos-crumb" data-testid="contracts-crumb">
              {caseId === null ? (
                <>
                  <Link to="/smlouvy">{t('webAdmin.contracts.title', { locale: LOCALE })}</Link>
                  {' › '}
                  {contract.title}
                </>
              ) : (
                <>
                  <Link to="/zakazky">
                    {t('webAdmin.contracts.crumbCases', { locale: LOCALE })}
                  </Link>
                  {' › '}
                  <Link to="/zakazky/$id" params={{ id: caseId }} search={{ sekce: 'smlouva' }}>
                    {caseTitle ?? t('webAdmin.contracts.crumbCase', { locale: LOCALE })}
                  </Link>
                  {' › '}
                  <Link to="/smlouvy">{t('webAdmin.contracts.title', { locale: LOCALE })}</Link>
                </>
              )}
            </span>
          </div>
        </div>

        {/*
          Krok UXP4b bod 4 (nalez P2-9) — JEDNA RADA AKCI. Do UXP4b stala nad
          dokumentem TRI rady: prepinac rezimu, rada akci (vcetne DRUHEHO
          tlacitka „Upravit text", ktere delalo totez jako zalozka rezimu)
          a zalozky smlouvy. Prepinac a akce jsou od tohoto kroku JEDNA lista
          (`reos-ed__bar`) a duplicitni tlacitko je pryc.
        */}
        <div className="reos-ed__bar">
        <div
          className="reos-ed__modes"
          role="tablist"
          aria-label={t('webAdmin.contracts.editor.modeLabel', { locale: LOCALE })}
          data-testid="contract-modes"
        >
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'fill'}
            className="reos-ed__mode"
            onClick={() => setMode('fill')}
            data-mode="fill"
            data-testid="contract-mode-fill"
          >
            {t('webAdmin.contracts.editor.modeFill', { locale: LOCALE })}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'text'}
            className="reos-ed__mode"
            onClick={() => setMode('text')}
            data-mode="text"
            data-testid="contract-mode-text"
          >
            {t('webAdmin.contracts.editor.modeText', { locale: LOCALE })}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'client'}
            className="reos-ed__mode"
            onClick={() => setMode('client')}
            data-mode="client"
            data-testid="contract-mode-client"
          >
            {t('webAdmin.contracts.editor.modeClient', { locale: LOCALE })}
          </button>
        </div>

        <div className="reos-ed__actions" data-testid="contract-editor-actions">
          <button
            type="button"
            className="rh-btn rh-btn--quiet"
            onClick={() => setCleanView((v) => !v)}
            aria-pressed={cleanView}
            data-testid="contract-clean-view"
          >
            {t(cleanView ? 'webAdmin.contracts.editor.coloredView' : 'webAdmin.contracts.editor.cleanView', {
              locale: LOCALE,
            })}
          </button>
          <button
            type="button"
            className="rh-btn rh-btn--quiet"
            onClick={() => void doPrint()}
            data-testid="contract-print-button"
          >
            {t('webAdmin.contracts.editor.printButton', { locale: LOCALE })}
          </button>
          {/* Zivy pruchod 13. 10. (V3): puvodne volalo stejny `doPrint` jako
              tlacitko „Tisk" — ROZHODNUTI UXS1-B zmeneno briefem step-PRF2,
              „Stáhnout PDF" ted dava SKUTECNY soubor z Gotenbergu. */}
          <button
            type="button"
            className="rh-btn rh-btn--quiet"
            onClick={() => void doDownloadPdf()}
            disabled={pdfState === 'preparing'}
            title={t('webAdmin.contracts.editor.pdfHint', { locale: LOCALE })}
            data-testid="contract-pdf-button"
          >
            {t('webAdmin.contracts.editor.pdfButton', { locale: LOCALE })}
          </button>
          {/* Stav sazby PDF. Prvek je v DOM VZDY a ma rezervovanou vysku
              jednoho radku — tataz uvaha jako u `contract-save-state` nize
              (krok PIL4 bod 2): hlaska, ktera az pri kliknuti VZNIKNE, zvysi
              tuhle listu o radek a posune vsechno pod ni. */}
          <span className="reos-ed__pdfstate" role="status" data-testid="contract-pdf-state" data-state={pdfState}>
            {pdfState === 'preparing'
              ? t('webAdmin.contracts.editor.pdfPreparing', { locale: LOCALE })
              : pdfState === 'error'
                ? t('webAdmin.contracts.editor.pdfDownloadError', { locale: LOCALE })
                : ''}
          </span>
          {/* Primarni akce predlohy: K PODPISU s poctem zbyvajicich udaju.
              Je to PRECHOD na podpisovou cast, ne odeslani — odesilat smi
              teprve uplna smlouva (tlacitko „Odeslat stranám" v panelu
              Strany, ktere server kontroluje toutez funkci). */}
          <button
            type="button"
            className="rh-btn rh-btn--primary reos-ed__tosign"
            onClick={() => setTab('signature')}
            data-testid="contract-to-signature"
          >
            {t('webAdmin.contracts.editor.toSignature', { locale: LOCALE })}
            <span
              className={`reos-ed__badge${plan.missing === 0 ? ' reos-ed__badge--ready' : ''}`}
              data-testid="contract-remaining-badge"
            >
              {plan.missing === 0
                ? t('webAdmin.contracts.editor.readyBadge', { locale: LOCALE })
                : t('webAdmin.contracts.editor.remainingBadge', {
                    locale: LOCALE,
                    values: { count: plan.missing },
                  })}
            </span>
          </button>
          {/* Krok PIL4 bod 2: misto pro hlasku o ulozeni je v DOM VZDY a ma
              rezervovanou vysku jednoho radku. `SaveStatus` se z `idle`
              nevykresluje vubec, takze jeho vznik pri ukladani zvysil tuhle
              listu o radek a posunul VSECHNO pod ni — vcetne prepinace
              „Formulář · Dokument". Klik mireny na souradnice z okamziku
              stisku pak dopadl vedle a prepnuti se „ztratilo" (tataz trida
              vady jako FIX7 nalez 2 u `.reos-doc-head__actions`). */}
          <span className="reos-ed__savestate" data-testid="contract-save-state">
            <SaveStatus
              status={sendStatus === 'idle' ? status : sendStatus}
              texts={{
                saving: t('webAdmin.contracts.saving', { locale: LOCALE }),
                saved: t('webAdmin.contracts.saved', { locale: LOCALE }),
                error: t('webAdmin.contracts.loadError', { locale: LOCALE }),
              }}
            />
          </span>
        </div>
        </div>
      </header>

      {/* Zalozky JEDNE smlouvy (krok UXS1): Editor / Odeslat k podpisu / Klient. */}
      <div className="reos-tabs reos-tabs--underline" role="tablist" data-testid="contracts-tabs">
        {EDITOR_TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            className="reos-tabs__tab"
            aria-selected={tab === item.id}
            onClick={() => setTab(item.id)}
            data-testid={`contracts-tab-${item.id}`}
          >
            {t(item.key, { locale: LOCALE })}
          </button>
        ))}
      </div>

      {tab === 'editor' && (
        <SplitEditorLayout
          editor={outlinePanel}
          preview={stagePanel}
          mobileEditLabel={t('webAdmin.contracts.editor.mobileForm', { locale: LOCALE })}
          mobilePreviewLabel={t('webAdmin.contracts.editor.mobileDocument', { locale: LOCALE })}
        />
      )}
      {tab === 'signature' && (
        <ContractSignatureView
          contractId={contractId}
          onBack={() => setTab('editor')}
          /*
           * Krok TOKYFIX1 (prekazka P6/2): hlaska brany vede PRIMO na volbu —
           * prepne na „Vyplnit" a posune na oddil „Volby v textu". Oddil se
           * vykresli az po prepnuti zalozky, proto az dalsi snimek.
           */
          onOpenChoices={() => {
            setTab('editor');
            requestAnimationFrame(() => {
              document
                .querySelector<HTMLElement>('[data-testid="contract-section-choices"]')
                ?.scrollIntoView({ block: 'center' });
            });
          }}
        />
      )}
      {/* Krok KP1: zalozka „Klient (magic link)" — panel s casovou osou
          odkazu, prevzeti udaju do teto smlouvy a nahledem, co klient dostane. */}
      {tab === 'client' && (
        <div className="reos-client-grid" data-testid="contract-client-tab">
          <div>
            {/* Krok UXD1 bod 6: stav odkazu stran a „Odeslat stranám ke
                kontrole" stoji TADY, kde se odkaz posila — ne v editoru
                vyplnovani. */}
            {partiesStatePanel}
            <ClientIntakeScreen
              contractId={contractId}
              {...(caseId !== null ? { caseId } : {})}
              // Krok UXP3 (P1-6): formular se uz neptá na jmeno a e-mail,
              // ktere strana smlouvy nese.
              prefill={partyToPrefill(parties, invitations)}
            />
            <p className="reos-list-note" data-testid="contract-client-privacy">
              {t('webAdmin.contracts.client.privacyNote', { locale: LOCALE })}
            </p>
          </div>
          <aside>
            <p className="reos-field-section__title">
              {t('webAdmin.contracts.client.phonePreview', { locale: LOCALE })}
            </p>
            <ClientPhonePreview contractTitle={contract.title} />
          </aside>
        </div>
      )}
    </div>
  );
}

/**
 * Lhuty ze smlouvy jako ukoly zakazky s terminem (krok T5, bod 6 auditu
 * mockupu). Do T4 poznamka pod seznamem smluv tvrdila, ze se lhuty propisuji
 * do ukolu a kalendare — produkt to neumel. Ted to umi: server je pocita
 * z dat smlouvy (`deadlines.ts`), tahle sekce je zaklada a odskrtava.
 *
 * Kalendar je samostatna obrazovka (CRM3); do te doby je lhuta ukol s datem —
 * presne jak to zadal R.B.
 */
function ContractDeadlinesSection({
  contractId,
}: {
  readonly contractId: string;
}): React.JSX.Element {
  const [rows, setRows] = React.useState<ContractDeadlineDto[] | null>(null);
  const [status, setStatus] = React.useState<SaveStatusKind>('idle');

  React.useEffect(() => {
    let cancelled = false;
    // Krok FLK1: uvodni nacteni jen DOPLNI prazdno. Kdyz makler mezitim
    // klikl „Založit" a odpoved prisla driv, starsi seznam ji nesmi prepsat
    // (pod zatezi brany flaky `contracts.spec.tsx` › „tlacitko zalozi ukoly").
    void listContractDeadlines(contractId).then(
      (items) => { if (!cancelled) setRows((current) => current ?? items); },
      () => { if (!cancelled) setRows((current) => current ?? []); },
    );
    return () => { cancelled = true; };
  }, [contractId]);

  async function create(): Promise<void> {
    setStatus('saving');
    try {
      setRows(await syncContractDeadlines(contractId));
      setStatus('saved');
    } catch {
      setStatus('error');
    }
  }

  async function toggle(row: ContractDeadlineDto): Promise<void> {
    const next = row.status === 'done' ? 'open' : 'done';
    const updated = await setContractDeadlineStatus(row.id, next);
    setRows((current) => (current ?? []).map((r) => (r.id === updated.id ? updated : r)));
  }

  return (
    <div className="reos-field-section" data-testid="contract-deadlines">
      <p className="reos-field-section__title">
        {t('webAdmin.contracts.editor.deadlinesHeading', { locale: LOCALE })}
      </p>
      <button
        type="button"
        className="rh-btn"
        onClick={() => void create()}
        data-testid="contract-deadlines-create"
      >
        {t('webAdmin.contracts.editor.deadlinesCreate', { locale: LOCALE })}
      </button>
      <SaveStatus
        status={status}
        texts={{
          saving: t('webAdmin.contracts.saving', { locale: LOCALE }),
          saved: t('webAdmin.contracts.editor.deadlinesCreated', { locale: LOCALE }),
          error: t('webAdmin.contracts.loadError', { locale: LOCALE }),
        }}
      />
      {rows !== null && rows.length === 0 && (
        <p data-testid="contract-deadlines-empty">
          {t('webAdmin.contracts.editor.deadlinesEmpty', { locale: LOCALE })}
        </p>
      )}
      {rows !== null && rows.length > 0 && (
        <ul data-testid="contract-deadlines-list">
          {rows.map((row) => (
            <li key={row.id} data-testid={`contract-deadline-${row.kind}`}>
              <span className={row.status === 'done' ? 'reos-check--ok' : undefined}>
                {row.title} — {formatDueDate(row.dueDate)}
              </span>{' '}
              <button
                type="button"
                className="rh-btn"
                onClick={() => void toggle(row)}
                data-testid={`contract-deadline-toggle-${row.kind}`}
              >
                {t(
                  row.status === 'done'
                    ? 'webAdmin.contracts.editor.deadlineReopen'
                    : 'webAdmin.contracts.editor.deadlineDone',
                  { locale: LOCALE },
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// -------------------------------------------------------------------- podpis

/**
 * PODPISY JEDNE SMLOUVY — zalozka „Odeslat k podpisu" (`?zalozka=signature`).
 *
 * Krok SML1 bod 3, predloha `docs/design/ux-v3/SmlouvaPodpis.dc.html` ve
 * vzhledu UX v4: v hlavicce nemovitost a postup zakazky (predloha v4
 * § Pravidla 3 a 5 — „obrázek a progress bar všude, kde se pracuje se
 * zakázkou"), pak poradi stran se stavem a casem podpisu, u strany na rade
 * karta s hlavnimi podminkami ZE SMLOUVY, souhlas a podpis, a vpravo dukaz
 * podpisu, historie a „Zrušit podepisování".
 *
 * Poradi, stav, pecet, dukazni balicek i zastaveni resi `SignaturePanel` —
 * ten uz nad skutecnym API stoji od kroku ES1 a tahle obrazovka mu dodava
 * jen to, co vi o SMLOUVE (strany s kontakty a hlavni podminky).
 */
/**
 * Veta o vysledku „Odeslat stranám" (krok UXP3, P1-6).
 *
 * Oprava e2e 2: kdyz se nepodarilo zmenit STAV smlouvy, nesmi veta tvrdit
 * „Stav smlouvy je „Ke kontrole““ — rekne se duvod a kolik odkazu presto
 * odeslo.
 */
function partyInviteNote(outcome: PartyInviteOutcome, transitionError: string | null): string {
  if (transitionError !== null) {
    return t('webAdmin.contracts.signature.sentStatusFailed', {
      locale: LOCALE,
      values: { reason: transitionError, count: outcome.created },
    });
  }
  if (outcome.error !== null) {
    return t('webAdmin.contracts.signature.sentInvitesError', {
      locale: LOCALE,
      values: { reason: outcome.error },
    });
  }
  if (outcome.created > 0) {
    return t('webAdmin.contracts.signature.sentInvites', {
      locale: LOCALE,
      values: { count: outcome.created },
    });
  }
  if (outcome.alreadyInvited > 0 && outcome.withoutEmail === 0) {
    return t('webAdmin.contracts.signature.sent', { locale: LOCALE });
  }
  return t('webAdmin.contracts.signature.sentNoInvites', { locale: LOCALE });
}

/**
 * Krok KLIKFIX1 (vada V6) — NEULOZENY PRECHOD STAVU NESMI NIC ODESLAT.
 *
 * Klikaci test 29. 10.: u neuplne smlouvy spadl prechod na 422
 * (`nemovitost.parcely[].cislo`), hlaska slibila „Zkuste odeslání za chvíli
 * zopakovat." — a klientce PRESTO vznikl odkaz k doplneni (stav „Odesláno").
 * Odeslany odkaz se vzit zpatky neda, takze se pozvanky zakladaji teprve
 * tehdy, kdyz smlouva do stavu opravdu presla.
 *
 * Jedno misto pro OBA panely („Odeslat stranám ke kontrole" v editoru
 * i „Odeslat stranám" v panelu podpisu) — do tohoto kroku nesly dva opsane
 * bloky a vada byla v obou.
 */
async function transitionAndInviteParties(input: {
  readonly contractId: string;
  readonly current: ContractDetailDto;
  readonly next: ContractStatusValue;
  readonly invitations: readonly PartyInvitationRef[];
}): Promise<{
  readonly contract: ContractDetailDto;
  readonly note: string;
  readonly ok: boolean;
  readonly created: number;
}> {
  const {
    contract: updated,
    error: transitionError,
    incomplete,
  } = await transitionIfNeeded(input.contractId, input.current, input.next);
  if (transitionError !== null && incomplete) {
    const nothingSent: PartyInviteOutcome = {
      created: 0,
      alreadyInvited: 0,
      withoutEmail: 0,
      error: null,
    };
    return {
      contract: updated,
      note: partyInviteNote(nothingSent, transitionError),
      ok: false,
      created: 0,
    };
  }
  const outcome = await inviteMissingParties({
    contractId: input.contractId,
    caseId: updated.caseId ?? null,
    parties: partiesOf(updated.fieldValues, partyRolesFor(updated.contractType, updated)),
    invitations: input.invitations,
  });
  return {
    contract: updated,
    note: partyInviteNote(outcome, transitionError),
    ok: transitionError === null,
    created: outcome.created,
  };
}

function ContractSignatureView({
  contractId,
  onBack,
  onOpenChoices,
}: {
  readonly contractId: string;
  readonly onBack: () => void;
  /** Krok TOKYFIX1 (prekazka P6/2): odkaz z hlasky brany na volbu v textu. */
  readonly onOpenChoices: () => void;
}): React.JSX.Element {
  const [contract, setContract] = React.useState<ContractDetailDto | null>(null);
  const [catalogs, setCatalogs] = React.useState<ContractCatalogsDto | null>(null);
  const [cover, setCover] = React.useState<CoverInfo | null>(null);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [status, setStatus] = React.useState<SaveStatusKind>('idle');
  // Kanal, kterym se odkaz k podpisu posle. Vychozi je „zkopirovat odkaz“:
  // bez nakonfigurovaneho odesilani by se smlouva tvarila, ze je odeslana,
  // kdyz se nikomu neposlala (viz ROZHODNUTI ES1 v runtime-config.ts).
  const [channel] = React.useState<'email' | 'sms' | 'odkaz'>('odkaz');
  /** Krok UXP3 (P1-6): PRAVDA o tom, co se stranam skutecne odeslalo. */
  const [sendNote, setSendNote] = React.useState<string | null>(null);
  const [invitations, setInvitations] = React.useState<readonly PartyInvitationRef[]>([]);

  React.useEffect(() => {
    void getContract(contractId)
      .then(setContract)
      .catch((err: unknown) => setError(errorMessage(err)));
    void getContractCatalogs().then(setCatalogs);
  }, [contractId]);

  /**
   * Fotka nemovitosti — jedna komponenta pro cely admin (`loadCaseCovers`).
   * Smlouva bez nemovitosti nema co dohledavat, takze se o nic nezada.
   */
  React.useEffect(() => {
    const propertyId = contract?.propertyId ?? null;
    let cancelled = false;
    if (propertyId !== null) {
      void loadCaseCovers([propertyId]).then(
        (covers) => { if (!cancelled) setCover(covers.get(propertyId) ?? null); },
        () => undefined,
      );
    }
    return () => { cancelled = true; };
  }, [contract?.propertyId]);

  React.useEffect(() => {
    let cancelled = false;
    void listClientInvitations({ contractId })
      .then((items) => { if (!cancelled) setInvitations(items); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [contractId]);

  /**
   * Krok UXP3 (P1-6): „Odeslat stranám" uz nehlasi uspech za samotny prechod
   * stavu — zaklada pozvanku kazde strane, ktera ji nema a ma e-mail, a vysledek
   * rekne tak, jak je (vcetne „nikomu neodeslo, strany nemaji e-mail").
   */
  async function send(): Promise<void> {
    setStatus('saving');
    setSendNote(null);
    try {
      const current = contract;
      if (current === null) throw new Error('contract not loaded');
      const next: ContractStatusValue =
        current.status === 'draft'
          ? 'ready_to_send'
          : current.status === 'ready_to_send'
            ? 'awaiting_signature'
            : 'awaiting_signature';
      const result = await transitionAndInviteParties({
        contractId,
        current,
        next,
        invitations,
      });
      setContract(result.contract);
      setSendNote(result.note);
      if (result.created > 0) {
        await listClientInvitations({ contractId }).then(setInvitations, () => undefined);
      }
      // Viz `sendToReview` v editoru: pri neuspesnem prechodu mluvi poznamka.
      setStatus(result.ok ? 'saved' : 'idle');
    } catch (err) {
      setStatus('error');
      setError(errorMessage(err));
    }
  }

  /* Tataz znacka jako v `ContractEditorScreen` vyse (krok QAFIX7): pad JEDNE
     obrazovky ma v adminu testid `*-load-error`, jinak o nem sada nevi. */
  if (error !== undefined && contract === null) {
    return (
      <p role="alert" data-testid="contract-load-error">
        {error.trim() === '' ? t('webAdmin.contracts.editor.loadError', { locale: LOCALE }) : error}
      </p>
    );
  }
  if (contract === null) return <p>{t('webAdmin.contracts.loading', { locale: LOCALE })}</p>;

  const parties = partiesOf(contract.fieldValues, partyRolesFor(contract.contractType, contract));
  // Strany pro panel podpisu (krok ES1): jmeno a kontakt z tehoz slovniku, aby
  // makler nepsal kontakt dvakrat (raz v tabulce, raz pri zalozeni sezeni).
  //
  // ES2 bod 2: `contact` mel jako fallback TEXT TLACITKA („Poslat odkaz“) a
  // panel z nej skladal `name` strany — ten text pak odesel do API a vratil se
  // jako jmeno podepisujiciho (`"name":"Poslat odkaz"`). Kontakt je proto bud
  // skutecny kontakt, nebo `null`; popisek do tabulky si sklada obrazovka.
  const partiesForPanel = parties.map((p) => ({
    role: p.role,
    index: p.index,
    name: partyDisplayName(p),
    email: p.values.email === '' ? null : p.values.email,
    phone: p.values.telefon === '' ? null : p.values.telefon,
  }));
  const subjectLabel =
    contract.subjectType === null
      ? null
      : (catalogs?.subjectTypes.find((s) => s.key === contract.subjectType)?.label ?? null);
  const summary = clientSummary(contract, subjectLabel);
  // Krok SML1 bod 3: hlavni podminky Z POLI SMLOUVY (ne natvrdo v UI).
  const conditions = signingConditions(contract);
  const missing = parties.some((p) => partyDisplayName(p) === null);
  const { errors } = contractCounts(contract);
  /**
   * Krok KLIKFIX1 (vada V6): smlouva, kterou server k podpisu neprijme.
   *
   * Branou je JEN `signing.ready` — odpoved serveru z tychz kontrol, ktere
   * prechod stavu odmitnou s 422 (klikaci test 29. 10. ji mel `false`
   * a tlacitko presto svitilo). Strana BEZ UDAJU gate neni: te se ma odkaz
   * k doplneni poslat prave timhle tlacitkem (`sendHintMissing`), proto
   * `missing` ani `errors` tlacitko nezhasina.
   */
  const notReady = contract.signing?.ready === false;
  const propertyLine = contractPropertyLine(contract, null);
  // Krok APP3 bod 1: stav i rozpor s osnovou jednou funkci (SSOT hlavicky).
  const headerState = contractHeaderState({
    status: contract.status,
    missing: contract.fillPlan.missing,
  });

  return (
    <div className="rh-v4-sigpage" data-testid="contract-signature">
      <V4NavBar
        testId="contract-signature-nav"
        back={
          <button type="button" onClick={onBack} data-testid="contract-signature-back">
            {`‹ ${t('webAdmin.contracts.backToEditor', { locale: LOCALE })}`}
          </button>
        }
      />

      {/* Hlavicka predlohy: nemovitost, nazev dokumentu a stav smlouvy. */}
      <div className="rh-v4-sigpage__head">
        <span className="rh-v4-thumb rh-v4-thumb--row" aria-hidden="true">
          <PropertyImage
            photoUrl={cover?.thumbUrl ?? null}
            propertyId={cover?.propertyId ?? null}
            lat={cover?.lat ?? null}
            lon={cover?.lon ?? null}
            size={{ width: 112, height: 112 }}
            testIdPrefix="contract-signature-thumb"
          />
        </span>
        <div className="rh-v4-sigpage__texts">
          <span className="rh-v4-crow__sub" data-testid="contract-signature-property">
            {propertyLine.missing
              ? t('webAdmin.contracts.list.propertyMissing', { locale: LOCALE })
              : propertyLine.text}
          </span>
          <h2 className="rh-v4-head__title">{contract.title}</h2>
          <span className="rh-v4-crow__meta">
            {t(TYPE_KEY[contract.contractType], { locale: LOCALE })}
            {' · '}
            {t(headerState.statusKey, { locale: LOCALE })}
            {/*
              Krok APP3 bod 1 (nalez A31): stav „Podepisuje se" vedle devíti
              nevyplnenych udaju neni vada pocitadla, ale rozpor, ktery ma
              hlavicka POJMENOVAT. Cislo jde z teze osnovy jako „zbývá N"
              v editoru a jako veta u brany podpisu.
            */}
            {headerState.gap !== null && (
              <>
                {' · '}
                <span data-testid="contract-signature-status-gap">
                  {t('webAdmin.contracts.editor.statusGap', {
                    locale: LOCALE,
                    values: { count: headerState.gap },
                  })}
                </span>
              </>
            )}
          </span>
          {/* Krok EDIT2 bod 10 (nalez K2): tady STAVAL pruh zakazky
              („20 % · do inzerce chybí 3 kroky"). Je to pripravenost
              INZERCE, ne stav smlouvy — na obrazovce „Odeslat k podpisu"
              cetla makleryne procenta jako stav podepisovani. Stav smlouvy
              rika stitek nad timhle radkem a panel pripravenosti pod nim. */}
        </div>
      </div>

      <p className="rh-v4-sig__note" data-testid="contract-signature-perex">
        {t('webAdmin.contracts.signature.perex', { locale: LOCALE })}
      </p>

      {/* Krok ES1: misto prepinace „uroven podpisu“ (ktery byl jen vizualni
          a nic neposilal) je tady panel nad skutecnym podepisovanim — politika
          urovni, poradi stran, stav, pecet, dukazni balicek, historie
          a zastaveni. Uroven se VOLIT neda: politika ji rozhodne (viz
          `signature-api.ts` — v requestu takove pole ani neni). */}
      <SignaturePanel
        contractId={contractId}
        parties={partiesForPanel}
        channel={channel}
        conditions={conditions}
        // Krok PIL3 bod 2 (F03): pripravenost pocita SERVER, panel ji jen cte.
        signing={contract.signing}
        // Krok APP3 bod 1 (A31): TATAZ osnova, z ktere je „zbývá N" u tlacitka.
        fillPlan={contract.fillPlan}
        /*
         * Krok TOKYFIX1 (prekazka P6/2): z hlasky brany vede odkaz PRIMO na
         * volbu — zalozka „Vyplnit" a posun na oddil „Volby v textu".
         */
        onOpenChoices={onOpenChoices}
      />

      {/* „Co podepisujete" pro klienta — generovane ZE SMLOUVY (bod 17 auditu). */}
      <V4Group testId="contract-signature-summary">
        <div className="rh-v4-card">
          <div className="rh-v4-h2">
            {t('webAdmin.contracts.signature.summaryHeading', { locale: LOCALE })}
          </div>
          {summary.length === 0 ? (
            <p className="rh-v4-sig__note">
              {t('webAdmin.contracts.signature.summaryNoData', { locale: LOCALE })}
            </p>
          ) : (
            summary.map((s) => (
              <div key={s.key}>
                <span className="rh-v4-crow__meta">{t(s.titleKey, { locale: LOCALE })}</span>
                <ul className="rh-v4-sig__list">
                  {s.lines.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </div>
      </V4Group>

      <div className="rh-v4-sig__acts">
        <button
          type="button"
          className="rh-v4-btn rh-v4-btn--filled"
          // Krok KLIKFIX1 (vada V6): u neuplne smlouvy uz tlacitko neslibuje
          // odeslani, ktere server stejne odmitne (422). Branou je ODPOVED
          // SERVERU (`signing.ready`), ne vypocet administrace; starsi
          // odpoved pole nenese, tam se tlacitko nezhasina.
          disabled={notReady}
          onClick={() => void send()}
          data-testid="contract-signature-send"
        >
          {t('webAdmin.contracts.signature.sendButton', { locale: LOCALE })}
        </button>
        <SaveStatus
          status={status}
          texts={{
            saving: t('webAdmin.contracts.saving', { locale: LOCALE }),
            saved: t('webAdmin.contracts.signature.sent', { locale: LOCALE }),
            error: t('webAdmin.contracts.loadError', { locale: LOCALE }),
          }}
        />
      </div>
      {sendNote !== null && (
        <p className="rh-v4-sig__note" role="status" data-testid="contract-send-parties-note">
          {sendNote}
        </p>
      )}
      {/* Krok KLIKFIX1 (vada V6): proc je tlacitko zhasnute. */}
      {notReady && (
        <p className="rh-v4-sig__note" data-testid="contract-signature-send-blocked">
          {t('webAdmin.contracts.signature.sendBlocked', { locale: LOCALE })}
        </p>
      )}
      <p className="rh-v4-sig__note" data-testid="contract-signature-hint">
        {errors > 0 || missing
          ? t('webAdmin.contracts.signature.sendHintMissing', { locale: LOCALE })
          : t('webAdmin.contracts.signature.sendHintReady', { locale: LOCALE })}
      </p>
    </div>
  );
}

// -------------------------------------------------------------------- klient

/**
 * Náhled telefonu, co klient dostane (mockup, pravý sloupec obrazovky
 * „Klient (magic link)").
 *
 * ROZHODNUTI T4-7: je to ZOBRAZENI, ne druhy mechanismus odesilani. Obsah
 * se sklada ze stejnych udaju, kterymi se krmi `ClientIntakeScreen` (nazev
 * smlouvy, seznam kroku), aby makler videl, co klient obdrzi, aniz by se
 * vytvarel druhy vypocet stavu. Skutecne odeslani resi KP1/ES1.
 */
function ClientPhonePreview({
  contractTitle,
}: {
  readonly contractTitle: string | null;
}): React.JSX.Element {
  /*
    Krok UXP1 (nalez P1-6): v zahlavi nahledu „Co klient dostane" stala
    ZNACKA CIZI KANCELARE — `webAdmin.contracts.client.brand` byla v katalogu
    natvrdo „MV Reality", takze i v DEMO kancelari videl makler cizi firmu.
    Znacka je DATO prihlaseni (nazev kancelare), ne text katalogu
    (CLAUDE.md §1 bod 5); kdyz nazev neznáme, stoji tu znacka produktu.
  */
  const tenantName = useSession((s) => s.tenant?.name ?? null);
  const brand =
    tenantName !== null && tenantName.trim() !== ''
      ? tenantName
      : t('webAdmin.contracts.client.brand', { locale: LOCALE });
  const steps = [
    t('webAdmin.contracts.client.whatData', { locale: LOCALE }),
    t('webAdmin.contracts.client.whatSign', { locale: LOCALE }),
  ];
  return (
    <div className="reos-phone" data-testid="contract-client-phone">
      <div className="reos-phone__top">
        <b data-testid="contract-client-phone-brand">{brand}</b>
        <div>{contractTitle ?? t('webAdmin.contracts.tabs.list', { locale: LOCALE })}</div>
      </div>
      <div className="reos-phone__steps" aria-hidden="true">
        <span className="reos-phone__step reos-phone__step--now" />
        <span className="reos-phone__step" />
      </div>
      <div className="reos-phone__body">
        <h4 className="reos-phone__title">{t('webAdmin.contracts.client.heading', { locale: LOCALE })}</h4>
        <ol className="reos-phone__list">
          {steps.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ol>
        <p className="reos-phone__note">{t('webAdmin.contracts.client.privacyNote', { locale: LOCALE })}</p>
      </div>
    </div>
  );
}

// ------------------------------------------------------------- sablony kancelare

export function OfficeTemplatesScreen({
  embedded = false,
}: {
  /** Vlozeno jako zalozka „Sablony kancelare" (mockup) misto cele stranky. */
  readonly embedded?: boolean;
} = {}): React.JSX.Element {
  const [view, setView] = React.useState<'list' | 'fromDemo' | 'import' | 'editMapping'>('list');
  const [templates, setTemplates] = React.useState<OfficeTemplateDto[] | null>(null);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [editTemplateId, setEditTemplateId] = React.useState<string | null>(null);

  React.useEffect(() => {
    void listOfficeTemplates().then(setTemplates);
  }, [refreshKey]);

  return (
    <div className={embedded ? '' : 'p-6'} data-testid="office-templates-page">
      {!embedded && (
        <h1 className="mb-1 text-xl font-bold">
          {t('webAdmin.contracts.templates.title', { locale: LOCALE })}
        </h1>
      )}
      <p className="mb-4 max-w-3xl text-sm text-gray-600">
        {t('webAdmin.contracts.templates.perex', { locale: LOCALE })}
      </p>

      {view === 'list' && (
        <div>
          {/*
            * Krok UXF4 bod 10 (#32): tri tlacitka vedle sebe, z nichz DVE
            * („Nahrát smlouvu (Word)" a „Import vlastní šablony") volaly
            * tentyz `setView('import')` — audit to popsal jako „nemají
            * vysvětlený rozdíl", protoze zadny nebyl. Ted je JEDNO hlavni
            * CTA „Přidat šablonu" a v nem VOLBA ZDROJE s popisem u kazde
            * moznosti. `<details>` je tentyz vzor jako technicke rozbaleni
            * v `portaly.tsx` — zadna nova komponenta.
            */}
          <details className="mb-3" data-testid="templates-add">
            <summary className="rh-btn rh-btn--primary inline-block cursor-pointer" data-testid="templates-add-button">
              {t('webAdmin.contracts.templates.addButton', { locale: LOCALE })}
            </summary>
            <p className="mt-2 text-sm text-gray-600">
              {t('webAdmin.contracts.templates.addHint', { locale: LOCALE })}
            </p>
            <div className="mt-2 flex flex-col gap-3">
              <div>
                <button
                  type="button"
                  className="rh-btn"
                  onClick={() => setView('import')}
                  data-testid="templates-upload-button"
                >
                  {t('webAdmin.contracts.templates.uploadButton', { locale: LOCALE })}
                </button>
                <p className="mt-1 text-sm text-gray-600" data-testid="templates-upload-hint">
                  {t('webAdmin.contracts.templates.uploadHint', { locale: LOCALE })}
                </p>
              </div>
              <div>
                <button
                  type="button"
                  className="rh-btn"
                  onClick={() => setView('fromDemo')}
                  data-testid="templates-from-demo-button"
                >
                  {t('webAdmin.contracts.templates.newFromDemoButton', { locale: LOCALE })}
                </button>
                <p className="mt-1 text-sm text-gray-600" data-testid="templates-from-demo-hint">
                  {t('webAdmin.contracts.templates.newFromDemoHint', { locale: LOCALE })}
                </p>
              </div>
            </div>
          </details>
          {templates !== null && templates.length === 0 && (
            <EmptyState title={t('webAdmin.contracts.templates.empty', { locale: LOCALE })} />
          )}
          {templates !== null && templates.length > 0 && (
            <ListTable
              testId="office-templates-table"
              caption={t('webAdmin.contracts.templates.title', { locale: LOCALE })}
              rows={templates}
              rowKey={(tpl) => tpl.id}
              rowTestId={(tpl) => `office-template-row-${tpl.id}`}
              columns={[
                {
                  key: 'name',
                  label: t('webAdmin.contracts.templates.colName', { locale: LOCALE }),
                  cell: (tpl) => tpl.name,
                },
                {
                  key: 'type',
                  label: t('webAdmin.contracts.templates.colType', { locale: LOCALE }),
                  cell: (tpl) => t(TYPE_KEY[tpl.contractType], { locale: LOCALE }),
                },
                {
                  key: 'fields',
                  label: t('webAdmin.contracts.templates.colFields', { locale: LOCALE }),
                  cell: (tpl) => (
                    <span data-testid={`office-template-fields-${tpl.id}`}>
                      {t('webAdmin.contracts.new.fieldsCount', {
                        locale: LOCALE,
                        values: { count: officeTemplateFieldCount(tpl) },
                      })}
                    </span>
                  ),
                },
                {
                  key: 'version',
                  label: t('webAdmin.contracts.templates.colVersion', { locale: LOCALE }),
                  cell: (tpl) =>
                    t('webAdmin.contracts.templates.versionShort', {
                      locale: LOCALE,
                      values: { version: tpl.version },
                    }),
                },
                {
                  // Krok UXD3 bod 2: volba „Vyplněné údaje tučně" na VZORU.
                  // Plati pro smlouvy, ktere ze vzoru teprve vzniknou — uz
                  // rozdelana smlouva si volbu drzi sama (ROZHODNUTI UXD3-A),
                  // aby se sazba dokumentu nezmenila pod rukama.
                  key: 'bold',
                  label: t('webAdmin.contracts.templates.boldFilledFields', { locale: LOCALE }),
                  cell: (tpl) => (
                    <input
                      type="checkbox"
                      checked={tpl.boldFilledFields !== false}
                      aria-label={t('webAdmin.contracts.templates.boldFilledFields', {
                        locale: LOCALE,
                      })}
                      onChange={(e) => {
                        const next = e.target.checked;
                        setTemplates(
                          (prev) =>
                            prev?.map((x) =>
                              x.id === tpl.id ? { ...x, boldFilledFields: next } : x,
                            ) ?? prev,
                        );
                        void setOfficeTemplateBoldFilledFields(tpl.id, next).catch(() =>
                          setRefreshKey((k) => k + 1),
                        );
                      }}
                      data-testid={`office-template-bold-${tpl.id}`}
                    />
                  ),
                },
                {
                  key: 'actions',
                  label: t('webAdmin.contracts.list.colActions', { locale: LOCALE }),
                  srOnlyLabel: true,
                  cell: (tpl) => (
                    <button
                      type="button"
                      className="rh-btn"
                      onClick={() => {
                        setEditTemplateId(tpl.id);
                        setView('editMapping');
                      }}
                      data-testid={`office-template-mapping-${tpl.id}`}
                    >
                      {t('webAdmin.contracts.templates.actionMapping', { locale: LOCALE })}
                    </button>
                  ),
                },
              ]}
            />
          )}
          <ClauseLibrarySection />
          <CustomSubjectsSection />
        </div>
      )}
      {view === 'fromDemo' && (
        <FromDemoForm
          onDone={() => {
            setRefreshKey((k) => k + 1);
            setView('list');
          }}
          onCancel={() => setView('list')}
        />
      )}
      {view === 'import' && (
        <ImportWizard
          onDone={() => {
            setRefreshKey((k) => k + 1);
            setView('list');
          }}
          onCancel={() => setView('list')}
        />
      )}
      {view === 'editMapping' && editTemplateId !== null && (
        <TemplateMappingEditor
          templateId={editTemplateId}
          onDone={() => {
            setRefreshKey((k) => k + 1);
            setEditTemplateId(null);
            setView('list');
          }}
          onCancel={() => {
            setEditTemplateId(null);
            setView('list');
          }}
        />
      )}
    </div>
  );
}

/**
 * „Upravit mapování“ (krok T6 bod 1) — tlacitko Mapování na karte sablony
 * dřív otvíralo prazdny import (nova nesouvisejici sablona); ted ukaze pole,
 * ktera sablona SKUTECNE pouziva (`templateFieldKeys`), a jejich klic jde
 * prejmenovat nebo odebrat. Ulozeni jde pres existujici verzovani
 * (`createOfficeTemplate` se stejnym nazvem supersedne aktualni verzi) —
 * zadny novy endpoint, zadna nova DB zmena.
 */
function TemplateMappingEditor({
  templateId,
  onDone,
  onCancel,
}: {
  readonly templateId: string;
  readonly onDone: () => void;
  readonly onCancel: () => void;
}): React.JSX.Element {
  const [tpl, setTpl] = React.useState<OfficeTemplateDto | null>(null);
  const [articles, setArticles] = React.useState<ContractArticleDto[]>([]);
  const [drafts, setDrafts] = React.useState<Record<string, string>>({});
  const [status, setStatus] = React.useState<SaveStatusKind>('idle');

  React.useEffect(() => {
    void getOfficeTemplate(templateId).then((loaded) => {
      setTpl(loaded);
      setArticles(loaded.articles as ContractArticleDto[]);
    });
  }, [templateId]);

  const fieldKeys = React.useMemo(() => templateFieldKeys(articles), [articles]);

  function rename(oldKey: string): void {
    const newKey = (drafts[oldKey] ?? oldKey).trim();
    if (newKey === '' || newKey === oldKey) return;
    setArticles((current) => renameTemplateField(current, oldKey, newKey));
    setDrafts((current) => {
      const next = { ...current };
      delete next[oldKey];
      return next;
    });
  }

  function remove(key: string): void {
    setArticles((current) => removeTemplateField(current, key));
  }

  async function save(): Promise<void> {
    if (tpl === null) return;
    setStatus('saving');
    try {
      await createOfficeTemplate({
        name: tpl.name,
        contractType: tpl.contractType,
        articles,
        repeatBlocks: [...tpl.repeatBlocks],
        repeatItemTemplate: { ...tpl.repeatItemTemplate },
        mapping: tpl.mapping.map((m) => ({ ...m })),
      });
      setStatus('saved');
      onDone();
    } catch {
      setStatus('error');
    }
  }

  if (tpl === null) {
    return <p>{t('webAdmin.contracts.loading', { locale: LOCALE })}</p>;
  }

  return (
    <FormSection
      title={t('webAdmin.contracts.templates.editMapping.heading', {
        locale: LOCALE,
        values: { name: tpl.name },
      })}
      footer={
        <div className="flex gap-2">
          <button
            type="button"
            className="rh-btn rh-btn--primary"
            onClick={() => void save()}
            data-testid="edit-mapping-save"
          >
            {t('webAdmin.contracts.templates.editMapping.save', { locale: LOCALE })}
          </button>
          <button type="button" className="rh-btn" onClick={onCancel}>
            {t('webAdmin.contracts.templates.import.back', { locale: LOCALE })}
          </button>
          <SaveStatus
            status={status}
            texts={{
              saving: t('webAdmin.contracts.saving', { locale: LOCALE }),
              saved: t('webAdmin.contracts.saved', { locale: LOCALE }),
              error: t('webAdmin.contracts.loadError', { locale: LOCALE }),
            }}
          />
        </div>
      }
    >
      <p className="text-sm text-gray-600">
        {t('webAdmin.contracts.templates.editMapping.perex', { locale: LOCALE })}
      </p>
      {fieldKeys.length === 0 && (
        <p data-testid="edit-mapping-empty">
          {t('webAdmin.contracts.templates.editMapping.empty', { locale: LOCALE })}
        </p>
      )}
      {fieldKeys.length > 0 && (
        <ul data-testid="edit-mapping-fields">
          {fieldKeys.map((key) => (
            <li key={key} data-testid={`edit-mapping-field-${key}`}>
              <input
                value={drafts[key] ?? key}
                onChange={(e) => setDrafts((current) => ({ ...current, [key]: e.target.value }))}
                data-testid={`edit-mapping-input-${key}`}
              />
              <button
                type="button"
                className="rh-btn"
                onClick={() => rename(key)}
                data-testid={`edit-mapping-rename-${key}`}
              >
                {t('webAdmin.contracts.templates.editMapping.rename', { locale: LOCALE })}
              </button>
              <button
                type="button"
                className="rh-btn"
                onClick={() => remove(key)}
                data-testid={`edit-mapping-remove-${key}`}
              >
                {t('webAdmin.contracts.templates.editMapping.remove', { locale: LOCALE })}
              </button>
            </li>
          ))}
        </ul>
      )}
    </FormSection>
  );
}

function FromDemoForm({
  onDone,
  onCancel,
}: {
  readonly onDone: () => void;
  readonly onCancel: () => void;
}): React.JSX.Element {
  const [demoTemplates, setDemoTemplates] = React.useState<DemoTemplateSummaryDto[]>([]);
  const [demoId, setDemoId] = React.useState('');
  const [name, setName] = React.useState('');

  React.useEffect(() => {
    /** Krok NOC1 bod 2: selhani nabidky vzoru nesmi vybublat do error boundary. */
    void listDemoTemplates().then(setDemoTemplates, () => undefined);
  }, []);

  async function submit(): Promise<void> {
    if (demoId === '') return;
    await createOfficeTemplateFromDemo(demoId, name.trim() === '' ? undefined : name.trim());
    onDone();
  }

  return (
    <FormSection
      title={t('webAdmin.contracts.templates.fromDemo.heading', { locale: LOCALE })}
      footer={
        <div className="flex gap-2">
          <button
            type="button"
            className="rh-btn rh-btn--primary"
            onClick={() => void submit()}
            data-testid="from-demo-submit"
          >
            {t('webAdmin.contracts.templates.fromDemo.submit', { locale: LOCALE })}
          </button>
          <button type="button" className="rh-btn" onClick={onCancel}>
            {t('webAdmin.contracts.new.cancel', { locale: LOCALE })}
          </button>
        </div>
      }
    >
      <FieldRow
        label={t('webAdmin.contracts.templates.fromDemo.templateLabel', { locale: LOCALE })}
      >
        <select
          value={demoId}
          onChange={(e) => setDemoId(e.target.value)}
          data-testid="from-demo-select"
        >
          <option value="">{t('webAdmin.contracts.new.chooseTemplate', { locale: LOCALE })}</option>
          {demoTemplates.map((tpl) => (
            <option key={tpl.id} value={tpl.id}>
              {tpl.nazev}
            </option>
          ))}
        </select>
      </FieldRow>
      <FieldRow label={t('webAdmin.contracts.templates.fromDemo.nameLabel', { locale: LOCALE })}>
        <input value={name} onChange={(e) => setName(e.target.value)} />
      </FieldRow>
    </FormSection>
  );
}

/**
 * Prah hromadneho potvrzeni navrhu. Drzi se textu tlacitka v mockupu
 * („Potvrdit všechny návrhy nad 90 %") — kdyz se zmeni cislo, musi se zmenit
 * i preklad `confirmAllSuggestions`, jinak by tlacitko slibovalo neco jineho,
 * nez dela. (Skript `contracts:import-docx` ma vlastni prah 85 % — tam je to
 * jen vychozi davka pro nasledne rucni dorovnani v UI.)
 */
const AUTO_CONFIRM_PCT = 90;

function ImportWizard({
  onDone,
  onCancel,
}: {
  readonly onDone: () => void;
  readonly onCancel: () => void;
}): React.JSX.Element {
  const [step, setStep] = React.useState<1 | 2 | 3>(1);
  const [name, setName] = React.useState('');
  const [contractType, setContractType] = React.useState<ContractType>('najem-byt');
  const [articleTitle, setArticleTitle] = React.useState('');
  const [paragraphsText, setParagraphsText] = React.useState('');
  const [highlightsText, setHighlightsText] = React.useState('');
  const [articles, setArticles] = React.useState<ContractArticleDto[]>([]);
  const [suggestions, setSuggestions] = React.useState<MappingSuggestionDto[]>([]);
  const [inflectionHits, setInflectionHits] = React.useState<InflectionHitDto[]>([]);
  const [decisions, setDecisions] = React.useState<Record<string, MappingDecision>>({});
  const [fieldOverride, setFieldOverride] = React.useState<Record<string, string>>({});
  const [manualMode, setManualMode] = React.useState(false);
  /**
   * Krok APP3 bod 3 (nalez A34): potvrzena rozhodnuti, ktera server do
   * sablony NEDOSTAL. Dokud je makléřka nevidi, sablona se neuklada.
   */
  const [importSkipped, setImportSkipped] = React.useState<readonly MappingSkipDto[]>([]);
  /*
   * Krok SML5 bod 1 — RUCNI CESTA, KDYZ NAVRHY NEDOBEHNOU. Mapovaci obrazovka
   * nabizela jen TEXTOVY vstup s napovedou „Hledat pole…", takze makler musel
   * znat technicky klic (`nemovitost.lv-cislo`). Kdyz navrh polí nepřijde,
   * nema odkud ho vzit a zasekne se. Seznam poli chodi z TEHOZ katalogu, ze
   * ktereho ho bere editor smlouvy (`GET /v1/contract-templates/catalogs`).
   */
  const [catalogs, setCatalogs] = React.useState<ContractCatalogsDto | null>(null);
  /*
   * Krok UXD3b (nalez SML5) — SEZNAM VOLEB PODLE TYPU SMLOUVY. Katalog nese
   * cely slovnik poli, takze u zprostredkovatelske smlouvy o prodeji nabizel
   * nájemní pole („Pronajímatel — jméno", `pronajimatel[].*`) a makler je
   * musel premapovat. Ktere pole k typu patri ROZHODL SERVER
   * (`fieldContractTypes` — tentyz vypocet, kterym navrhar zahazuje nesedici
   * navrhy); UI jen filtruje. Pole BEZ atributu plati pro vsechny typy.
   */
  const pickerFields = React.useMemo(
    () =>
      (catalogs?.fields ?? []).filter(
        (f) => f.contractTypes === undefined || f.contractTypes.includes(contractType),
      ),
    [catalogs, contractType],
  );
  React.useEffect(() => {
    let zije = true;
    void (async () => {
      try {
        const nactene = await getContractCatalogs();
        if (zije) setCatalogs(nactene);
      } catch {
        // Katalog je jen seznam k vyberu — bez nej zustavaji navrhy ze serveru.
      }
    })();
    return () => {
      zije = false;
    };
  }, []);
  // Krok T5 bod 20: skutecne nahrani `.docx` (multipart), ne jen vlozeny text.
  const [docxStatus, setDocxStatus] = React.useState<'idle' | 'reading' | 'done' | 'error'>('idle');
  const [docxSummary, setDocxSummary] = React.useState<string | null>(null);
  const [docxError, setDocxError] = React.useState<string | null>(null);

  async function uploadDocx(file: File): Promise<void> {
    setDocxStatus('reading');
    setDocxError(null);
    try {
      const result = await importContractTemplateDocx(file);
      setArticles(result.articles);
      setSuggestions(result.suggestions);
      setInflectionHits(result.inflectionHits);
      /*
       * Krok SML6 bod 2: TYP SMLOUVY Z DOKUMENTU. Formular drzel vychozi
       * „nájem bytu", takze zprostredkovatelska smlouva o prodeji se ulozila
       * jako najem — a smlouvy z te sablony pak hlasily „PENB musí být
       * přílohou nájmu bytu". Rozpoznany typ se predvyplni; makler ho muze
       * zmenit (seznam typu zustava).
       */
      if (result.detectedContractType != null) setContractType(result.detectedContractType);
      // Bod 4: nazev sablony je TITUL SMLOUVY z dokumentu, ne nazev souboru
      // („navrh-smlouvy-v3"). Kdyz dokument titul nenese, zustava nazev souboru.
      if (name.trim() === '') {
        setName(result.documentTitle ?? file.name.replace(/\.[^.]+$/, ''));
      }
      setDocxSummary(
        t('webAdmin.contracts.templates.uploadResult', {
          locale: LOCALE,
          values: {
            paragraphs: result.paragraphCount,
            highlights: result.highlights.length,
            mapped: result.suggestions.filter((s) => s.fieldKey !== null).length,
          },
        }),
      );
      setDocxStatus('done');
      setStep(2);
    } catch (err) {
      setDocxStatus('error');
      /*
       * Krok SML5 bod 3: do SML5 se tu ukazovala hlaska ze serveru, coz
       * u 4xx znamenalo „Validace vstupu selhala" — makler z toho nepozna,
       * ze nahral `.doc`, ze je soubor pres limit nebo ze je dokument
       * prazdny. Mapa duvodu je v `form-errors.ts` (SSOT pro vic formularu).
       */
      setDocxError(humanApiError(err, 'webAdmin.contracts.templates.uploadError'));
    }
  }

  async function suggest(): Promise<void> {
    const paragraphs = paragraphsText.split('\n').filter((p) => p.trim() !== '');
    const highlights = highlightsText.split('\n').filter((p) => p.trim() !== '');
    const result = await suggestImportMapping({
      articles: [{ title: articleTitle || 'Článek I', paragraphs }],
      highlights,
    });
    setArticles(result.articles);
    setSuggestions(result.suggestions);
    setInflectionHits(result.inflectionHits);
    setStep(2);
  }

  const decidedCount = Object.keys(decisions).length;

  /**
   * Krok APP3 bod 3 (nalez A34) — ZAHOZENA ROZHODNUTI SE VYPISOU PRED
   * ULOZENIM.
   *
   * Od kroku KLIKFIX1 (vada B6) import nejednoznacny usek radeji NEPREPISE.
   * Makléřka o tom ale nevedela: potvrdila navrhy, sablona se ulozila a masku
   * `xxxxxxx` misto pole nasla az ve smlouve, kterou cte klient (audit
   * 1. 11. 2026, bod 34). Prvni kliknuti proto rozhodnuti PROMITNE a kdyz
   * server nejake zahodil, obrazovka je vypise a ceka — ulozi se az druhym
   * kliknutim („Uložit šablonu i tak"). Bez zahozenych rozhodnuti se nic
   * nemeni: jedno kliknuti ulozi jako dosud.
   */
  async function applyAndSave(force = false): Promise<void> {
    const decided = suggestions.filter((s) => decisions[s.id] !== undefined);
    const applyDecisions: ImportApplyDecision[] = decided.map((s) => ({
      suggestionId: s.id,
      snippet: s.snippet,
      decision: decisions[s.id] ?? 'nechat-text',
      ...(fieldOverride[s.id] !== undefined
        ? { fieldKey: fieldOverride[s.id] }
        : s.fieldKey !== null
          ? { fieldKey: s.fieldKey }
          : {}),
    }));
    const applied = await applyImportMapping({
      articles,
      decisions: applyDecisions,
      inflectionHits,
    });
    const skipped = applied.skipped ?? [];
    if (skipped.length > 0 && !force) {
      setImportSkipped(skipped);
      return;
    }
    // Krok T6 bod 1: bez `mapping` karta sablony ukazovala „0 polí“, i kdyz
    // aplikovana rozhodnuti pole skutecne dosadila do textu — `mapping` je
    // audit rozhodnuti mapovaci obrazovky, karta uz svuj pocet pocita z textu
    // (`officeTemplateFieldCount`), ale slovnik ma zustat pravdivy pro pozdejsi
    // audit i pro obrazovku „Upravit mapování“.
    const mapping = decided
      .filter((s) => decisions[s.id] !== 'nechat-text')
      .map((s) => ({
        ...s,
        decision: decisions[s.id] as MappingDecision,
        fieldKey: fieldOverride[s.id] ?? s.fieldKey,
      }));
    await createOfficeTemplate({ name, contractType, articles: applied.articles, mapping });
    onDone();
  }

  return (
    <div data-testid="templates-import-wizard">
      <h3>{t('webAdmin.contracts.templates.import.heading', { locale: LOCALE })}</h3>
      {/* Vysledek nahrani `.docx` zustava videt i na mapovaci obrazovce —
          makler musi mit porad pred ocima, z ceho navrhy vznikly. */}
      {docxStatus === 'reading' && (
        <p data-testid="import-docx-reading">
          {t('webAdmin.contracts.templates.uploadReading', { locale: LOCALE })}
        </p>
      )}
      {docxSummary !== null && (
        <p className="text-sm text-gray-600" data-testid="import-docx-summary">
          {docxSummary}
        </p>
      )}
      {docxError !== null && (
        <p role="alert" data-testid="import-docx-error">
          {docxError}
        </p>
      )}
      {step === 1 && (
        <FormSection
          title={t('webAdmin.contracts.templates.import.step1', { locale: LOCALE })}
          footer={
            <div className="flex gap-2">
              <button
                type="button"
                className="rh-btn rh-btn--primary"
                onClick={() => void suggest()}
                data-testid="import-suggest-button"
              >
                {t('webAdmin.contracts.templates.import.suggestButton', { locale: LOCALE })}
              </button>
              <button type="button" className="rh-btn" onClick={onCancel}>
                {t('webAdmin.contracts.templates.import.back', { locale: LOCALE })}
              </button>
            </div>
          }
        >
          {/* Mockup bod 20: „Nahrát smlouvu (Word)" — skutecny vstup pro
              soubor. Rucni vlozeni textu zustava jako druha cesta. */}
          <FieldRow label={t('webAdmin.contracts.templates.uploadFileLabel', { locale: LOCALE })}>
            <input
              type="file"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file !== undefined) void uploadDocx(file);
              }}
              data-testid="import-docx-file"
            />
          </FieldRow>
          <p className="text-sm text-gray-600">
            {t('webAdmin.contracts.templates.uploadOrManual', { locale: LOCALE })}
          </p>
          <FieldRow label={t('webAdmin.contracts.templates.import.nameLabel', { locale: LOCALE })}>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="import-name"
            />
          </FieldRow>
          <FieldRow label={t('webAdmin.contracts.templates.import.typeLabel', { locale: LOCALE })}>
            <select
              value={contractType}
              onChange={(e) => setContractType(e.target.value as ContractType)}
            >
              {(Object.keys(TYPE_KEY) as ContractType[]).map((tp) => (
                <option key={tp} value={tp}>
                  {t(TYPE_KEY[tp], { locale: LOCALE })}
                </option>
              ))}
            </select>
          </FieldRow>
          <FieldRow
            label={t('webAdmin.contracts.templates.import.articleTitleLabel', { locale: LOCALE })}
          >
            <input value={articleTitle} onChange={(e) => setArticleTitle(e.target.value)} />
          </FieldRow>
          <FieldRow
            label={t('webAdmin.contracts.templates.import.paragraphsLabel', { locale: LOCALE })}
          >
            <textarea
              value={paragraphsText}
              onChange={(e) => setParagraphsText(e.target.value)}
              data-testid="import-paragraphs"
            />
          </FieldRow>
          <FieldRow
            label={t('webAdmin.contracts.templates.import.highlightsLabel', { locale: LOCALE })}
          >
            <textarea
              value={highlightsText}
              onChange={(e) => setHighlightsText(e.target.value)}
              data-testid="import-highlights"
            />
          </FieldRow>
          <p className="text-sm text-gray-600" data-testid="import-upload-hint">
            {t('webAdmin.contracts.templates.uploadHint', { locale: LOCALE })}
          </p>
          <p className="text-sm text-gray-600" data-testid="import-note-hint">
            {t('webAdmin.contracts.templates.import.noteHint', { locale: LOCALE })}
          </p>
        </FormSection>
      )}
      {step === 2 && (
        <FormSection
          title={t('webAdmin.contracts.templates.import.step2', { locale: LOCALE })}
          footer={
            <button
              type="button"
              className="rh-btn rh-btn--primary"
              onClick={() => setStep(3)}
              data-testid="import-apply-button"
            >
              {t('webAdmin.contracts.templates.import.applyButton', { locale: LOCALE })}
            </button>
          }
        >
          {/* Krok SML6 bod 1: LISTA NAD SEZNAMEM MIST. Do SML6 visel uprostred
              obrazovky samotny zaskrtavatko „Ruční režim" a pod nim dve vety —
              makler nevedel, co po nem obrazovka chce. Vse, co plati pro CELY
              seznam (postup, hromadne potvrzeni, rezim), stoji na jednom miste. */}
          <div className="rh-v4-maptop">
            <p className="rh-v4-maptop__progress" data-testid="import-progress">
              {t('webAdmin.contracts.templates.progressMapping', {
                locale: LOCALE,
                values: {
                  done: decidedCount,
                  total: suggestions.length,
                  suggestions: manualMode ? 0 : suggestions.length,
                  unmapped: suggestions.length - decidedCount,
                },
              })}
            </p>
            <label className="rh-v4-maptop__mode">
              <input
                type="checkbox"
                checked={manualMode}
                onChange={(e) => setManualMode(e.target.checked)}
                data-testid="import-manual-mode"
              />
              <span>{t('webAdmin.contracts.templates.manualMode', { locale: LOCALE })}</span>
            </label>
          </div>
          <p className="rh-v4-crow__why">
            {t('webAdmin.contracts.templates.mappingHintImport', { locale: LOCALE })}
          </p>
          {/* Krok SML5 bod 1: navrh polí muze nedobehnout (sluzba nedostupna,
              mustr bez zvyraznenych mist). Obrazovka to rekne a NABIZI dalsi
              krok — bez toho zustal makler nad prazdnym seznamem. */}
          {suggestions.length === 0 && (
            <p role="status" data-testid="import-no-suggestions">
              {t('webAdmin.contracts.templates.import.noSuggestions', { locale: LOCALE })}
            </p>
          )}
          {/* Mockup nabizi hromadne potvrzeni nad 90 % — bez nej by makler(ka)
              u mustru klikal(a) 44 navrhu jeden po druhem. Klic
              `confirmAllSuggestions` uz v i18n byl, jen ho nic nepouzivalo. */}
          <button
            type="button"
            className="rh-btn"
            data-testid="import-confirm-all"
            onClick={() =>
              setDecisions((d) => {
                const next = { ...d };
                for (const s of suggestions) {
                  if (s.fieldKey !== null && s.confidencePct >= AUTO_CONFIRM_PCT) {
                    next[s.id] = 'potvrdit';
                  }
                }
                return next;
              })
            }
          >
            {t('webAdmin.contracts.templates.confirmAllSuggestions', { locale: LOCALE })}
          </button>
          {/*
           * Krok SML6 bod 1 — MAPOVANI, JAK HO MAKLER PRECTE.
           *
           * Do SML6 byl radek jeden `<div>` bez sazby: uryvek, stav a klic se
           * slepily do „xxxxxxxxxxxxNávrh: pronajimatel[].jmeno (jistota
           * 92 %)" (nalez R.B. 12. 10. ze snimku brany). Teď je radek dvojice:
           * VLEVO uryvek dokumentu s vetou kolem mista, VPRAVO stav jako
           * stitek a vyber pole LIDSKYM NAZVEM. Technicky klic se v UI
           * neukazuje vubec — popisek chodi ze slovniku poli na serveru.
           */}
          <ul className="rh-v4-map" data-testid="import-mapping-list">
            {suggestions.map((s) => {
              const decision = decisions[s.id];
              const chosenKey = fieldOverride[s.id] ?? s.fieldKey;
              const chosenLabel =
                catalogs?.fields.find((f) => f.key === chosenKey)?.label ?? s.fieldLabel ?? null;
              const stateClass =
                decision === 'potvrdit'
                  ? ' is-confirmed'
                  : decision === 'zmenit' || decision === 'rucne'
                    ? ' is-manual'
                    : decision === 'nechat-text'
                      ? ' is-text'
                      : chosenLabel === null
                        ? ' is-unmapped'
                        : '';
              return (
                <li
                  key={s.id}
                  className={`rh-v4-map__row${stateClass}`}
                  data-testid={`import-suggestion-${s.id}`}
                >
                  <div className="rh-v4-map__doc">
                    <span className="rh-v4-map__ctx">{s.contextBefore ?? ''}</span>
                    <mark className="rh-v4-map__place">{s.snippet}</mark>
                    <span className="rh-v4-map__ctx">{s.contextAfter ?? ''}</span>
                  </div>
                  <div className="rh-v4-map__field">
                    <span
                      className="rh-v4-map__badge"
                      data-testid={`import-suggestion-detail-${s.id}`}
                    >
                      {decision === 'nechat-text'
                        ? t('webAdmin.contracts.templates.import.decisionKeptBadge', {
                            locale: LOCALE,
                          })
                        : decision === 'potvrdit'
                          ? t('webAdmin.contracts.templates.mappingLegendConfirmed', {
                              locale: LOCALE,
                            })
                          : chosenLabel === null
                            ? t('webAdmin.contracts.templates.mappingLegendUnmapped', {
                                locale: LOCALE,
                              })
                            : t('webAdmin.contracts.templates.import.suggestionDetail', {
                                locale: LOCALE,
                                values: { field: chosenLabel, confidence: s.confidencePct },
                              })}
                    </span>
                    {/* Krok SML5 bod 1: VYBER ZE SEZNAMU, ne psani technickeho
                        klice. SML6: seznam je PREDVYBRANY na navrzene pole,
                        takze makler cte nazev pole, ne klic. */}
                    <select
                      aria-label={t('webAdmin.contracts.templates.import.fieldChoiceLabel', {
                        locale: LOCALE,
                      })}
                      className="rh-v4-map__select"
                      value={chosenKey ?? ''}
                      data-testid={`import-field-picker-${s.id}`}
                      onChange={(e) => {
                        setFieldOverride((f) => ({ ...f, [s.id]: e.target.value }));
                        setDecisions((d) => ({ ...d, [s.id]: 'zmenit' }));
                      }}
                    >
                      <option value="">
                        {t('webAdmin.contracts.templates.import.fieldPickerEmpty', {
                          locale: LOCALE,
                        })}
                      </option>
                      {/* Pole, ktere server navrhl, ale katalog v prohlizeci
                          (jeste) nema — bez teto polozky by `<select>` spadl na
                          prazdnou volbu a navrh by z obrazovky zmizel. */}
                      {chosenKey !== null &&
                        chosenKey !== '' &&
                        pickerFields.some((f) => f.key === chosenKey) !== true && (
                          <option value={chosenKey}>
                            {chosenLabel ??
                              t('webAdmin.contracts.templates.import.fieldPickerEmpty', {
                                locale: LOCALE,
                              })}
                          </option>
                        )}
                      {pickerFields.map((f) => (
                        <option key={f.key} value={f.key}>
                          {f.label}
                        </option>
                      ))}
                    </select>
                    <div className="rh-v4-map__actions">
                      <button
                        type="button"
                        className="rh-btn"
                        data-testid={`import-confirm-${s.id}`}
                        onClick={() => setDecisions((d) => ({ ...d, [s.id]: 'potvrdit' }))}
                      >
                        {t('webAdmin.contracts.templates.import.decisionConfirm', {
                          locale: LOCALE,
                        })}
                      </button>
                      <button
                        type="button"
                        className="rh-btn"
                        onClick={() => setDecisions((d) => ({ ...d, [s.id]: 'nechat-text' }))}
                      >
                        {t('webAdmin.contracts.templates.import.decisionKeep', { locale: LOCALE })}
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </FormSection>
      )}
      {step === 3 && (
        <FormSection
          title={t('webAdmin.contracts.templates.import.step3', { locale: LOCALE })}
          footer={
            <div className="flex gap-2">
              <button
                type="button"
                className="rh-btn rh-btn--primary"
                onClick={() => void applyAndSave()}
                data-testid="import-trial-fill"
              >
                {t('webAdmin.contracts.templates.trialFill', { locale: LOCALE })}
              </button>
              <button
                type="button"
                className="rh-btn"
                onClick={() => void applyAndSave()}
                data-testid="import-save-button"
              >
                {t('webAdmin.contracts.templates.saveTemplate', { locale: LOCALE })}
              </button>
            </div>
          }
        >
          {/* Krok SML5 bod 1: nazev sablony se po nahrani `.docx` prevzal
              z NAZVU SOUBORU a uz nebyl kde zmenit — „navrh-smlouvy-v3"
              zustal nazvem sablony kancelare navzdy. Pole patri ke kroku,
              kde se sablona uklada. */}
          <FieldRow label={t('webAdmin.contracts.templates.import.nameLabel', { locale: LOCALE })}>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="import-name-final"
            />
          </FieldRow>
          {/*
            Krok APP3 bod 3 (nalez A34): CO IMPORT ZAHODIL. Server vraci
            duvod u kazdeho rozhodnuti (`skipped`), obrazovka ho jmenuje
            u useku a u pole — makléřka tak vi, kde v sablone zustal text
            z dokumentu, a muze zvyraznit delsi usek nebo pole doplnit
            v editoru. Dokud to necte, sablona se neuklada.
          */}
          {importSkipped.length > 0 && (
            <div role="alert" data-testid="import-skipped">
              <p>
                {t('webAdmin.contracts.templates.import.skippedHeading', { locale: LOCALE })}
              </p>
              <ul>
                {importSkipped.map((skip) => (
                  <li key={`${skip.suggestionId}-${skip.reason}`} data-testid={`import-skipped-${skip.suggestionId}`}>
                    {t(IMPORT_SKIP_KEY[skip.reason], {
                      locale: LOCALE,
                      values: {
                        snippet: skip.snippet,
                        /*
                         * Popisek pole bere PRVNI ze serveru (`fieldLabel`
                         * navrhu, krok SML6 bod 1) — mapovaci obrazovka nesmi
                         * kreslit technicky klic ani kdyz se katalog poli
                         * nenacte. Katalog je zachranna sit.
                         */
                        field:
                          skip.fieldKey === null
                            ? ''
                            : (suggestions.find((s) => s.id === skip.suggestionId)?.fieldLabel ??
                               fieldLabel(catalogs, skip.fieldKey, undefined)),
                      },
                    })}
                  </li>
                ))}
              </ul>
              <p data-testid="import-skipped-note">
                {t('webAdmin.contracts.templates.import.skippedNote', { locale: LOCALE })}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="rh-btn rh-btn--primary"
                  onClick={() => void applyAndSave(true)}
                  data-testid="import-skipped-save-anyway"
                >
                  {t('webAdmin.contracts.templates.import.skippedSaveAnyway', { locale: LOCALE })}
                </button>
                <button
                  type="button"
                  className="rh-btn"
                  onClick={() => {
                    setImportSkipped([]);
                    setStep(2);
                  }}
                  data-testid="import-skipped-back"
                >
                  {t('webAdmin.contracts.templates.import.skippedBackToMapping', { locale: LOCALE })}
                </button>
              </div>
            </div>
          )}
        </FormSection>
      )}
    </div>
  );
}

/**
 * KNIHOVNA KLAUZULI KANCELARE (krok SML3 bod 3) — CRUD pod „Mustry
 * a klauzule".
 *
 * Klauzule je hotovy odstavec, ktery makler vklada do konkretni smlouvy.
 * Uprava klauzule tady uz vlozenym smlouvam NIC NEMENI — po vlozeni zije text
 * u smlouvy (ES1 bod 4: podepsany text se zpetne menit nesmi). Vyrazeni je
 * proto taky soft: radek je stopa, odkud ten odstavec prisel.
 */
function ClauseLibrarySection(): React.JSX.Element {
  const [clauses, setClauses] = React.useState<readonly ContractClauseDto[]>([]);
  const [title, setTitle] = React.useState('');
  const [note, setNote] = React.useState('');
  const [body, setBody] = React.useState('');
  const [editId, setEditId] = React.useState<string | null>(null);
  const [status, setStatus] = React.useState<SaveStatusKind>('idle');
  const [error, setError] = React.useState<string | undefined>(undefined);

  const reload = React.useCallback(async (): Promise<void> => {
    setClauses(await listContractClauses());
  }, []);

  React.useEffect(() => {
    void reload();
  }, [reload]);

  function reset(): void {
    setEditId(null);
    setTitle('');
    setNote('');
    setBody('');
  }

  async function submit(): Promise<void> {
    if (title.trim() === '' || body.trim() === '') return;
    setStatus('saving');
    try {
      if (editId === null) {
        await createContractClause({ title: title.trim(), note: note.trim(), body: body.trim() });
      } else {
        await updateContractClause(editId, {
          title: title.trim(),
          note: note.trim(),
          body: body.trim(),
        });
      }
      await reload();
      reset();
      setStatus('saved');
      setError(undefined);
    } catch (err) {
      setStatus('error');
      setError(errorMessage(err));
    }
  }

  async function archive(id: string): Promise<void> {
    setStatus('saving');
    try {
      await archiveContractClause(id);
      await reload();
      if (editId === id) reset();
      setStatus('saved');
    } catch (err) {
      setStatus('error');
      setError(errorMessage(err));
    }
  }

  return (
    <FormSection title={t('webAdmin.contracts.clauses.heading', { locale: LOCALE })}>
      <p>{t('webAdmin.contracts.clauses.perex', { locale: LOCALE })}</p>
      {clauses.length === 0 ? (
        <p data-testid="clauses-empty">
          {t('webAdmin.contracts.clauses.empty', { locale: LOCALE })}
        </p>
      ) : (
        /*
         * Krok UXF4 bod 10 (#32): „Akce Upravit/Vyřadit nalepené na textu
         * klauzulí" — radky jsou ted sdilena `ListTable` (FUNK1) a akce maji
         * VLASTNI PRAVY SLOUPEC (`align: 'right'`), ne pokracovani vety.
         * Tabulka je tataz jako u sablon vys, takze se na 390 px prekresli
         * na karty stejne jako zbytek adminu.
         */
        <ListTable
          testId="clauses-list"
          caption={t('webAdmin.contracts.clauses.heading', { locale: LOCALE })}
          rows={clauses}
          rowKey={(clause) => clause.id}
          rowTestId={(clause) => `clause-row-${clause.id}`}
          columns={[
            {
              key: 'title',
              label: t('webAdmin.contracts.clauses.titleField', { locale: LOCALE }),
              cell: (clause) => clause.title,
            },
            {
              key: 'note',
              label: t('webAdmin.contracts.clauses.noteField', { locale: LOCALE }),
              cell: (clause) => (clause.note !== null && clause.note !== '' ? clause.note : ''),
            },
            {
              key: 'actions',
              label: t('webAdmin.contracts.list.colActions', { locale: LOCALE }),
              srOnlyLabel: true,
              align: 'right',
              cell: (clause) =>
                clause.source === 'reos' ? (
                  // Standardni klauzule REOS (GDPR1) dodava REOS — kancelar ji
                  // vklada do smluv, ale neupravuje ani nevyrazuje.
                  <span data-testid={`clause-reos-${clause.id}`}>
                    {t('webAdmin.contracts.clauses.reosReadOnly', { locale: LOCALE })}
                  </span>
                ) : (
                  <>
                    <button
                      type="button"
                      className="reos-checks__jump"
                      onClick={() => {
                        setEditId(clause.id);
                        setTitle(clause.title);
                        setNote(clause.note ?? '');
                        setBody(clause.body);
                      }}
                      data-testid={`clause-edit-${clause.id}`}
                    >
                      {t('webAdmin.contracts.clauses.edit', { locale: LOCALE })}
                    </button>
                    <button
                      type="button"
                      className="reos-checks__jump"
                      onClick={() => void archive(clause.id)}
                      data-testid={`clause-archive-${clause.id}`}
                    >
                      {t('webAdmin.contracts.clauses.archive', { locale: LOCALE })}
                    </button>
                  </>
                ),
            },
          ]}
        />
      )}
      <FieldRow label={t('webAdmin.contracts.clauses.titleField', { locale: LOCALE })}>
        <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="clause-title" />
      </FieldRow>
      <FieldRow label={t('webAdmin.contracts.clauses.noteField', { locale: LOCALE })}>
        <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="clause-note" />
      </FieldRow>
      <FieldRow label={t('webAdmin.contracts.clauses.bodyField', { locale: LOCALE })}>
        <textarea
          value={body}
          rows={4}
          onChange={(e) => setBody(e.target.value)}
          data-testid="clause-body"
        />
      </FieldRow>
      <button
        type="button"
        className="rh-btn rh-btn--primary"
        onClick={() => void submit()}
        data-testid="clause-save"
      >
        {t(
          editId === null
            ? 'webAdmin.contracts.clauses.save'
            : 'webAdmin.contracts.clauses.saveEdit',
          { locale: LOCALE },
        )}
      </button>
      {editId !== null && (
        <button type="button" className="rh-btn" onClick={reset} data-testid="clause-cancel">
          {t('webAdmin.contracts.clauses.cancel', { locale: LOCALE })}
        </button>
      )}
      <SaveStatus
        status={status}
        texts={{
          saving: t('webAdmin.contracts.saving', { locale: LOCALE }),
          saved: t('webAdmin.contracts.saved', { locale: LOCALE }),
          error: error ?? t('webAdmin.contracts.loadError', { locale: LOCALE }),
        }}
      />
    </FormSection>
  );
}

function CustomSubjectsSection(): React.JSX.Element {
  const [subjects, setSubjects] = React.useState<CustomSubjectDto[]>([]);
  const [label, setLabel] = React.useState('');
  const [rod, setRod] = React.useState<'m' | 'z'>('m');

  React.useEffect(() => {
    void listCustomSubjects().then(setSubjects);
  }, []);

  async function submit(): Promise<void> {
    if (label.trim() === '') return;
    const created = await createCustomSubject({
      label: label.trim(),
      secondPad: label.trim(),
      rod,
    });
    setSubjects((prev) => [...prev, created]);
    setLabel('');
  }

  return (
    <FormSection
      title={t('webAdmin.contracts.templates.customSubjects.heading', { locale: LOCALE })}
    >
      {subjects.length === 0 ? (
        <p>{t('webAdmin.contracts.templates.customSubjects.empty', { locale: LOCALE })}</p>
      ) : (
        <ul>
          {subjects.map((s) => (
            <li key={s.id}>{s.forms.label}</li>
          ))}
        </ul>
      )}
      <FieldRow
        label={t('webAdmin.contracts.templates.customSubjects.labelField', { locale: LOCALE })}
      >
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          data-testid="custom-subject-label"
        />
      </FieldRow>
      <FieldRow
        label={t('webAdmin.contracts.templates.customSubjects.genderLabel', { locale: LOCALE })}
      >
        <select value={rod} onChange={(e) => setRod(e.target.value as 'm' | 'z')}>
          <option value="m">
            {t('webAdmin.contracts.templates.customSubjects.genderM', { locale: LOCALE })}
          </option>
          <option value="z">
            {t('webAdmin.contracts.templates.customSubjects.genderZ', { locale: LOCALE })}
          </option>
        </select>
      </FieldRow>
      <button
        type="button"
        className="rh-btn"
        onClick={() => void submit()}
        data-testid="custom-subject-save"
      >
        {t('webAdmin.contracts.templates.customSubjects.save', { locale: LOCALE })}
      </button>
    </FormSection>
  );
}
